import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mic, MicOff, Volume2, User, ArrowLeft, Loader2, MessageCircle, Video, VideoOff, TrendingUp, Award, Target, XCircle, CheckCircle, X, Brain, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import ScrollToBottom from 'react-scroll-to-bottom';
import clsx from 'clsx';
import OwenPresence from '../components/OwenPresence';
import useAudioLevel from '../hooks/useAudioLevel';
import useTTSDetection from '../hooks/useTTSDetection';
import { useStreaming } from '../hooks/useStreaming'; // 🌊 NEW: Streaming hook
import { useStreamingSSE } from '../hooks/useStreamingSSE'; // 🚀 P1.3: SSE Streaming hook
import { useOwenStateMachine } from '../hooks/useOwenStateMachine'; // 🎯 MACHINE D'ÉTAT
// 🛡️ Import des utilitaires API robustes (timeout, retry, déduplication)
import { safeFetch, safeFetchJSON, audioPlayer, requestDeduplicator } from '../utils/apiHelpers';
// 🔒 P0 FIX: useSafeFetch avec AbortController obligatoire
import useSafeFetch from '../hooks/useSafeFetch';
// 🎯 PHASE 2: Import du cache de scénarios
import { getScenarioFromCache, saveScenarioToCache, clearScenarioCache } from '../utils/scenarioCache';
import { getBackendUrl, getApiUrl } from '../config/environment';
// 🎯 GOUVERNANCE PÉDAGOGIQUE: Import du bandeau objectif
import ActiveObjectiveBanner from '../components/ActiveObjectiveBanner';
// 🎯 P1.0: Import du sélecteur de thème
import ThemeSelector from '../components/ThemeSelector';
// 🔄 SYNCHRONISATION: Import des événements pédagogiques
import { notifyEvaluationComplete } from '../utils/pedagogicalEvents';
// 🔒 P0 EXECUTION CONTEXT: Arrêt des tâches à la sortie de page
import { usePageExecutionContext } from '../hooks/usePageExecutionContext';
// 🔒 P0 MOA (21 Jan 2026): Session credits pour griser boutons quand épuisés
import { useSessionCredits } from '../hooks/useSessionCredits';
// 🔒 P0 UX MOA (21 Jan 2026): Modal premium pour fin de crédit (pas d'erreur)
import { SessionsExhaustedModal } from '../components/premium/SessionsExhaustedModal';
// 🔒 Mars 2026: Hook centralisé pour l'objectif actif
import useActiveObjective from '../hooks/useActiveObjective';
// Avr 2026: Rendu Markdown unifié (partagé avec EntrainementEcrit)
import { OwenMarkdown } from '../components/OwenMarkdown';
// 🛑 Mai 2026 (Iter338): Bus audio centralisé pour stop audio sur route change
import OwenAudioBus from '../services/owenAudioBus';

const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

// 🔒 P0 FIX (Jan 2026): Le parsing des 3 blocs est fait côté backend UNIQUEMENT.
// Ce parser est conservé UNIQUEMENT comme fallback d'urgence si le backend ne renvoie pas les blocs.
// Le backend est LA source de vérité via feedback_blocks.
const parseP12FeedbackFallback = (feedbackText) => {
  if (!feedbackText) return null;
  
  // Essayer de parser avec délimiteur |||
  if (feedbackText.includes('|||')) {
    const parts = feedbackText.split('|||').map(p => p.trim()).filter(p => p);
    if (parts.length >= 3) {
      return {
        observation: parts[0],
        axe_evolution: parts[1],
        relance: parts[2]
      };
    }
  }
  
  // Fallback minimal si pas de délimiteurs
  return {
    observation: feedbackText,
    axe_evolution: '',
    relance: ''
  };
};

// 🎯 P1.2: Composant d'affichage du feedback en blocs structurés
const P12FeedbackBlocks = ({ p12Structure, rawContent }) => {
  // Si pas de structure P1.2, afficher le contenu brut avec Markdown
  if (!p12Structure || (!p12Structure.observation && !p12Structure.axe_evolution && !p12Structure.relance)) {
    return <OwenMarkdown>{rawContent}</OwenMarkdown>;
  }
  
  return (
    <div className="space-y-4" data-testid="p12-feedback-blocks">
      {/* Observation */}
      {p12Structure.observation && (
        <div className="bg-blue-50/70 rounded-xl p-4 border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🔍</span>
            <h4 className="font-semibold text-blue-800 text-sm uppercase tracking-wide">Observation</h4>
          </div>
          <div className="text-blue-900 leading-relaxed"><OwenMarkdown>{p12Structure.observation}</OwenMarkdown></div>
        </div>
      )}
      
      {/* Axe d'évolution */}
      {p12Structure.axe_evolution && (
        <div className="bg-amber-50/70 rounded-xl p-4 border border-amber-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🎯</span>
            <h4 className="font-semibold text-amber-800 text-sm uppercase tracking-wide">Axe d'évolution</h4>
          </div>
          <div className="text-amber-900 leading-relaxed"><OwenMarkdown>{p12Structure.axe_evolution}</OwenMarkdown></div>
        </div>
      )}
      
      {/* Relance */}
      {p12Structure.relance && (
        <div className="bg-violet-50/70 rounded-xl p-4 border border-violet-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">❓</span>
            <h4 className="font-semibold text-violet-800 text-sm uppercase tracking-wide">Relance</h4>
          </div>
          <div className="text-violet-900 leading-relaxed font-medium"><OwenMarkdown>{p12Structure.relance}</OwenMarkdown></div>
        </div>
      )}
    </div>
  );
};

const CoachVocal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // 🔒 P0 MOA (21 Jan 2026): Session credits pour griser boutons quand épuisés
  // 🔒 P0 MOA (Jan 2026): refreshSilent = refresh SANS changement de loading (évite refresh intempestif)
  const { isExhausted: sessionsExhausted, current: sessionsCurrent, refresh: refreshSessions, refreshSilent: refreshSessionsSilent, loading: sessionsLoading } = useSessionCredits();
  
  // 🔒 P0 UX MOA (21 Jan 2026): État pour modal recharge premium (pas d'erreur)
  const [showSessionsExhaustedModal, setShowSessionsExhaustedModal] = useState(false);
  
  // 🔒 P0 EXECUTION CONTEXT: Arrêt des tâches backend à la sortie de page
  usePageExecutionContext('coach-vocal');
  
  // 🔒 P0 FIX: useSafeFetch avec AbortController automatique
  const { safeFetch: safeFetchHook, isAborted } = useSafeFetch();
  const moduleIdParam = searchParams.get('moduleId');
  const modeParam = searchParams.get('mode');
  // 🔒 Seuls 2 modes : free et certifiant
  const isFreeMode = modeParam === 'free';
  // ✅ NE PAS fallback au module 1 si mode libre (pas de scénario de formation)
  const moduleId = moduleIdParam || (isFreeMode ? null : '1');
  
  // 🏆 COACH PREMIUM TEC-1: Mode libre = Coaching Premium (PAS D4)
  // RÈGLE: En mode libre, Owen est un coach premium bienveillant
  // 🏆 D4 COMPLÈTEMENT DÉSACTIVÉ - Toujours Coach Premium
  const isCoachPremiumMode = isFreeMode;  // 🏆 Mode Coach Premium uniquement
  
  const owenContextParam = searchParams.get('context');
  let coachPremiumContext = null;  // 🏆 Renommé de owenD4Context → coachPremiumContext
  try {
    if (owenContextParam) {
      coachPremiumContext = JSON.parse(decodeURIComponent(owenContextParam));
    }
  } catch (e) {
    console.warn('[CoachVocal] Could not parse context:', e);
  }
  
  // 🏆 COACH PREMIUM: Contexte par défaut pour mode libre
  if (isFreeMode && !coachPremiumContext) {
    coachPremiumContext = {
      type: "coach_premium",  // 🏆 Toujours coach premium
      gravity: "accompagnement",  // 🏆 Accompagnement bienveillant
      label: "Entraînement libre avec Owen",
      init: null,
      source: "coach_premium_mode",
      fetchOpener: true
    };
  }
  
  // États
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPlayingQueue, setIsPlayingQueue] = useState(false); // 🎯 CORRECTION UX: State pour tracker la lecture via queue
  const [currentEmotion, setCurrentEmotion] = useState('empathique');
  const [transcript, setTranscript] = useState('');
  const [conversation, setConversation] = useState([]);
  const [error, setError] = useState(null);
  const [moduleName, setModuleName] = useState('Coach DIA');
  const [playingMessageIndex, setPlayingMessageIndex] = useState(null); // Track which message is playing audio
  const [recordingDuration, setRecordingDuration] = useState(0); // Track recording duration in seconds
  const recordingTimerRef = useRef(null); // Timer for recording duration
  
  // NOUVEAUX: Scénario
  const [scenario, setScenario] = useState(null);
  const [userName, setUserName] = useState('');
  
  // 🎯 GOUVERNANCE PÉDAGOGIQUE: Objectif actif (hook centralisé — SOURCE UNIQUE)
  const {
    activeObjective,
    initialProgressRef,
    handleObjectiveLoaded,
    updateProgress: updateActiveObjectiveProgress
  } = useActiveObjective({ captureInitialProgress: true });
  const [objectiveScenario, setObjectiveScenario] = useState(null);
  
  // 🎭 ROLEPLAY STATE: Track roleplay session for correct feedback routing
  const [roleplaySessionData, setRoleplaySessionData] = useState(null);
  const hasPlayedWelcomeRef = useRef(false); // Use ref instead of state for immediate synchronization
  const isInitializingRef = useRef(false); // Prevent duplicate initialization
  const initTimestampRef = useRef(null); // Track initialization timestamp
  const activeRequestsRef = useRef(new Set()); // Track active API requests by ID
  
  // 🔒 P0 MOA (Jan 2026): SESSION_RUN_ID — Chaîne de traçabilité certifiante
  // Stocke l'ID unique de session reçu du backend via SSE event 'session_started'
  // DOIT être transmis à l'endpoint de feedback final pour isoler les preuves
  const sessionRunIdRef = useRef(null);
  
  // NOUVEAUX: États du coach vocal (initial, loading, active, error)
  const [coachState, setCoachState] = useState('initial'); // 'initial', 'loading', 'active', 'error'
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // 🔒 P0 MOA (Jan 2026): Flag d'initialisation complète - OPTION C
  // Une fois true, le useEffect d'init ne se re-déclenchera JAMAIS
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // ✨ NOUVEAUX: Synchronisation texte ↔ voix (affichage mot par mot)
  const [scenarioTextRevealed, setScenarioTextRevealed] = useState(false); // Le texte est-il révélé ?
  const [revealedScenarioText, setRevealedScenarioText] = useState(''); // Texte révélé progressivement
  const [isRevealingText, setIsRevealingText] = useState(false); // Animation en cours ?
  const revealIntervalRef = useRef(null); // Référence pour l'intervalle d'animation
  
  // Progression et recommandations
  const [progressSummary, setProgressSummary] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [userLevel, setUserLevel] = useState('débutant');
  const [showProgressPanel, setShowProgressPanel] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false); // Owen 360° popover toggle
  
  // Owen 360° Proactive Intro (Phase 4)
  const [proactiveIntroMessage, setProactiveIntroMessage] = useState(null);

  // 📊 Session outcome tracking (Phase 5 — 12/02/2026)
  const sessionStartedAtRef = useRef(Date.now());
  const turnsCountRef = useRef(0);
  const lastVerdictRef = useRef(null);
  const lastDirectionRef = useRef(null);
  const outcomeLoggedRef = useRef(false);

  // 🔒 P0 — Mesure de durée d'enregistrement micro (envoyée au backend pour
  // contrôle qualité Whisper et observabilité). MS du start du MediaRecorder
  // jusqu'au onstop. Permet aussi le pré-check size côté client.
  const recordingStartTimeRef = useRef(0);
  const recordedMimeTypeRef = useRef(null);
  
  // 🎯 Global Owen speaking state for button disabling
  const [isOwenSpeaking, setIsOwenSpeaking] = useState(false);
  const [isInitialScenarioPlaying, setIsInitialScenarioPlaying] = useState(true);  // Verrouillage initial jusqu'à fin intro
  
  // 🔒 P0 ÉTATS VOCAUX CANONIQUES OWEN (ALIGNEMENT UI ↔ AUDIO)
  // THINKING = Génération scénario INITIAL (LLM)
  // GENERATING_RESPONSE = Génération réponse après parole utilisateur (LLM)
  // PREPARING_AUDIO = TTS en cours, audio pas encore prêt
  // SPEAKING = Audio RÉELLEMENT en lecture (event audio.onplay reçu)
  const [owenVocalState, setOwenVocalState] = useState('IDLE'); // 'IDLE' | 'THINKING' | 'GENERATING_RESPONSE' | 'PREPARING_AUDIO' | 'SPEAKING'
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // 🧱 MODE BLOCS: MACHINE À ÉTATS UNIFIÉE (MOA 03/01/2026)
  // ═══════════════════════════════════════════════════════════════════════════════
  // IDLE → THINKING → SPEAKING → DONE / ERROR
  // - THINKING: déclenché par envoi message utilisateur
  // - SPEAKING: déclenché UNIQUEMENT par audio.play()
  // - DONE: déclenché UNIQUEMENT par audio.onended
  // ═══════════════════════════════════════════════════════════════════════════════
  const [blocState, setBlocState] = useState('IDLE'); // 'IDLE' | 'THINKING' | 'SPEAKING' | 'DONE' | 'ERROR'
  const owenAudioRef = useRef(null); // Référence audio pour machine à états
  
  // 🎯 STATE MACHINE VOCALE (source unique de vérité)
  const [vocalStage, setVocalStage] = useState('IDLE');
  // États possibles: IDLE, SCENARIO_LOADING, SCENARIO_STREAMING, USER_TURN, 
  //                  PROCESSING_USER, OWEN_RESPONSE, 
  //                  FEEDBACK_FINAL, FEEDBACK_FINAL_STREAMING, SESSION_ENDED
  
  // 🎯 P1.0: Thème sélectionné par l'utilisateur (mode libre uniquement)
  const [selectedTheme, setSelectedTheme] = useState(null);
  
  // 🔒 ANTI-BOUCLE POST-CLÔTURE (Avril 2026)
  // Quand true, l'UI bloque l'enregistrement et l'envoi de messages
  const [sessionTerminee, _setSessionTermineeLegacy] = useState(false);
  // 🎯 Iter339 — Source de vérité UX : sessionPhase (state machine à 5 phases).
  //   ACTIVE → STOP_CONVERSATION → EVALUATION_READY → EVALUATION_OPEN → SESSION_CLOSED
  //   Règle métier : "Session terminée" et verrouillage complet UNIQUEMENT en SESSION_CLOSED.
  //   Audio Owen NE DOIT PAS être coupé avant EVALUATION_OPEN (règle produit).
  const [sessionPhase, setSessionPhase] = useState('ACTIVE');
  const sessionPhaseRef = useRef('ACTIVE');
  useEffect(() => { sessionPhaseRef.current = sessionPhase; }, [sessionPhase]);

  // Matrice de transitions autorisées. Toute transition hors matrice = no-op + warn (zéro fallback).
  const ALLOWED_PHASE_TRANSITIONS = useMemo(() => ({
    ACTIVE:             ['STOP_CONVERSATION'],
    STOP_CONVERSATION:  ['EVALUATION_READY'],
    EVALUATION_READY:   ['EVALUATION_OPEN'],
    EVALUATION_OPEN:    ['SESSION_CLOSED'],
    SESSION_CLOSED:     [],
  }), []);

  const transitionSessionPhase = useCallback((next, source = 'unknown') => {
    const current = sessionPhaseRef.current;
    if (current === next) return;
    const allowed = ALLOWED_PHASE_TRANSITIONS[current] || [];
    if (!allowed.includes(next)) {
      console.warn(`[sessionPhase] ❌ invalid transition ${current} → ${next} (from: ${source})`);
      return;
    }
    console.log(`[sessionPhase] ✅ ${current} → ${next} (from: ${source})`);
    sessionPhaseRef.current = next;
    setSessionPhase(next);
  }, [ALLOWED_PHASE_TRANSITIONS]);

  // Iter339 — Synchronisation du drapeau legacy sessionTerminee (lecture seule
  // pour les consommateurs historiques). Source de vérité = sessionPhase.
  useEffect(() => {
    _setSessionTermineeLegacy(sessionPhase === 'SESSION_CLOSED');
  }, [sessionPhase]);

  // 🎯 FLOW ÉVALUATION UNIFIÉ (Fév 2026)
  //   Séquence STRICTE et NON-NÉGOCIABLE au clic "Évaluation (Feedback)":
  //     'idle'       → état par défaut (avant clic)
  //     'analyzing'  → immédiatement après clic, pendant la génération du feedback
  //     'speaking'   → déclenché EXCLUSIVEMENT par audio.onplay (TTS réel)
  //     'completed'  → déclenché EXCLUSIVEMENT par audio.onended (fin réelle du TTS)
  //   INTERDICTION: setTimeout, approximations, sauts d'état.
  const [evaluationFlowState, setEvaluationFlowState] = useState({ phase: 'idle' });
  
  // Références
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null); // NEW: Store the media stream
  const isCancelledRef = useRef(false); // NEW: Track if recording was cancelled
  const isProcessingAudioRef = useRef(false); // ✅ GARDE ANTI-RÉENTRANCE pour processAudio
  
  // 🔒 P0 FIX: Guards pour cleanup propre à la sortie de page
  const isMountedRef = useRef(true); // Flag de montage pour ignorer callbacks post-démontage
  const ttsAbortControllerRef = useRef(null); // AbortController dédié pour requêtes TTS
  const initAbortControllerRef = useRef(null); // 🔒 P0 BIS: AbortController pour flux d'initialisation
  
  // 🎨 NEW: Audio detection hooks
  const { startListening: startAudioDetection, stopListening: stopAudioDetection, isListening: isAudioDetecting } = useAudioLevel();
  const { isSpeaking: isTTSSpeaking } = useTTSDetection(audioRef);
  
  // 🎯 PHASE 3: Ref pour le callback onChunk (permet de le changer dynamiquement)
  const onChunkCallbackRef = useRef(null);
  
  // 🎯 AUDIO-SYNCED DISPLAY: Affichage progressif synchronisé avec audio
  const phrasesBuffer = useRef([]);
  const currentPhraseIndex = useRef(0);
  const audioQueue = useRef([]);
  const isPlayingAudioQueue = useRef(false);
  const currentAudioRef = useRef(null);
  const nextOrderToPlay = useRef(0);
  const displayedTextLength = useRef(0);
  const currentlyPlayingPhrase = useRef(null);
  const audioSyncInterval = useRef(null);  // Interval pour mise à jour sync audio
  const currentPlayingMessageId = useRef(null);  // ID du message en cours de lecture
  
  // 🔧 P0 HEARTBEAT: Ref pour stocker le dernier message (retry)
  const lastUserMessageRef = useRef(null);
  
  // 🚀 TTS PROGRESSIF: Queue de segments audio pour lecture séquentielle
  const audioSegmentQueue = useRef([]);
  const currentSegmentOrder = useRef(0);
  const isPlayingSegment = useRef(false);
  const progressiveAudioActive = useRef(false);
  
  // 🎯 PHASE 3A: Timeout de sécurité pour forcer génération TTS
  const lastPhraseTime = useRef(Date.now());
  const safetyTimeout = useRef(null);
  
  // 🔧 FIX STREAMING: Timeout de sécurité audio (25s max)
  const audioTimeoutRef = useRef(null);
  
  
  // 🎯 CORRECTION #4: Ref pour marquer le premier message d'Owen (exclusion salutation)
  const firstOwenMessageIdRef = useRef(null);
  
  // 🔒 P0 ALIGNEMENT UI ↔ AUDIO: Source de vérité UNIQUE pour "Owen est en train de parler"
  // RÈGLE ABSOLUE: Owen ne peut être en mode SPEAKING que si audio.onplay a été reçu
  // Cela garantit que l'utilisateur ne voit JAMAIS "Owen parle" sans entendre de son
  const isOwenCurrentlySpeaking = owenVocalState === 'SPEAKING' || isTTSSpeaking;
  
  // 🔒 P0 États de préparation (pour UI mais PAS pour mode "parle")
  const isOwenThinking = owenVocalState === 'THINKING';  // Génération scénario INITIAL
  const isOwenGeneratingResponse = owenVocalState === 'GENERATING_RESPONSE';  // Génération réponse
  const isOwenPreparingAudio = owenVocalState === 'PREPARING_AUDIO';  // TTS en cours
  
  // 🎯 MACHINE D'ÉTAT OWEN - Source de vérité unique
  const owenStateMachine = useOwenStateMachine();
  const {
    state: owenState,
    phase: owenPhase,
    isIdle: owenIsIdle,
    isStreaming: owenIsStreaming,
    isGeneratingTTS: owenIsGeneratingTTS,
    isPlayingAudio: owenIsPlayingAudio,
    isDone: owenIsDone,
    startStreaming: owenStartStreaming,
    addTextChunk: owenAddTextChunk,
    completeStreaming: owenCompleteStreaming,
    markTTSReady: owenMarkTTSReady,
    startAudioPlayback: owenStartAudioPlayback,
    updateDisplayedText: owenUpdateDisplayedText,
    nextAudio: owenNextAudio,
    complete: owenComplete,
    error: owenError,
    reset: owenReset,
    PHASES: OWEN_PHASES
  } = owenStateMachine;
  
  // 🌊 Streaming hook (utilisé uniquement pour réception chunks)
  const { 
    streamedText, 
    isStreaming, 
    error: streamError, 
    startStream, 
    cancelStream,
    resetStream 
  } = useStreaming({
    onComplete: async (fullText) => {
      console.log('[Streaming] ✅ Complete:', fullText.length, 'chars');
      // Géré par la machine d'état
    },
    onError: (err) => {
      console.error('[Streaming] ❌ Error:', err);
      owenError('Streaming failed: ' + err.message);
    },
    onChunk: (chunk, fullText) => {
      // 🎯 CORRECTION #2: Utiliser onChunkCallbackRef si défini (pour processAudio)
      if (onChunkCallbackRef.current) {
        console.log('[useStreaming-onChunk] ✅ CORRECTION #2: Calling onChunkCallbackRef.current');
        onChunkCallbackRef.current(chunk, fullText);
      }
      // Sinon accumuler dans la machine d'état (mode non-vocal)
      else if (owenIsStreaming) {
        owenAddTextChunk(chunk);
      }
    }
  });
  
  
  // Owen 360° Fetch Recommendations
  const fetchRecommendations = async () => {
    if (!user?.user_id) return;
    
    try {
      const mockRecommendations = [
        {
          type: "emotion_based",
          module_id: "9",
          module_title: "Gérer son stress",
          reason: "Stress récurrent détecté",
          relevance_score: 0.92
        },
        {
          type: "progression_based",
          module_id: "7",
          module_title: "Savoir dire non",
          reason: "Renforcer assertivité",
          relevance_score: 0.85
        }
      ];
      
      try {
        // 🔒 P0 FIX: Use safeFetchHook (native fetch + AbortController + single body read)
        const data = await safeFetchHook(
          `${API}/ai/recommendations/${user.user_id}?max_recommendations=2`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            timeout: 2000
          }
        );
        
        if (data.recommendations && data.recommendations.length > 0) {
          setRecommendations(data.recommendations.slice(0, 2));
          return;
        }
      } catch (apiError) {
        // 🔒 P0 FIX: Ignorer les erreurs d'abort
        if (!isAborted(apiError)) {
          console.log('⚠️ Using mock recommendations');
        }
      }
      
      setRecommendations(mockRecommendations);
    } catch (error) {
      console.error('⚠️ Error loading recommendations:', error);
    }
  };
  
  useEffect(() => {
    if (user && scenario) {
      fetchRecommendations();
      fetchProactiveIntroMessage();
      
      // 🚀 PRÉ-CALCUL: Précharger la trajectoire utilisateur en avance
      preloadUserTrajectory();
    }
  }, [user, scenario]);
  
  // 🚀 OPTIMISATION: Préchargement de la trajectoire utilisateur
  const preloadUserTrajectory = async () => {
    if (!user?.user_id) return;
    
    try {
      console.log('[preloadUserTrajectory] 🚀 Starting trajectory preload...');
      // 🔒 P0 FIX: Use safeFetchHook
      const data = await safeFetchHook(
        `${API}/roleplay/preload-trajectory`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            user_id: user.user_id,
            user_first_name: user.name || user.first_name || 'ami'
          }),
          timeout: 10000
        }
      );
      
      console.log('[preloadUserTrajectory] ✅ Trajectory preloaded:', data.status);
    } catch (error) {
      // 🔒 P0 FIX: Silencieux - ignorer abort et erreurs réseau
      if (!isAborted(error)) {
        console.log('[preloadUserTrajectory] ⚠️ Preload skipped (will compute on demand)');
      }
    }
  };
  
  const fetchProactiveIntroMessage = async () => {
    if (!user?.user_id) return;
    
    try {
      // 🔒 P0 FIX: Use safeFetchHook
      const data = await safeFetchHook(
        `${API}/ai/predictive-feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            user_id: user.user_id,
            context: "session_start"
          }),
          timeout: 3000
        }
      );
      
      if (data.feedback_type === "encouragement" && data.message) {
        setProactiveIntroMessage(data.message);
      }
    } catch (error) {
      // 🔒 P0 FIX: Silencieux - ignorer abort et erreurs
      if (!isAborted(error)) {
        console.log('⚠️ No proactive intro message');
      }
    }
  };
  
  // ✨ PHASE 2: Computed state for intelligent audio management
  const isHistoryPlaying = playingMessageIndex !== null;

  // 💡 NOUVEAU: Gérer l'état global isOwenSpeaking automatiquement
  // 🎯 FIX UX AUDIO: Inclure isTTSSpeaking pour synchronisation avec fin d'audio réelle
  useEffect(() => {
    const owenIsSpeaking = 
      isInitialScenarioPlaying ||  // Verrouillage initial
      isSpeaking || 
      isTTSSpeaking ||  // 🎯 FIX: Détection audio réelle via hook
      isHistoryPlaying || 
      isProcessing;  // Pendant génération réponse Owen
    
    setIsOwenSpeaking(owenIsSpeaking);
    
    console.log('[isOwenSpeaking] État global:', {
      isInitialScenarioPlaying,
      isSpeaking,
      isTTSSpeaking,
      isHistoryPlaying,
      isProcessing
    });
  }, [isInitialScenarioPlaying, isSpeaking, isTTSSpeaking, isHistoryPlaying, isProcessing]);

  // 🎯 STATE MACHINE : Fonction de transition sécurisée
  const transitionTo = (newStage, reason = '') => {
    const timestamp = new Date().toISOString().substring(11, 23);
    console.log(`[${timestamp}] [VocalStage] ${vocalStage} → ${newStage} (${reason})`);
    console.log(`[${timestamp}] [ButtonStates] canUseMic: ${vocalStage === 'USER_TURN' && !isListening}, canUseTerminer: ${vocalStage === 'USER_TURN' && !isListening}`);
    setVocalStage(newStage);
  };
  
  // 🔧 FIX STREAMING: Helper pour garantir retour à USER_TURN après audio
  const ensureUserTurnAfterAudio = (reason = 'audio_ended') => {
    console.log(`[ensureUserTurnAfterAudio] Transitioning to USER_TURN (${reason})`);
    setIsSpeaking(false);
    transitionTo('USER_TURN', reason);
    
    // Nettoyer TOUS les flags audio/streaming des messages
    setConversation(prev => prev.map(msg => ({
      ...msg,
      isPlayingAudio: false,
      isStreaming: false
    })));
    
    // Nettoyer le timeout de sécurité
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }
  };
  
  // 🔧 FIX STREAMING: Setup timeout sécurité audio (25s max)
  const setupAudioSafetyTimeout = (audio, timeoutMs = 60000) => {
    // Nettoyer timeout précédent si existe
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
    }
    
    audioTimeoutRef.current = setTimeout(() => {
      console.warn('[SAFETY] Audio timeout after', timeoutMs, 'ms - forcing cleanup');
      if (audio) {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch (err) {
          console.warn('[SAFETY] Could not pause audio:', err);
        }
      }
      ensureUserTurnAfterAudio('audio_safety_timeout');
    }, timeoutMs);
    
    // Nettoyer le timeout dans onended
    const originalOnended = audio.onended;
    audio.onended = function(...args) {
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      if (originalOnended) {
        return originalOnended.apply(this, args);
      }
    };
    
    // Nettoyer le timeout dans onerror aussi
    const originalOnerror = audio.onerror;
    audio.onerror = function(...args) {
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      if (originalOnerror) {
        return originalOnerror.apply(this, args);
      }
    };
  };
  
  // 🎯 ÉTATS DÉRIVÉS (computed from vocalStage)
  // Note: isSpeaking reste un state (ligne 30) pour compatibilité avec code existant
  // 🎯 CORRECTION UX AUDIO: Désactiver boutons si Owen parle OU si pas en tour utilisateur
  // 🔒 P0 MOA (21 Jan 2026): Désactiver aussi si sessions épuisées
  // 🎯 Iter339 — Verrou UI centralisé sur sessionPhase (source de vérité unique).
  //   RÈGLE MÉTIER : verrouillage complet UNIQUEMENT en SESSION_CLOSED.
  //   Avant : STOP_CONVERSATION / EVALUATION_READY / EVALUATION_OPEN = UI libre
  //           (seuls micro et input sont désactivés — whitelist ← X toujours active).
  const isEvaluationLocked = evaluationFlowState.phase === 'analyzing' || evaluationFlowState.phase === 'speaking';
  const isTerminalLocked = sessionPhase === 'SESSION_CLOSED';
  const isUILocked = isEvaluationLocked || isTerminalLocked;
  const buttonsDisabled = isOwenCurrentlySpeaking || !['IDLE', 'USER_TURN'].includes(vocalStage) || sessionsExhausted || isUILocked;
  // Micro OFF dès que la conversation est stoppée (STOP_CONVERSATION / EVALUATION_READY / …)
  const canUseMic = sessionPhase === 'ACTIVE'
    && vocalStage === 'USER_TURN'
    && !isListening
    && !isOwenCurrentlySpeaking
    && !sessionsExhausted
    && !isUILocked;
  const isLoadingContent = ['SCENARIO_LOADING', 'PROCESSING_USER', 'FEEDBACK_FINAL'].includes(vocalStage);
  
  // 🎯 Iter339 — Bouton Évaluation : actif en ACTIVE (CTA secondaire avec
  // conditions classiques) et en EVALUATION_READY (CTA principal, gold).
  // INACTIF en STOP_CONVERSATION (Owen finit sa phrase), EVALUATION_OPEN
  // (modal ouvert), SESSION_CLOSED (session réellement close).
  const canUseTerminer = !isEvaluationLocked && (
    sessionPhase === 'EVALUATION_READY'
    || (
      sessionPhase === 'ACTIVE'
      && vocalStage === 'USER_TURN'
      && !isListening
      && !isOwenCurrentlySpeaking
      && !sessionsExhausted
    )
  );
  
  // 🎯 CORRECTIF FIN DE SESSION: Pendant FEEDBACK_FINAL et FEEDBACK_FINAL_STREAMING, seul le bouton X est actif
  const isSessionEnding = ['FEEDBACK_FINAL', 'FEEDBACK_FINAL_STREAMING', 'SESSION_ENDED'].includes(vocalStage);
  
  // 🎯 SYNCHRONISER isSpeaking avec vocalStage
  useEffect(() => {
    const shouldBeSpeaking = ['SCENARIO_STREAMING', 'OWEN_RESPONSE', 'FEEDBACK_FINAL_STREAMING'].includes(vocalStage);
    if (isSpeaking !== shouldBeSpeaking) {
      setIsSpeaking(shouldBeSpeaking);
      console.log(`[isSpeaking] Synced with vocalStage: ${shouldBeSpeaking} (stage: ${vocalStage})`);
    }
  }, [vocalStage, isSpeaking]);
  
  // 🔒 P0 UX MOA (21 Jan 2026): Ouvrir modal recharge automatiquement si sessions épuisées
  //   Iter339 — Modal en overlay non-destructif :
  //     • L'UI conversation reste montée → tous les states/refs survivent
  //     • L'audio en cours est PAUSÉ via OwenAudioBus (pas stopAll : on veut
  //       pouvoir reprendre via les contrôles UI au retour, sans race)
  useEffect(() => {
    if (sessionsExhausted && !showSessionsExhaustedModal) {
      console.log('[Iter339] Sessions épuisées détectées - overlay non-destructif');
      setShowSessionsExhaustedModal(true);
      try {
        // Import dynamique pour éviter un cycle d'import si jamais.
        import('../services/owenAudioBus').then(({ default: bus }) => {
          bus.pauseAll('credits_modal');
        }).catch(() => { /* noop */ });
      } catch (_) { /* noop */ }
    }
  }, [sessionsExhausted, showSessionsExhaustedModal]);
  
  // 🎯 Iter339 — Effets dérivés de sessionPhase (state machine unique).
  //   STOP_CONVERSATION : désactiver UNIQUEMENT l'input micro (anti-relance).
  //                       Owen continue de parler — AUCUN audio.pause() ici.
  //   SESSION_CLOSED    : exécuter TOUS les side-effects de clôture (audio
  //                       cleanup, transition vocalStage, refresh progress,
  //                       log session_outcomes).
  //   EVALUATION_READY / EVALUATION_OPEN : rien de spécial ici
  //                       (endSession gère déjà le stop audio résiduel).
  useEffect(() => {
    if (sessionPhase === 'STOP_CONVERSATION') {
      console.log('[sessionPhase effect] 🛑 STOP_CONVERSATION → micro off (audio Owen préservé)');
      try {
        if (isListening) cancelRecording();
      } catch (err) {
        console.warn('[sessionPhase effect] cancelRecording failed:', err);
      }
      return;
    }

    if (sessionPhase !== 'SESSION_CLOSED') return;

    console.log('[sessionPhase effect] 🔒 SESSION_CLOSED → cleanup final + side-effects');

    // A. Stop recording (garde-fou — micro devrait déjà être off depuis STOP_CONVERSATION)
    try {
      if (isListening) cancelRecording();
    } catch (err) {
      console.warn('[SESSION_CLOSED] cancelRecording failed:', err);
    }

    // B. Stop audio Owen définitivement (AUTORISÉ ici — règle produit)
    try {
      if (audioRef.current && typeof audioRef.current.pause === 'function') {
        audioRef.current.pause();
        audioRef.current = null;
      }
    } catch (err) {
      console.warn('[SESSION_CLOSED] audio.pause failed:', err);
    }

    // C. Reset transient states
    setIsSpeaking(false);
    setIsProcessing(false);

    // D. Force vocalStage terminal (pour legacy labels)
    if (vocalStage !== 'SESSION_ENDED') {
      transitionTo('SESSION_ENDED', 'session_phase_closed');
    }

    // E. Refresh preuves + progression (single source of truth)
    try {
      notifyEvaluationComplete({
        module_id: moduleId ? parseInt(moduleId) : undefined,
        reason: 'session_closed',
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      console.warn('[SESSION_CLOSED] notifyEvaluationComplete failed:', err);
    }
    try {
      fetchProgressSummary();
    } catch (err) {
      console.warn('[SESSION_CLOSED] fetchProgressSummary failed:', err);
    }

    // F. Log session_outcomes — idempotent via outcomeLoggedRef
    if (!outcomeLoggedRef.current) {
      outcomeLoggedRef.current = true;
      (async () => {
        try {
          const objective = progressSummary?.active_objective || {};
          const validated = Array.isArray(objective.validated_criteria) ? objective.validated_criteria : [];
          const allCriteria = Array.isArray(objective.all_criteria) ? objective.all_criteria : [];
          const remaining = allCriteria.filter((c) => !validated.includes(c));
          const durationSec = Math.max(0, Math.floor((Date.now() - sessionStartedAtRef.current) / 1000));
          // Type d'issue selon l'état réel à la clôture :
          //   completed       = tous les critères validés
          //   stopped_user    = l'utilisateur a explicitement arrêté (last_direction=transition + closure avant dernier critère)
          //   criterion_only  = au moins 1 critère validé mais pas tous
          //   early_exit      = fallback : aucun critère validé
          let outcomeType = 'early_exit';
          if (allCriteria.length && validated.length === allCriteria.length) outcomeType = 'completed';
          else if (lastDirectionRef.current === 'transition' && validated.length < allCriteria.length) outcomeType = 'stopped_user';
          else if (validated.length > 0) outcomeType = 'criterion_only';

          const payload = {
            user_id: localStorage.getItem('coachdia_user_id') || 'anonymous',
            session_id: `coach-vocal-${sessionStartedAtRef.current}`,
            module_id: moduleId ? parseInt(moduleId) : 0,
            objective_id: objective.objective_id || 'unknown',
            objective_title: objective.title || 'unknown',
            outcome_type: outcomeType,
            validated_criteria: validated,
            remaining_criteria: remaining,
            total_criteria_count: Math.max(1, allCriteria.length),
            turns_count: turnsCountRef.current || 0,
            duration_seconds: durationSec,
            last_verdict: lastVerdictRef.current,
            last_direction: lastDirectionRef.current,
          };
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/session_outcomes/log`;
          await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          console.log('[SESSION_OUTCOME] logged outcome=', outcomeType, 'session=', payload.session_id);
        } catch (err) {
          console.warn('[SESSION_OUTCOME] log failed (non-blocking):', err);
        }
      })();
    }
  }, [sessionPhase]);

  // 🔒 Iter339 — L'ancien garde-fou "vocalStage === SESSION_ENDED → setSessionTerminee(true)"
  //     a été RETIRÉ : trigger implicite qui verrouillait l'UI avant
  //     SESSION_CLOSED. vocalStage suit désormais sessionPhase, pas l'inverse.

  // 🎯 Iter339 — Fallback idempotent : quand Owen finit de parler (isSpeaking/
  //     isOwenCurrentlySpeaking passent à false) ET qu'on est en STOP_CONVERSATION,
  //     on bascule automatiquement vers EVALUATION_READY. Sans ce fallback,
  //     une latence backend sur l'event `evaluation_ready` bloquerait l'UX.
  //     La matrice rejette la transition si pas en STOP_CONVERSATION → idempotent.
  useEffect(() => {
    if (sessionPhase === 'STOP_CONVERSATION' && !isSpeaking && !isOwenCurrentlySpeaking) {
      transitionSessionPhase('EVALUATION_READY', 'audio_owen_ended_fallback');
    }
  }, [sessionPhase, isSpeaking, isOwenCurrentlySpeaking, transitionSessionPhase]);

  // 🎯 Iter339 — Transition EVALUATION_OPEN → SESSION_CLOSED quand le feedback
  //     TTS est consommé (phase 'completed'). Seule voie vers SESSION_CLOSED :
  //     clôture RÉELLE de la session après consultation effective du feedback.
  useEffect(() => {
    if (sessionPhase === 'EVALUATION_OPEN' && evaluationFlowState.phase === 'completed') {
      transitionSessionPhase('SESSION_CLOSED', 'evaluation_feedback_completed');
    }
  }, [sessionPhase, evaluationFlowState.phase, transitionSessionPhase]);

  
  // 🔧 Helper function: Extract first name only from full name
  const extractFirstName = (fullName) => {
    if (!fullName) return 'Manager';
    
    // Remove extra spaces and split by space
    const nameParts = fullName.trim().split(/\s+/);
    
    // Return first part only (prénom)
    const firstName = nameParts[0];
    
    console.log(`[extractFirstName] Full name: "${fullName}" → First name: "${firstName}"`);
    return firstName;
  };
  
  // Emojis pour les émotions
  const emotionEmojis = {
    empathique: '🤝',
    inspirant: '⚡',
    calme: '🧘',
    humoristique: '😊'
  };
  
  const emotionLabels = {
    empathique: 'Empathique',
    inspirant: 'Inspirant',
    calme: 'Calme',
    humoristique: 'Humoristique'
  };
  
  // 🎯 V16 - Labels de posture Owen
  const postureLabels = {
    COACH: 'Posture Coach',
    ROLEPLAY: 'Jeu de rôle'
  };
  
  const postureEmojis = {
    COACH: '🎯',
    ROLEPLAY: '🎭'
  };
  
  const levelEmojis = {
    'découverte': '🌱',
    'application': '🎯',
    'maîtrise': '⚡',
    'leadership_influence': '👑',
    'mentor_architecte': '🏛️'
  };
  
  const levelLabels = {
    'découverte': 'Découverte',
    'application': 'Application',
    'maîtrise': 'Maîtrise',
    'leadership_influence': 'Leadership d\'Influence',
    'mentor_architecte': 'Mentor / Architecte'
  };
  
  // NOUVELLE FONCTION: Récupérer la progression et recommandations
  const fetchProgressSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // ✅ CRITICAL FIX: Use safeFetchJSON to prevent "Response body already used"
      const data = await safeFetchJSON(`${BACKEND_URL}/api/coach/progress-summary`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setProgressSummary(data);
      setUserLevel(data.competence_level);
      setRecommendations(data.next_recommendations || []);
    } catch (err) {
      console.error('Erreur récupération progression:', err);
    }
  };
  
  // Démarrer l'enregistrement audio
  const startListening = async () => {
    // 🔒 P0 UX MOA (21 Jan 2026): Guard sessions épuisées - ouvrir modal, pas d'appel backend
    if (sessionsExhausted) {
      console.log('[startListening] 🔒 GUARD: Sessions épuisées - ouverture modal recharge');
      setShowSessionsExhaustedModal(true);
      return;
    }
    
    // ✅ GARDE ANTI-RÉENTRANCE: Empêcher plusieurs enregistrements simultanés
    if (isListening) {
      console.warn('[startListening] ⚠️ BLOCKED - Already listening');
      return;
    }
    
    // 🔧 P0 FIX C: Garde renforcée vocalStage avec log explicite
    if (vocalStage !== 'USER_TURN' && vocalStage !== 'IDLE') {
      console.warn(`[startListening] ⚠️ GARDE C: vocalStage=${vocalStage}, attendu USER_TURN ou IDLE`);
      toast.info('⏳ Attends qu\'Owen finisse...', { duration: 2000 });
      return;
    }
    
    // 🔧 P0 FIX C: Garde renforcée Owen parle encore
    if (isOwenCurrentlySpeaking) {
      console.warn('[startListening] ⚠️ GARDE C: Owen parle encore (isOwenCurrentlySpeaking=true)');
      toast.info('🔊 Owen parle encore, patiente un instant...', { duration: 2000 });
      return;
    }
    
    if (isProcessing) {
      console.warn('[startListening] ⚠️ BLOCKED - Processing in progress');
      toast.warning('Attends qu\'Owen finisse de parler...');
      return;
    }
    
    if (isSpeaking) {
      console.warn('[startListening] ⚠️ BLOCKED - Owen is speaking');
      toast.info('Laisse Owen finir de parler avant d\'enregistrer');
      return;
    }
    
    try {
      console.log('[startListening] ✅ Starting audio recording...');
      console.log('[startListening] 🔧 P0 FIX C: États validés - vocalStage:', vocalStage, 'isOwenCurrentlySpeaking:', isOwenCurrentlySpeaking);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; // Store stream reference
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      isCancelledRef.current = false; // Reset cancellation flag
      // 🔒 P0 — Capture timestamp & mime réel (audit qualité Whisper)
      recordingStartTimeRef.current = Date.now();
      recordedMimeTypeRef.current = mediaRecorder.mimeType || null;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        console.log('[mediaRecorder.onstop] Triggered');
        
        // Stop audio detection
        stopAudioDetection();
        
        // ✅ GARDE CRITIQUE: Empêcher le traitement multiple si onstop appelé plusieurs fois
        if (isProcessingAudioRef.current) {
          console.warn('[mediaRecorder.onstop] ⚠️ BLOCKED - processAudio already running, skipping');
          return;
        }
        
        // Only process if NOT cancelled and we have audio chunks
        if (!isCancelledRef.current && audioChunksRef.current.length > 0) {
          console.log('[mediaRecorder.onstop] Processing audio blob...');
          
          // 🔒 P0 OPTIMISTIC UI: Commit immédiat du message utilisateur AVANT transcription
          // L'utilisateur voit instantanément son message, la transcription est découplée
          const optimisticMessageId = `user-${Date.now()}-optimistic`;
          const optimisticMessage = {
            id: optimisticMessageId,
            role: 'user',
            content: '🎤 Transcription en cours...',
            timestamp: new Date().toISOString(),
            isTranscribing: true  // Flag pour identifier le message temporaire
          };
          setConversation(prev => [...prev, optimisticMessage]);
          console.log('[mediaRecorder.onstop] 🚀 P0 OPTIMISTIC UI: Message utilisateur ajouté IMMÉDIATEMENT');
          
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
          await processAudio(audioBlob, optimisticMessageId);  // Passer l'ID pour mise à jour
        } else {
          console.log('[mediaRecorder.onstop] Skipping processAudio - cancelled or no chunks');
        }
        
        // Arrêter le stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };
      
      mediaRecorder.start();
      setIsListening(true);
      setError(null);
      setRecordingDuration(0);
      
      // ✅ Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
      // 🎨 NEW: Start audio level detection
      await startAudioDetection();
      console.log('🎤 Audio level detection started');
    } catch (err) {
      console.error('Erreur microphone:', err);
      setError('Impossible d\'accéder au microphone. Vérifiez les permissions.');
      toast.error('⚠️ Impossible d\'accéder au microphone', { duration: 5000 });
    }
  };
  
  // Arrêter l'enregistrement (IDEMPOTENT)
  const stopListening = () => {
    // ✅ Garde idempotente - ne faire qu'une fois
    if (!isListening) {
      console.log('[stopListening] Already stopped, ignoring');
      return;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('[stopListening] Stopping MediaRecorder');
      mediaRecorderRef.current.stop();
      setIsListening(false);
      
      // ✅ Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      // 🎨 NEW: Stop audio level detection
      stopAudioDetection();
      console.log('🔇 Audio level detection stopped');
    } else {
      console.log('[stopListening] MediaRecorder already inactive');
      setIsListening(false);
    }
  };
  
  // Annuler l'enregistrement en cours (IDEMPOTENT)
  const cancelRecording = () => {
    if (!isListening) {
      console.log('[cancelRecording] Not listening, nothing to cancel');
      return;
    }
    
    console.log('[cancelRecording] Cancelling recording...');
    
    // Mark as cancelled to prevent processing
    isCancelledRef.current = true;
    
    // Clear audio chunks immediately
    audioChunksRef.current = [];
    
    // Stop audio detection
    stopAudioDetection();
    
    // Stop the recorder (idempotent check)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    
    // Stop all media tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // Reset state
    setIsListening(false);
    setTranscript('');
    setRecordingDuration(0);
    
    // ✅ Clear recording timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    
    // ✅ Show cancellation message with auto-dismiss (5s)
    toast.info('🔄 Enregistrement annulé', { duration: 5000 });
  };
  
  // ✅ ÉTAPE 5: Adjust pathway after session (continuous learning)
  const adjustPathwayAfterSession = async (userId, moduleId, conversationContext) => {
    try {
      console.log('[adjustPathway] 🎯 Adjusting pathway after vocal session');
      
      // Extract session data
      const emotions = [...new Set(conversationContext
        .filter(msg => msg.emotion)
        .map(msg => msg.emotion))];
      
      const sessionDuration = conversationContext.length > 0 
        ? (new Date(conversationContext[conversationContext.length - 1].timestamp) - 
           new Date(conversationContext[0].timestamp)) / (1000 * 60) // minutes
        : 0;
      
      const sessionData = {
        emotions: emotions,
        topics: [], // Could extract from conversation content
        duration_minutes: Math.max(1, Math.round(sessionDuration)),
        message_count: conversationContext.length
      };
      
      // Call adjustment endpoint
      // 🔒 P0 FIX: Injection header X-Execution-Context-Id
      const response = await fetch(`${API}/owen/pathway/adjust_after_session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          ...(window.__currentExecutionContextId && { 
            'X-Execution-Context-Id': window.__currentExecutionContextId 
          })
        },
        body: JSON.stringify({
          user_id: userId,
          session_type: 'vocal',
          module_id: moduleId,
          session_data: sessionData
        })
      });
      
      if (!response.ok) {
        console.log('[adjustPathway] ⚠️ Non-critical: pathway adjustment failed');
        return;
      }
      
      const data = await response.json();
      
      if (data.success && data.recommendations && data.recommendations.length > 0) {
        console.log('[adjustPathway] ✅ Pathway adjusted. Recommendations:', data.recommendations);
        
        // Update recommendations state for UI
        setRecommendations(data.recommendations);
        
        // Show subtle notification
        toast.success(`🎯 Owen a ajusté votre parcours personnalisé`, {
          duration: 3000,
          position: 'bottom-right'
        });
      }
      
    } catch (error) {
      // Non-blocking: log but don't show error to user
      console.log('[adjustPathway] Non-critical error:', error.message);
    }
  };

  // Terminer la session et obtenir un feedback complet V7.1
  const endSession = async () => {
    // 🎯 FLOW ÉVALUATION — Séquence STRICTE (Fév 2026)
    //   1. phase='analyzing' IMMÉDIATEMENT (verrouille UI + affiche "Analyse en cours")
    //   2. phase='speaking'  UNIQUEMENT sur audio.onplay
    //   3. phase='completed' UNIQUEMENT sur audio.onended → transition SESSION_CLOSED
    // 🎯 Iter339 — Transition explicite vers EVALUATION_OPEN (user a cliqué).
    //   La matrice rejette si on n'est ni en ACTIVE ni en EVALUATION_READY.
    console.log('[endSession] 🎯 Starting session end - phase=analyzing');
    // Si la session n'est pas encore passée par STOP_CONVERSATION/EVALUATION_READY
    // (cas : user clique Évaluation avant fin de conversation), on route d'abord.
    if (sessionPhaseRef.current === 'ACTIVE') {
      transitionSessionPhase('STOP_CONVERSATION', 'user_click_evaluation_from_active');
    }
    if (sessionPhaseRef.current === 'STOP_CONVERSATION') {
      transitionSessionPhase('EVALUATION_READY', 'user_click_evaluation_from_stop');
    }
    transitionSessionPhase('EVALUATION_OPEN', 'user_click_evaluation');
    setEvaluationFlowState({ phase: 'analyzing' });
    transitionTo('FEEDBACK_FINAL', 'user_requested_end');
    
    // Stop any playing audio
    if (audioRef.current) {
      if (typeof audioRef.current.pause === 'function') {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsSpeaking(false);
    }
    
    setIsProcessing(true);
    
    try {
      // ✅ PHASE 1: Validate and sanitize all fields
      const sanitizedUserId = String(user?.user_id || user?.id || 'anonymous');
      const sanitizedModuleId = moduleId ? String(moduleId) : '';
      const sanitizedUserName = userName ? String(userName) : 'Manager';
      const sanitizedModuleName = moduleName ? String(moduleName) : 'Coach DIA';
      
      // ✅ PHASE 2: Prepare conversation history for V7.1 feedback
      const conversationHistory = conversation.map(msg => ({
        role: String(msg.role || ''),
        content: String(msg.content || '')
      }));
      
      console.log('[endSession V7.1] Validation complete:', {
        userId: sanitizedUserId,
        moduleId: sanitizedModuleId,
        userName: sanitizedUserName,
        conversationLength: conversationHistory.length
      });
      
      // 🏆 FEEDBACK PREMIUM: Utiliser le bon endpoint selon le mode
      let data;
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      // ═══════════════════════════════════════════════════════════════════════════
      // 🎭 DÉTECTION ROLEPLAY PRIORITAIRE (AVANT TOUTE CONDITION DE MODE)
      // ═══════════════════════════════════════════════════════════════════════════
      // RÈGLE ABSOLUE: Si la session contient un jeu de rôle, TOUJOURS utiliser
      // le feedback premium trajectoire, quel que soit le mode (free, guided, module)
      // ═══════════════════════════════════════════════════════════════════════════
      
      const conversationText = conversationHistory.map(m => m.content || '').join(' ');
      const conversationTextLower = conversationText.toLowerCase();
      
      // 🎭 Liste exhaustive des marqueurs de jeu de rôle
      const roleplayMarkers = [
        // Marqueurs explicites
        "j'incarne",
        "🎭",
        "personnage",
        "simulation",
        "jeu de rôle",
        "roleplay",
        // Noms de personnages connus
        "marc dupont",
        "sophie martin",
        "thomas bernard",
        "claire moreau",
        "pierre leroy",
        "julie chen",
        "alexandre durand",
        "marie lambert",
        "alex",  // Personnage courant
        "senior frustré",
        "perfectionniste",
        "démotivé",
        "surconfiant",
        "résistant au changement",
        // Contextes de roleplay
        "je suis votre",
        "vous êtes face à",
        "situation :",
        "votre collaborateur",
        "votre manager",
        "votre collègue",
        // Patterns de conversation roleplay
        "besoin de plus de structure",
        "manque de visibilité",
        "préoccupations d'alex",
        "reformulant les préoccupations"
      ];
      
      // 🎭 DÉTECTION ROLEPLAY MULTI-SOURCE
      // Source 1: État stocké dans roleplaySessionData
      // Source 2: Contexte avec type roleplay
      // Source 3: Marqueurs textuels dans la conversation
      // Source 4: 🆕 Posture ROLEPLAY dans les messages de conversation
      const hasRoleplayPosture = conversation.some(msg => msg.posture === 'ROLEPLAY');
      
      const isRolePlaySession = 
        roleplaySessionData !== null ||
        (coachPremiumContext?.type === 'roleplay') ||
        (coachPremiumContext?.character_key) ||
        hasRoleplayPosture ||  // 🆕 Détection par posture
        roleplayMarkers.some(marker => conversationTextLower.includes(marker.toLowerCase()));
      
      console.log('[endSession] 🎭 ROLEPLAY DETECTION (PRIORITAIRE):', {
        roleplaySessionData: !!roleplaySessionData,
        contextType: coachPremiumContext?.type,
        contextCharKey: coachPremiumContext?.character_key,
        hasRoleplayPosture: hasRoleplayPosture,  // 🆕
        markersFound: roleplayMarkers.filter(m => conversationTextLower.includes(m.toLowerCase())),
        isRolePlaySession: isRolePlaySession,
        isFreeMode: isFreeMode,
        isCoachPremiumMode: isCoachPremiumMode
      });
      
      // 🎯 EXTRAIRE LE CHARACTER_KEY de plusieurs sources
      let characterKey = roleplaySessionData?.character_key || coachPremiumContext?.character_key || null;
      
      // Fallback: extraire depuis la conversation
      if (!characterKey && isRolePlaySession) {
        // Patterns pour détecter le personnage
        const characterPatterns = [
          /j'incarne\s*:?\s*([a-zéèêàùâîôûç\s]+?)(?:,|\.|!|\n|$)/i,
          /je suis\s+([a-zéèêàùâîôûç]+\s+[a-zéèêàùâîôûç]+)/i,
          /personnage\s*:?\s*([a-zéèêàùâîôûç\s]+?)(?:,|\.|!|\n|$)/i,
          /préoccupations\s+d['']([a-zéèêàùâîôûç]+)/i  // "préoccupations d'Alex"
        ];
        
        for (const pattern of characterPatterns) {
          const match = conversationText.match(pattern);
          if (match) {
            // Mapper les noms aux clés valides du backend
            // Clés disponibles: senior_frustre, perfectionniste_epuisee, nouveau_anxieux,
            // conflictuel_silencieux, demotivee_discrete, resistant_feedback,
            // communicateur_indirect, leader_conteste, burnout_cache,
            // opposant_systematique, promoteur_frustre, nouvelle_manager
            const nameToKey = {
              // Noms explicites → clés backend
              'thomas': 'senior_frustre',
              'marc dupont': 'senior_frustre',
              'sophie': 'perfectionniste_epuisee',
              'sophie martin': 'perfectionniste_epuisee',
              'alex': 'demotivee_discrete',
              'alexandre': 'senior_frustre',
              'alexandre durand': 'senior_frustre',
              'claire': 'leader_conteste',
              'claire moreau': 'leader_conteste',
              'pierre': 'resistant_feedback',
              'pierre leroy': 'resistant_feedback',
              'julie': 'nouveau_anxieux',
              'julie chen': 'nouveau_anxieux',
              'marie': 'perfectionniste_epuisee',
              'marie lambert': 'perfectionniste_epuisee',
              // Descriptions → clés
              'senior frustré': 'senior_frustre',
              'perfectionniste': 'perfectionniste_epuisee',
              'démotivé': 'demotivee_discrete',
              'démotivée': 'demotivee_discrete',
              'anxieux': 'nouveau_anxieux',
              'résistant': 'resistant_feedback',
              'conflictuel': 'conflictuel_silencieux',
              'burnout': 'burnout_cache'
            };
            const matchedName = match[1].toLowerCase().trim();
            characterKey = nameToKey[matchedName] || matchedName.replace(/\s+/g, '_');
            console.log('[endSession] 🎭 Detected character:', matchedName, '→', characterKey);
            break;
          }
        }
        
        // Fallback final
        if (!characterKey) {
          characterKey = 'senior_frustre';
          console.log('[endSession] 🎭 Using default character key');
        }
      }
      
      // 🎯 DÉTECTER L'ACTE FINAL
      let finalAct = 'RESOLUTION';
      const lastAssistantMsg = [...conversationHistory].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMsg?.metadata?.act) {
        finalAct = lastAssistantMsg.metadata.act;
      }
      
      // ═══════════════════════════════════════════════════════════════════════════
      // 🏆 ROUTING DU FEEDBACK (PRIORITÉ: DÉCISIONNEL QUALIOPI POUR TOUS)
      // ═══════════════════════════════════════════════════════════════════════════
      // 🔒 P0 MIGRATION: TOUS les feedbacks vocaux utilisent le moteur DÉCISIONNEL
      // [DECISIONAL FEEDBACK MODE ACTIVE – NO LEGACY ALLOWED]
      // ═══════════════════════════════════════════════════════════════════════════
      
      if (isRolePlaySession) {
        // ═══════════════════════════════════════════════════════════════════════
        // 🔒 MODE ROLEPLAY → FEEDBACK DÉCISIONNEL OWEN (Qualiopi)
        // ═══════════════════════════════════════════════════════════════════════
        // MIGRATION P0: Suppression du feedback trajectory legacy
        // Utilisation EXCLUSIVE du moteur décisionnel pour conformité Qualiopi
        // ═══════════════════════════════════════════════════════════════════════
        console.log('[endSession] 🔒 [DECISIONAL FEEDBACK MODE ACTIVE – NO LEGACY ALLOWED]');
        console.log('[endSession] 🎭 ROLEPLAY MODE → Routing to DECISIONAL feedback (Qualiopi)');
        console.log('[endSession] 🎭 Character:', characterKey, '| Final act:', finalAct);
        
        const sessionId = `roleplay-${Date.now()}-${sanitizedUserId}`;
        
        const decisionalFeedbackRequest = {
          module_id: parseInt(sanitizedModuleId.replace('M', ''), 10) || 0,
          session_id: sessionId,
          session_type: 'vocal_roleplay',
          conversation_history: conversationHistory,
          // 🔒 D2 MOA (Déc 2025): Transmettre objective_id pour cohérence mid-session ↔ feedback
          objective_id: activeObjective?.id || null,
          // 🔒 P0 MOA (Jan 2026): Timestamp début de session pour isolation temporelle du feedback
          session_start_timestamp: initTimestampRef.current || Date.now(),
          // 🔒 P1.5-C MOA (Jan 2026): Progression initiale FIGÉE au démarrage de la session
          // Owen utilisera cette valeur pour annoncer "Ton niveau est passé de X% à Y%"
          initial_progress_percentage: initialProgressRef.current,
          // ═══════════════════════════════════════════════════════════════════════
          // 🔒 P0 MOA (Jan 2026): SESSION_RUN_ID — CHAÎNE DE TRAÇABILITÉ CERTIFIANTE
          // ═══════════════════════════════════════════════════════════════════════
          // ID unique reçu du backend via SSE event 'session_started'.
          // OBLIGATOIRE pour isoler les preuves de CETTE session.
          // ═══════════════════════════════════════════════════════════════════════
          session_run_id: sessionRunIdRef.current || null,
          // Métadonnées roleplay pour enrichir le feedback décisionnel
          roleplay_context: {
            character_key: characterKey || 'senior_frustre',
            final_act: finalAct,
            difficulty_level: coachPremiumContext?.difficulty || 'standard'
          }
        };
        
        console.log('[endSession] 🔒 Decisional request (roleplay):', {
          module_id: decisionalFeedbackRequest.module_id,
          session_id: sessionId,
          // 🔒 D2 MOA: Log objective_id
          objective_id: decisionalFeedbackRequest.objective_id,
          // 🔒 P1.5-C MOA: Log progression initiale
          initial_progress: decisionalFeedbackRequest.initial_progress_percentage,
          // 🔒 P0 MOA (Jan 2026): Log session_run_id
          session_run_id: decisionalFeedbackRequest.session_run_id,
          session_type: 'vocal_roleplay',
          history_length: conversationHistory.length,
          character: characterKey
        });
        
        // 🚀 RETRY AUTOMATIQUE: 3 tentatives avec timeout progressif
        // 🔧 P0 (Fev 2026) — Bumpé [60,90,120] → [90,120,180] pour rester
        // cohérent avec backend DEFAULT_TIMEOUT=120s + apiHelpers 180s.
        const maxRetries = 3;
        const timeouts = [90000, 120000, 180000]; // 90s, 120s, 180s
        let lastError = null;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          const currentTimeout = timeouts[attempt - 1];
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), currentTimeout);
          
          try {
            console.log(`[endSession] 🔄 Decisional feedback attempt ${attempt}/${maxRetries} (timeout: ${currentTimeout/1000}s)`);
            
            // 🔒 P0 FIX: Injection header X-Execution-Context-Id
            const response = await fetch(`${BACKEND_URL}/api/pedagogical/session/decisional-feedback`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...(window.__currentExecutionContextId && { 
                  'X-Execution-Context-Id': window.__currentExecutionContextId 
                })
              },
              body: JSON.stringify(decisionalFeedbackRequest),
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
              throw new Error(`Decisional Feedback generation failed: ${response.status}`);
            }
            
            const decisionalData = await response.json();
            console.log(`[endSession] ✅ Decisional feedback success on attempt ${attempt}`);
            
            // Extraire le feedback formaté
            const formattedFeedback = decisionalData.formatted_feedback || decisionalData.decision?.formatted_feedback || '';
            
            data = {
              feedback_text: formattedFeedback,
              progress_percent: decisionalData.progress_percent,
              is_objective_validated: decisionalData.is_objective_validated,
              status: decisionalData.status,
              _isDecisional: true,
              _isRoleplayPremium: true
            };
            
            break; // Succès, sortir de la boucle
            
          } catch (fetchError) {
            clearTimeout(timeoutId);
            lastError = fetchError;
            
            if (fetchError.name === 'AbortError') {
              console.warn(`[endSession] ⏱️ Attempt ${attempt} timeout (${currentTimeout/1000}s)`);
            } else {
              console.error(`[endSession] ❌ Attempt ${attempt} error:`, fetchError.message);
            }
            
            // Si c'est la dernière tentative, lever l'erreur
            if (attempt === maxRetries) {
              console.error('[endSession] 🚫 All retry attempts failed');
              throw lastError;
            }
            
            // Attendre avant le retry (backoff progressif)
            const backoffDelay = attempt * 1000; // 1s, 2s, 3s
            console.log(`[endSession] ⏳ Waiting ${backoffDelay}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
          }
        }
        
        // 🔒 ROLEPLAY: Feedback DÉCISIONNEL (structure Qualiopi)
        const feedbackMessage = {
          role: 'assistant',
          content: data.feedback_text,
          emotion: 'empathique',
          posture: 'DECISIONAL_FEEDBACK',
          timestamp: new Date().toISOString(),
          isFinalFeedback: true,
          isDecisional: true,
          metadata: {
            progress_percent: data.progress_percent,
            is_objective_validated: data.is_objective_validated,
            character_played: characterKey,
            final_act: finalAct,
            feedback_mode: 'DECISIONAL_QUALIOPI'
          }
        };
        
        setConversation(prev => [...prev, feedbackMessage]);
        setCurrentEmotion('empathique');
        
        console.log('[endSession] 🔒 Decisional Premium Feedback received:', data.feedback_text?.substring(0, 100) + '...');
        
        // 🎯 GÉNÉRATION TTS: Feedback décisionnel COMPLET
        // Le feedback décisionnel DOIT être parlé, pas juste affiché
        // 🚀 P1 OPTIM: Utiliser /text-to-speech-hd-fast (sans Whisper) pour gagner ~5-10s
        try {
          console.log('[endSession] 🚀 [P1 OPTIM] Generating TTS HD (sans Whisper) for decisional feedback...');
          const ttsStartTime = Date.now();
          // 🔒 P0 FIX: AbortController pour annuler TTS à la sortie de page
          ttsAbortControllerRef.current = new AbortController();
          // 🔒 P0 FIX: Injection header X-Execution-Context-Id
          const decisionalTtsResponse = await fetch(`${BACKEND_URL}/api/ai/text-to-speech-hd-fast`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              ...(window.__currentExecutionContextId && { 
                'X-Execution-Context-Id': window.__currentExecutionContextId 
              })
            },
            body: JSON.stringify({
              text: data.feedback_text,
              emotion: 'empathique',
              mode: 'certifiant'
            }),
            signal: ttsAbortControllerRef.current.signal
          });
          
          // 🔒 P0 FIX: Ne pas traiter si démonté
          if (!isMountedRef.current) {
            console.log('[endSession] ⏹️ TTS response received but component unmounted, ignoring');
            return;
          }
          
          if (decisionalTtsResponse.ok) {
            const decisionalTtsData = await decisionalTtsResponse.json();
            // Stocker l'audio URL pour lecture ultérieure
            data.audio_url = decisionalTtsData.audio_url;
            const ttsDuration = Date.now() - ttsStartTime;
            console.log(`[endSession] ✅ [P1 OPTIM] TTS HD généré en ${ttsDuration}ms (Whisper skipped: ${decisionalTtsData.whisper_skipped})`);
          } else {
            console.warn('[endSession] ⚠️ Decisional TTS failed, feedback will be text-only');
          }
        } catch (ttsError) {
          console.error('[endSession] ⚠️ Decisional TTS error:', ttsError);
        }
        
        // 🔒 VERROU: En roleplay, JAMAIS de phrase générique additionnelle
        // Le feedback décisionnel contient déjà une structure complète
        data._isRoleplayPremium = true;  // Flag pour éviter la phrase générique
        data._isDecisional = true;       // Flag pour identifier le mode décisionnel
        
      } else if (isFreeMode || isCoachPremiumMode) {
        // ═══════════════════════════════════════════════════════════════════════
        // 🔒 MODE TEC-1 LIBRE → FEEDBACK DÉCISIONNEL OWEN (Qualiopi)
        // ═══════════════════════════════════════════════════════════════════════
        // MIGRATION P0: Tous les modes utilisent le feedback décisionnel
        // [DECISIONAL FEEDBACK MODE ACTIVE – NO LEGACY ALLOWED]
        // ═══════════════════════════════════════════════════════════════════════
        console.log('[endSession] 🔒 [DECISIONAL FEEDBACK MODE ACTIVE – NO LEGACY ALLOWED]');
        console.log('[endSession] 🏆 TEC-1 MODE → Routing to DECISIONAL feedback (Qualiopi)');
        
        const sessionId = `tec1-${Date.now()}-${sanitizedUserId}`;
        
        const decisionalFeedbackRequest = {
          module_id: parseInt(sanitizedModuleId.replace('M', ''), 10) || 0,
          session_id: sessionId,
          session_type: 'vocal_tec1',
          conversation_history: conversationHistory,
          user_first_name: userName || 'ami',
          // 🔒 D2 MOA (Déc 2025): Transmettre objective_id pour cohérence universelle
          objective_id: activeObjective?.id || null,
          // 🔒 P0 MOA (Jan 2026): Timestamp début de session pour isolation temporelle du feedback
          session_start_timestamp: initTimestampRef.current || Date.now(),
          // 🔒 P1.5-C MOA (Jan 2026): Progression initiale FIGÉE au démarrage de la session
          initial_progress_percentage: initialProgressRef.current,
          // 🔒 P0 MOA (Jan 2026): SESSION_RUN_ID — Chaîne de traçabilité certifiante
          session_run_id: sessionRunIdRef.current || null
        };
        
        console.log('[endSession] 🔒 D2 MOA: Decisional request avec objective_id:', decisionalFeedbackRequest.objective_id);
        console.log('[endSession] 🔒 P1.5-C MOA: Progression initiale:', decisionalFeedbackRequest.initial_progress_percentage, '%');
        console.log('[endSession] 🔒 P0 MOA: session_run_id:', decisionalFeedbackRequest.session_run_id);
        
        // 🔒 P0 FIX: Injection header X-Execution-Context-Id
        const response = await fetch(`${BACKEND_URL}/api/pedagogical/session/decisional-feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(window.__currentExecutionContextId && { 
              'X-Execution-Context-Id': window.__currentExecutionContextId 
            })
          },
          body: JSON.stringify(decisionalFeedbackRequest)
        });
        
        if (!response.ok) {
          throw new Error(`Decisional Feedback generation failed: ${response.status}`);
        }
        
        const decisionalData = await response.json();
        const formattedFeedback = decisionalData.formatted_feedback || decisionalData.decision?.formatted_feedback || '';
        
        // 🔒 P0 FIX (Jan 2026): Utiliser les blocs parsés par le backend (NE PAS re-parser)
        // Le backend envoie feedback_blocks avec observation, axe_evolution, relance déjà extraits
        const backendBlocks = decisionalData.feedback_blocks || {};
        const p12Structure = {
          observation: backendBlocks.observation || '',
          axe_evolution: backendBlocks.axe_evolution || '',
          relance: backendBlocks.relance || ''
        };
        
        // Log pour debug
        console.log('[endSession] 🔒 P0 FIX: Using backend-parsed blocks:', {
          observation: p12Structure.observation ? `${p12Structure.observation.substring(0, 50)}...` : '(VIDE)',
          axe_evolution: p12Structure.axe_evolution ? `${p12Structure.axe_evolution.substring(0, 50)}...` : '(VIDE)',
          relance: p12Structure.relance ? `${p12Structure.relance.substring(0, 50)}...` : '(VIDE)'
        });
        
        // Fallback sur parsing local si backend n'a pas envoyé les blocs
        const finalP12Structure = (p12Structure.observation || p12Structure.axe_evolution || p12Structure.relance)
          ? p12Structure
          : parseP12FeedbackFallback(formattedFeedback);
        
        if (!backendBlocks.observation && !backendBlocks.axe_evolution && !backendBlocks.relance) {
          console.warn('[endSession] ⚠️ Backend blocks empty - falling back to local parsing');
        }
        
        data = {
          feedback: formattedFeedback,
          metadata: {
            progress_percent: decisionalData.progress_percent,
            is_objective_validated: decisionalData.is_objective_validated,
            feedback_mode: 'DECISIONAL_QUALIOPI'
          },
          _isDecisional: true,
          _isP12Libre: decisionalData.p12_mode_libre || true
        };
        
        const feedbackMessage = {
          role: 'assistant',
          content: data.feedback,
          emotion: 'empathique',
          posture: 'DECISIONAL_FEEDBACK',
          timestamp: new Date().toISOString(),
          isFinalFeedback: true,
          isDecisional: true,
          isP12Libre: true,  // 🎯 P1.2: Flag pour affichage en blocs
          p12Structure: finalP12Structure,  // 🔒 P0 FIX: Blocs du backend (pas re-parsés)
          metadata: data.metadata
        };
        
        setConversation(prev => [...prev, feedbackMessage]);
        setCurrentEmotion('empathique');
        
        console.log('[endSession] 🔒 Decisional Feedback (TEC-1) received:', data.feedback?.substring(0, 100) + '...');
        console.log('[endSession] 🎯 [P1.2 LIBRE] feedback_structured=true, isP12Libre=true, blocks_from_backend=true');
        
        // 🎯 P1.2 ACTION 2: Générer TTS pour le feedback libre (OBLIGATOIRE)
        // 🚀 P1 OPTIM: Utiliser /text-to-speech-hd-fast (sans Whisper) pour gagner ~5-10s
        try {
          console.log('[endSession] 🚀 [P1 OPTIM] [P1.2 TTS] Generating TTS HD (sans Whisper) for libre feedback...');
          const ttsStartTime = Date.now();
          // 🔒 P0 FIX: AbortController pour annuler TTS à la sortie de page
          ttsAbortControllerRef.current = new AbortController();
          // 🔒 P0 FIX: Injection header X-Execution-Context-Id
          const ttsResponse = await fetch(`${BACKEND_URL}/api/ai/text-to-speech-hd-fast`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              ...(window.__currentExecutionContextId && { 
                'X-Execution-Context-Id': window.__currentExecutionContextId 
              })
            },
            body: JSON.stringify({
              text: formattedFeedback,
              emotion: 'empathique',
              mode: 'free'
            }),
            signal: ttsAbortControllerRef.current.signal
          });
          
          // 🔒 P0 FIX: Ne pas traiter si démonté
          if (!isMountedRef.current) {
            console.log('[endSession] ⏹️ TTS response received but component unmounted, ignoring');
            return;
          }
          
          if (ttsResponse.ok) {
            const ttsData = await ttsResponse.json();
            data.audio_url = ttsData.audio_url;
            const ttsDuration = Date.now() - ttsStartTime;
            console.log(`[endSession] ✅ [P1 OPTIM] [P1.2 TTS] TTS HD généré en ${ttsDuration}ms (Whisper skipped: ${ttsData.whisper_skipped})`);
          } else {
            console.warn('[endSession] ⚠️ [P1.2 TTS] tts_triggered=false - TTS generation failed');
          }
        } catch (ttsError) {
          console.error('[endSession] ❌ [P1.2 TTS] tts_triggered=false - Error:', ttsError);
        }
        
      } else {
        // ═══════════════════════════════════════════════════════════════════════
        // 🔒 MODE PÉDAGOGIQUE STANDARD - FEEDBACK DÉCISIONNEL OWEN
        // ═══════════════════════════════════════════════════════════════════════
        // GOUVERNANCE QUALIOPI: Owen = UNIQUE autorité décisionnelle
        // - Décision basée sur preuves Pinecone + MongoDB
        // - Pourcentages verrouillés (40%, 60%, 100%)
        // - Persistance tracée et opposable
        // ═══════════════════════════════════════════════════════════════════════
        console.log('[endSession] 🔒 DECISIONAL MODE - Using Owen decisional feedback (Qualiopi)');
        
        const sessionId = `session-${Date.now()}-${sanitizedUserId}`;
        
        const decisionalFeedbackRequest = {
          module_id: parseInt(sanitizedModuleId.replace('M', ''), 10) || 0,
          session_id: sessionId,
          session_type: 'vocal',
          conversation_history: conversationHistory,
          // 🔒 D2 MOA (Déc 2025): Transmettre objective_id pour cohérence universelle
          objective_id: activeObjective?.id || null,
          // 🔒 P0 MOA (Jan 2026): Timestamp début de session pour isolation temporelle du feedback
          session_start_timestamp: initTimestampRef.current || Date.now(),
          // 🔒 P1.5-C MOA (Jan 2026): Progression initiale FIGÉE au démarrage de la session
          initial_progress_percentage: initialProgressRef.current,
          // 🔒 P0 MOA (Jan 2026): SESSION_RUN_ID — Chaîne de traçabilité certifiante
          session_run_id: sessionRunIdRef.current || null
        };
        
        console.log('[endSession] 🔒 Decisional request:', {
          module_id: decisionalFeedbackRequest.module_id,
          session_id: sessionId,
          // 🔒 D2 MOA: Log objective_id
          objective_id: decisionalFeedbackRequest.objective_id,
          // 🔒 P1.5-C MOA: Log progression initiale
          initial_progress: decisionalFeedbackRequest.initial_progress_percentage,
          // 🔒 P0 MOA (Jan 2026): Log session_run_id
          session_run_id: decisionalFeedbackRequest.session_run_id,
          history_length: conversationHistory.length
        });
        
        // 🔒 P0 FIX: Injection header X-Execution-Context-Id
        const response = await fetch(`${BACKEND_URL}/api/pedagogical/session/decisional-feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(window.__currentExecutionContextId && { 
              'X-Execution-Context-Id': window.__currentExecutionContextId 
            })
          },
          body: JSON.stringify(decisionalFeedbackRequest)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[endSession] 🔒 Decisional feedback error:', errorText);
          throw new Error(`Decisional feedback generation failed: ${response.status}`);
        }
        
        const decisionalData = await response.json();
        console.log('[endSession] 🔒 Decisional response:', {
          status: decisionalData.status,
          progress: decisionalData.progress_percent,
          validated: decisionalData.is_objective_validated,
          pinecone_used: decisionalData.pinecone_used
        });
        
        // Extraire le feedback formaté
        const formattedFeedback = decisionalData.formatted_feedback || decisionalData.decision?.formatted_feedback || '';
        
        // Si module complété (tous objectifs validés)
        if (decisionalData.status === 'no_active_objective' || decisionalData.module_completed) {
          const completionMessage = {
            role: 'assistant',
            content: `🎓 **Module terminé !**\n\nFélicitations ${userName} ! Tous les objectifs de ce module ont été validés par Owen.\n\n✅ Progression : 100%\n\nTu peux maintenant passer au module suivant ou télécharger ton attestation.`,
            emotion: 'enthousiaste',
            posture: 'COACH',
            timestamp: new Date().toISOString(),
            isFinalFeedback: true,
            isDecisional: true,
            metadata: { module_completed: true }
          };
          
          setConversation(prev => [...prev, completionMessage]);
          data = { feedback: completionMessage.content, _isDecisional: true };
        } else {
          // Feedback décisionnel normal
          const owenDecision = decisionalData.decision?.owen_decision || {};
          const progressPercent = owenDecision.progress_percent || decisionalData.progress_percent || 0;
          const isValidated = owenDecision.is_objective_validated || decisionalData.is_objective_validated || false;
          
          // Construire le message de feedback enrichi
          let feedbackContent = formattedFeedback;
          
          // Ajouter les métadonnées de progression si disponibles
          if (!feedbackContent.includes('Progression')) {
            feedbackContent += `\n\n📊 **Progression objectif actif**: ${progressPercent}%`;
          }
          
          const feedbackMessage = {
            role: 'assistant',
            content: feedbackContent,
            emotion: isValidated ? 'enthousiaste' : 'encourageant',
            posture: 'COACH_DECISIONAL',
            timestamp: new Date().toISOString(),
            isFinalFeedback: true,
            isDecisional: true,
            metadata: {
              decision_id: decisionalData.decision?.decision_id,
              progress_percent: progressPercent,
              is_validated: isValidated,
              pinecone_used: decisionalData.pinecone_used,
              pinecone_evidences: decisionalData.pinecone_evidences_count
            }
          };
          
          setConversation(prev => [...prev, feedbackMessage]);
          setCurrentEmotion(feedbackMessage.emotion);
          
          data = { 
            feedback: feedbackContent, 
            _isDecisional: true,
            progress_percent: progressPercent
          };
        }
        
        console.log('[endSession] 🔒 Decisional feedback applied:', {
          feedback_length: data.feedback?.length,
          progress: data.progress_percent
        });
      }
      
      // ═══════════════════════════════════════════════════════════════════════════
      // 🔒 P1.5-D MOA (Jan 2026): SYNCHRONISATION UI APRÈS FEEDBACK
      // ═══════════════════════════════════════════════════════════════════════════
      // RÈGLE: Après le feedback, l'UI DOIT refléter la progression finale
      // - Mettre à jour activeObjective.progress
      // - Notifier le bandeau pour qu'il se rafraîchisse
      // ═══════════════════════════════════════════════════════════════════════════
      if (data.progress_percent !== undefined && data.progress_percent !== null) {
        const finalProgress = data.progress_percent;
        const initialProgress = initialProgressRef.current || 0;
        
        console.log('[endSession] 🔒 P1.5-D: Synchronisation UI après feedback');
        console.log(`[endSession] 🔒 P1.5-D: Progression ${initialProgress}% → ${finalProgress}%`);
        
        // Mettre à jour l'état activeObjective via le hook centralisé
        if (activeObjective) {
          updateActiveObjectiveProgress(finalProgress);
          console.log('[endSession] 🔒 P1.5-D: activeObjective.progress mis à jour:', finalProgress);
        }
        
        // Notifier le bandeau pour rafraîchissement (via événement existant)
        notifyEvaluationComplete({
          module_id: parseInt(sanitizedModuleId) || 0,
          progress_updated: true,
          new_progress: finalProgress,
          initial_progress: initialProgress,
          source: 'p15d_sync'
        });
        
        console.log('[endSession] 🔒 P1.5-D: Événement de synchro émis');
      }
      
      // 🎯 Audio de fin (adapté au mode)
      // ═══════════════════════════════════════════════════════════════════════════
      // 🔒 RÈGLE ABSOLUE: En ROLEPLAY ou mode premium, PAS de phrase générique
      // Le feedback contient déjà une clôture - En mode décisionnel, pas de phrase additionnelle
      // ═══════════════════════════════════════════════════════════════════════════
      let closingPhrase;
      let closingEmotion;
      
      if (data._isRoleplayPremium || isFreeMode || isCoachPremiumMode || data._isDecisional) {
        // 🔒 Mode Décisionnel/Premium: Le feedback est auto-suffisant
        closingPhrase = null;
        closingEmotion = 'empathique';
        console.log('[endSession] 🔒 Decisional/Premium mode - No generic closing phrase needed');
      } else {
        // Fallback (ne devrait plus arriver avec migration complète)
        closingPhrase = `Session terminée. Merci ${userName} et à bientôt !`;
        closingEmotion = 'encourageant';
      }
      
      console.log('[endSession] 🎯 Closing mode:', data._isRoleplayPremium ? 'Roleplay' : (data._isDecisional ? 'Decisional' : (isFreeMode ? 'CoachPremium' : 'Standard')), '| Phrase:', closingPhrase);
      
      // Générer TTS pour la phrase finale (seulement en mode standard NON-roleplay)
      // 🚀 P1 OPTIM: Utiliser /text-to-speech-hd-fast (sans Whisper)
      let closingAudioUrl = null;
      if (closingPhrase) {
        try {
          console.log('[endSession] 🚀 [P1 OPTIM] Generating closing phrase TTS HD (sans Whisper)...');
          const ttsStartTime = Date.now();
          // 🔒 P0 FIX: AbortController pour annuler TTS à la sortie de page
          ttsAbortControllerRef.current = new AbortController();
          // 🔒 P0 FIX: Injection header X-Execution-Context-Id
          const ttsResponse = await fetch(`${BACKEND_URL}/api/ai/text-to-speech-hd-fast`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              ...(window.__currentExecutionContextId && { 
                'X-Execution-Context-Id': window.__currentExecutionContextId 
              })
            },
            body: JSON.stringify({
              text: closingPhrase,
              emotion: closingEmotion,
              mode: 'free'
            }),
            signal: ttsAbortControllerRef.current.signal
          });
          
          // 🔒 P0 FIX: Ne pas traiter si démonté
          if (!isMountedRef.current) {
            console.log('[endSession] ⏹️ Closing TTS response received but component unmounted, ignoring');
            return;
          }
          
          if (ttsResponse.ok) {
            const ttsData = await ttsResponse.json();
            closingAudioUrl = ttsData.audio_url;
            const ttsDuration = Date.now() - ttsStartTime;
            console.log(`[endSession] ✅ [P1 OPTIM] Closing phrase TTS HD généré en ${ttsDuration}ms`);
          }
        } catch (ttsError) {
          console.error('[endSession] ⚠️ Closing phrase TTS generation failed:', ttsError);
        }
      }
      
      // ✅ CORRECTIF: Play feedback audio if generated + phrase finale
      if (data.audio_url || closingAudioUrl) {
        // 🎯 LOG OBLIGATOIRE: Feedback audio disponible
        console.log('[endSession] 🔊 FEEDBACK VOCAL ACTIVÉ');
        console.log('[endSession] 📊 Stats:', {
          feedback_text_length: data.feedback?.length || 0,
          audio_url_present: !!data.audio_url,
          audio_url_type: data.audio_url?.substring(0, 30) || 'NONE',
          closing_audio_present: !!closingAudioUrl
        });
        
        // 🔒 P0 ALIGNEMENT: Transition vers PREPARING_AUDIO (pas SPEAKING!)
        transitionTo('FEEDBACK_FINAL_STREAMING', 'feedback_audio_ready');
        setOwenVocalState('PREPARING_AUDIO');
        console.log('🔊 [OWEN_STATE=PREPARING_AUDIO] Feedback audio en préparation');
        
        // Fonction pour jouer les audios en séquence
        const playAudioSequence = async () => {
          try {
            // 1. Jouer feedback principal si disponible
            if (data.audio_url) {
              console.log('[endSession] 🎙️ TTS_REQUESTED=true - Playing main feedback audio');
              const feedbackAudio = OwenAudioBus.createAudio(data.audio_url, { label: 'feedback' });
              audioRef.current = feedbackAudio;
              
              await new Promise((resolve, reject) => {
                // Lip-sync: feed feedback text and duration
                feedbackAudio.onplay = () => {
                  setOwenVocalState('SPEAKING');
                  setIsSpeaking(true);
                  // 🎯 FLOW ÉVALUATION: phase='speaking' déclenchée par TTS réel
                  setEvaluationFlowState({ phase: 'speaking' });
                  const dur = feedbackAudio.duration;
                  console.log('[OWEN_STATE=SPEAKING] Feedback audio réellement diffusé (phase=speaking)');
                };
                feedbackAudio.ondurationchange = () => {
                  const dur = feedbackAudio.duration;
                };
                feedbackAudio.onended = () => {
                  console.log('[endSession] ✅ AUDIO_PLAYED=true - Main feedback audio completed');
                  resolve();
                };
                feedbackAudio.onerror = (e) => {
                  console.error('[endSession] ❌ AUDIO_PLAYED=false - Audio error:', e);
                  reject(e);
                };
                feedbackAudio.play().catch(reject);
              });
            }
            
            // 2. Jouer phrase finale si présente
            if (closingAudioUrl) {
              console.log('[endSession] 🎙️ Playing closing phrase audio');
              const closingAudio = OwenAudioBus.createAudio(closingAudioUrl, { label: 'closing-phrase' });
              audioRef.current = closingAudio;
              
              await new Promise((resolve, reject) => {
                closingAudio.onplay = () => {
                  setOwenVocalState('SPEAKING');
                  setIsSpeaking(true);
                  // 🎯 FLOW ÉVALUATION: maintenir phase='speaking' sur closing audio
                  setEvaluationFlowState({ phase: 'speaking' });
                  const dur = closingAudio.duration;
                };
                closingAudio.ondurationchange = () => {
                  const dur = closingAudio.duration;
                };
                closingAudio.onended = resolve;
                closingAudio.onerror = reject;
                closingAudio.play().catch(reject);
              });
              
              console.log('[endSession] ✅ Closing phrase audio completed');
            }
            
            // Tout terminé - transition vers SESSION_ENDED
            console.log('[endSession] 🏁 TTS_SUCCESS=true - All audio playback completed');
            setOwenVocalState('IDLE');
            setIsSpeaking(false);
            // 🎯 FLOW ÉVALUATION: phase='completed' déclenchée par fin RÉELLE du TTS
            setEvaluationFlowState({ phase: 'completed' });
            transitionTo('SESSION_ENDED', 'session_end_complete');
            toast.success('Session terminée avec succès !');
            
          } catch (audioError) {
            console.error('[endSession] ❌ TTS_SUCCESS=false - Audio playback error:', audioError);
            setOwenVocalState('IDLE');
            setIsSpeaking(false);
            // 🎯 FLOW ÉVALUATION: en cas d'erreur audio, on libère aussi l'UI
            setEvaluationFlowState({ phase: 'completed' });
            transitionTo('SESSION_ENDED', 'session_end_audio_error');
            toast.success('Session terminée avec succès !');
          }
        };
        
        // Démarrer la séquence audio
        playAudioSequence();
        
      } else {
        console.log('[endSession] ⚠️ NO_AUDIO - text-only feedback');
        console.log('[endSession] 📊 Debug:', {
          data_audio_url: data.audio_url,
          closing_audio_url: closingAudioUrl,
          mode: data._isRoleplayPremium ? 'roleplay' : (isFreeMode ? 'free' : 'standard')
        });
        setIsSpeaking(false);
        // 🎯 FLOW ÉVALUATION: pas d'audio → phase='completed' direct
        setEvaluationFlowState({ phase: 'completed' });
        transitionTo('SESSION_ENDED', 'session_end_no_audio');
        toast.success('Session terminée avec succès !');
      }
      
      // ✅ ÉTAPE 5: 🎓 QUALIOPI - Évaluation pédagogique Owen (CRITIQUE)
      // Owen analyse automatiquement la session et met à jour les objectifs
      try {
        console.log('[endSession] 🎓 Triggering Owen pedagogical evaluation...');
        // 🔒 P0 FIX: Injection header X-Execution-Context-Id
        const evalResponse = await fetch(`${BACKEND_URL}/api/pedagogical/internal/evaluate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(window.__currentExecutionContextId && { 
              'X-Execution-Context-Id': window.__currentExecutionContextId 
            })
          },
          body: JSON.stringify({
            user_id: sanitizedUserId,
            module_id: parseInt(sanitizedModuleId) || 0,
            session_id: `vocal_${Date.now()}`,
            session_type: 'vocal',
            conversation_history: conversationHistory
          })
        });
        
        if (evalResponse.ok) {
          const evalResult = await evalResponse.json();
          console.log('[endSession] 🎓 Owen evaluation complete:', evalResult);
          
          // 🔄 SYNCHRONISATION: Notifier les composants que l'évaluation est terminée
          notifyEvaluationComplete({
            module_id: parseInt(sanitizedModuleId) || 0,
            ...evalResult
          });
          
          // 🔒 AUDIT CERTIFIANT V6 (30/12/2024): Popup UNIQUEMENT si VALID_PROOF
          // UNE preuve = VALID_PROOF persistée MongoDB uniquement
          if (evalResult.module_newly_validated) {
            toast.success('🏆 Module validé par Owen !', { duration: 5000 });
          } else if (evalResult.new_evidences_count > 0 && evalResult.proof_status === 'valid_proof') {
            // 🔒 Afficher le popup SEULEMENT si proof_status === 'valid_proof'
            toast.info(`📚 ${evalResult.new_evidences_count} preuve(s) validée(s)`, { duration: 3000 });
          }
          // ❌ Pas de popup pour les preuves partielles, intentions, ou non-observées
        } else {
          console.warn('[endSession] ⚠️ Owen evaluation failed:', evalResponse.status);
        }
      } catch (evalError) {
        console.error('[endSession] ⚠️ Owen evaluation error (non-blocking):', evalError);
        // Non-blocking: ne pas empêcher la fin de session
      }
      
      // ✅ ÉTAPE 6: Adjust pathway after session
      await adjustPathwayAfterSession(sanitizedUserId, sanitizedModuleId, conversationHistory);
      
    } catch (error) {
      console.error('[endSession] ❌ Error ending session:', error);
      // ✅ PHASE 3: Auto-dismiss error after 5s
      toast.error('Erreur lors de la génération du feedback', { duration: 5000 });
      
      const userName = user?.name || 'Manager';
      
      // ═══════════════════════════════════════════════════════════════════════════
      // 🔒 RÈGLE: En ROLEPLAY, même en cas d'erreur, PAS de fallback générique
      // On affiche un message d'erreur honnête, pas un faux feedback
      // ═══════════════════════════════════════════════════════════════════════════
      
      // Vérifier si c'était une session roleplay (re-détecter car on est dans le catch)
      // 🆕 MULTI-SOURCE: Texte + Posture des messages
      const conversationText = conversation.map(m => m.content || '').join(' ').toLowerCase();
      const hasRoleplayPostureInCatch = conversation.some(msg => msg.posture === 'ROLEPLAY');
      
      const wasRoleplaySession = 
        hasRoleplayPostureInCatch ||  // 🆕 Détection par posture
        conversationText.includes("j'incarne") ||
        conversationText.includes("🎭") ||
        conversationText.includes("personnage") ||
        conversationText.includes("alex") ||
        conversationText.includes("préoccupations") ||
        conversationText.includes("jeu de rôle") ||
        conversationText.includes("collaborateur") ||
        conversationText.includes("manager");
      
      console.log('[endSession CATCH] 🎭 Roleplay detection:', {
        hasRoleplayPosture: hasRoleplayPostureInCatch,
        wasRoleplaySession: wasRoleplaySession
      });
      
      if (wasRoleplaySession) {
        // 🎭 ROLEPLAY: Message d'erreur honnête, PAS de faux feedback
        console.log('[endSession] 🎭 Roleplay error - showing honest error message');
        setConversation(prev => [...prev, {
          role: 'assistant',
          content: `${userName}, je rencontre un problème technique pour générer ton feedback personnalisé. 😔\n\nCe que je peux te dire : tu as fait preuve d'engagement dans cet échange. La prochaine fois, je pourrai te donner un retour plus détaillé.\n\n🔄 Tu peux relancer une session pour avoir un feedback complet.`,
          emotion: 'empathique',
          posture: 'COACH',
          timestamp: new Date().toISOString()
        }]);
      } else {
        // Mode standard: fallback acceptable
        const closingPhrase = `Session terminée. Merci ${userName} et à bientôt !`;
        setConversation(prev => [...prev, {
          role: 'assistant',
          content: `Merci ${userName} pour cette session de coaching ! 🙏\n\nContinuez à pratiquer les concepts du module "${moduleName}".\n\n✅ ${closingPhrase}`,
          emotion: 'empathique',
          posture: 'COACH',
          timestamp: new Date().toISOString()
        }]);
      }
      
      // Tenter de jouer une phrase finale adaptée
      try {
        const token = localStorage.getItem('token');
        const fallbackPhrase = wasRoleplaySession 
          ? `${userName}, je rencontre un souci technique. Relance une session pour ton feedback complet.`
          : `Session terminée. Merci ${userName} et à bientôt !`;
        
        // 🔒 P0 FIX: Injection header X-Execution-Context-Id
        const ttsResponse = await fetch(`${BACKEND_URL}/api/ai/text-to-speech`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(window.__currentExecutionContextId && { 
              'X-Execution-Context-Id': window.__currentExecutionContextId 
            })
          },
          body: JSON.stringify({
            text: fallbackPhrase,
            emotion: 'empathique'
          })
        });
        
        if (ttsResponse.ok) {
          const ttsData = await ttsResponse.json();
          if (ttsData.audio_url) {
            console.log('[endSession] 🔊 Playing fallback closing phrase');
            // 🎯 CORRECTION: Transition vers FEEDBACK_FINAL_STREAMING
            transitionTo('FEEDBACK_FINAL_STREAMING', 'fallback_audio_ready');
            setIsSpeaking(true);
            const closingAudio = OwenAudioBus.createAudio(ttsData.audio_url, { label: 'closing-fallback' });
            audioRef.current = closingAudio;
            
            closingAudio.onplay = () => {
              setOwenVocalState('SPEAKING');
              // 🎯 FLOW ÉVALUATION: phase='speaking' sur fallback closing audio
              setEvaluationFlowState({ phase: 'speaking' });
              const dur = closingAudio.duration;
            };
            closingAudio.ondurationchange = () => {
              const dur = closingAudio.duration;
            };
            
            closingAudio.onended = () => {
              console.log('[endSession] ✅ Fallback closing phrase completed');
              setIsSpeaking(false);
              setOwenVocalState('IDLE');
              // 🎯 FLOW ÉVALUATION: phase='completed' sur fin fallback audio
              setEvaluationFlowState({ phase: 'completed' });
              transitionTo('SESSION_ENDED', 'session_end_fallback_complete');
            };
            
            closingAudio.onerror = () => {
              console.error('[endSession] ❌ Fallback audio error');
              setIsSpeaking(false);
              setOwenVocalState('IDLE');
              setEvaluationFlowState({ phase: 'completed' });
              transitionTo('SESSION_ENDED', 'session_end_fallback_error');
            };
            
            closingAudio.play().catch(() => {
              setIsSpeaking(false);
              setEvaluationFlowState({ phase: 'completed' });
              transitionTo('SESSION_ENDED', 'session_end_fallback_play_error');
            });
          } else {
            // ttsResponse.ok mais pas d'audio_url: libérer quand même l'UI
            setEvaluationFlowState({ phase: 'completed' });
            transitionTo('SESSION_ENDED', 'session_end_fallback_no_audio');
          }
        } else {
          // ttsResponse non-ok: libérer l'UI
          setEvaluationFlowState({ phase: 'completed' });
          transitionTo('SESSION_ENDED', 'session_end_fallback_tts_not_ok');
        }
      } catch (fallbackError) {
        console.error('[endSession] ⚠️ Fallback TTS failed:', fallbackError);
        setIsSpeaking(false);
        setEvaluationFlowState({ phase: 'completed' });
        transitionTo('SESSION_ENDED', 'session_end_fallback_failed');
      }
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Convertir audio en base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  
  // Traiter l'audio enregistré
  // 🎯 AUDIO-SYNCED: Calculer le texte à afficher basé sur position audio
  const updateDisplayedContentBasedOnAudio = (messageId, fullText, audio) => {
    if (!audio || !fullText) return;
    
    const currentTime = audio.currentTime;
    const duration = audio.duration;
    
    if (!duration || duration === 0) return;
    
    // Calculer le pourcentage de progression
    const progress = Math.min(currentTime / duration, 1);
    
    // Calculer le nombre de caractères à afficher
    const charsToDisplay = Math.floor(fullText.length * progress);
    const displayedText = fullText.substring(0, charsToDisplay);
    
    // Mettre à jour le message
    setConversation(prev => prev.map(msg => {
      if (msg.id === messageId) {
        return {
          ...msg,
          displayedContent: displayedText,
          audioProgress: progress,
          highlightPosition: charsToDisplay
          // ✅ FIX SCINTILLEMENT: Supprimé _updateId qui force re-render
        };
      }
      return msg;
    }));
  };
  
  // 🎯 SYNC PARFAITE: Jouer audio avec affichage progressif synchronisé
  const playNextInQueue = () => {
    // 🔒 P0 FIX: Ne rien faire si composant démonté
    if (!isMountedRef.current) {
      console.log('[playNextInQueue] ⏹️ Component unmounted, ignoring');
      return;
    }
    
    // Si déjà en train de jouer, ne rien faire
    if (isPlayingAudioQueue.current) {
      console.log('[playNextInQueue] ⏸️ SYNC: Already playing, waiting...');
      return;
    }
    
    // Chercher le prochain audio dans l'ordre
    const nextItem = audioQueue.current.find(item => item.order === nextOrderToPlay.current && item.status === 'ready');
    
    if (!nextItem) {
      console.log('[playNextInQueue] ⏳ SYNC: Waiting for order', nextOrderToPlay.current, 'to be ready');
      
      // 🔧 P0 FIX COHÉRENCE: NE PAS transiter vers USER_TURN ici !
      // La queue peut être vide parce qu'on a RETIRÉ le dernier élément pour le jouer
      // La transition vers USER_TURN doit se faire UNIQUEMENT dans audio.onended
      // quand le dernier audio est VRAIMENT terminé
      if (audioQueue.current.length === 0 && !isPlayingAudioQueue.current && vocalStage !== 'USER_TURN') {
        console.log('[playNextInQueue] 🔧 P0 FIX: Queue vide mais on vérifie si audio en cours...');
        // Ne rien faire - la transition se fera dans audio.onended
      }
      
      return;
    }
    
    // Retirer de la queue
    audioQueue.current = audioQueue.current.filter(item => item.order !== nextItem.order);
    console.log('[playNextInQueue] ▶️ SYNC: Playing phrase #', nextItem.order, ':', nextItem.phrase.substring(0, 40));
    
    // 🎯 FIX ÉTAT: Si c'est la première phrase (order 0), désactiver isProcessing maintenant qu'Owen parle
    if (nextItem.order === 0) {
      console.log('[playNextInQueue] ✅ FIX: First audio starting - disabling isProcessing');
      setIsProcessing(false);
      // 🎯 CORRECTION: Transiter vers OWEN_RESPONSE quand Owen commence à parler
      transitionTo('OWEN_RESPONSE', 'owen_audio_started');
    }
    
    isPlayingAudioQueue.current = true;
    setIsPlayingQueue(true);
    setOwenVocalState('PREPARING_AUDIO');
    console.log('🔊 [OWEN_STATE=PREPARING_AUDIO] Audio phrase en préparation');
    currentlyPlayingPhrase.current = nextItem.phrase;
    
    const audio = OwenAudioBus.createAudio(nextItem.audioUrl, { label: `queue-phrase-${nextItem.order}` });
    currentAudioRef.current = audio;
    audioRef.current = audio;
    
    // P2.1: Pré-charger le segment suivant pour transition sans gap
    const nextNextItem = audioQueue.current.find(
      item => item.order === nextItem.order + 1 && item.status === 'ready'
    );
    let preloadedNextAudio = null;
    if (nextNextItem) {
      preloadedNextAudio = OwenAudioBus.createAudio(nextNextItem.audioUrl, { label: `queue-preload-${nextNextItem.order}` });
      preloadedNextAudio.preload = 'auto';
      console.log('[P2.1] 🎵 Pré-chargement segment suivant #', nextNextItem.order);
    }
    
    // 🔒 P0 ALIGNEMENT: SPEAKING uniquement sur onplay (audio réellement diffusé)
    audio.onplay = () => {
      setOwenVocalState('SPEAKING');
      setIsSpeaking(true);
      // Lip-sync: Feed phrase text and duration to avatar
      const dur = audio.duration;
      console.log('🗣️ [OWEN_STATE=SPEAKING] Audio phrase réellement diffusé');
    };

    // Update duration as soon as metadata is available (mobile fix)
    audio.ondurationchange = () => {
      const dur = audio.duration;
    };
    
    // 🎯 FIX PERSISTANCE: Préparer l'affichage progressif en conservant TOUT le texte précédent
    const phraseToDisplay = nextItem.phrase;
    currentPlayingMessageId.current = nextItem.messageId;
    
    console.log('[playNextInQueue] 🎯 FIX: Will display phrase progressively:', phraseToDisplay.substring(0, 40));
    
    // ✅ CRITICAL FIX STALE CLOSURE: Récupérer previousDisplayedText DANS le callback
    // pour accéder à l'état ACTUEL et non à la closure stale
    setConversation(prev => {
      // Trouver le message dans l'état ACTUEL (prev), pas dans la closure stale
      const currentMsg = prev.find(m => m.id === nextItem.messageId);
      const previousDisplayedText = currentMsg?.displayedContent || '';
      const separator = previousDisplayedText.length > 0 ? ' ' : '';
      
      console.log('[playNextInQueue] 🔥 CRITICAL FIX: Previous displayed text length:', previousDisplayedText.length, '(from CURRENT state)');
      
      // Marquer comme en cours de lecture avec le texte précédent CORRECT
      return prev.map(msg => {
        if (msg.id === nextItem.messageId) {
          return {
            ...msg,
            currentPhraseToAdd: phraseToDisplay,
            previousDisplayedText: previousDisplayedText, // Texte précédent de l'état ACTUEL
            separator: separator,
            isPlayingAudio: true
            // ✅ FIX SCINTILLEMENT: Supprimé _updateId qui force re-render
          };
        }
        return msg;
      });
    });
    
    // 🇫🇷 SUPPRESSION STREAMING VISUEL (28 déc 2024)
    // ════════════════════════════════════════════════════════════════════════════
    // Le texte est affiché COMPLET immédiatement, pas progressivement
    // Aligne le frontend avec l'architecture backend non-streaming
    // ════════════════════════════════════════════════════════════════════════════
    audio.onloadedmetadata = () => {
      console.log('[playNextInQueue] 🎯 Audio loaded, duration:', audio.duration, 's');
      console.log('[playNextInQueue] 🇫🇷 STREAMING VISUEL DÉSACTIVÉ - Affichage immédiat');
      
      // 🔒 AFFICHER IMMÉDIATEMENT le texte complet (pas d'intervalle progressif)
      setConversation(prev => prev.map(msg => {
        if (msg.id === nextItem.messageId) {
          const previousText = msg.previousDisplayedText || '';
          const sep = msg.separator || '';
          const fullDisplayed = previousText + sep + phraseToDisplay;
          
          console.log('[playNextInQueue] 🇫🇷 Affichage immédiat complet:', fullDisplayed.length, 'chars');
          
          return {
            ...msg,
            displayedContent: fullDisplayed,
            audioProgress: 0,
            isPlayingAudio: true
          };
        }
        return msg;
      }));
    };
    
    audio.onended = () => {
      // 🔒 P0 FIX: Ne rien faire si composant démonté
      if (!isMountedRef.current) {
        console.log('[audio.onended] ⏹️ Component unmounted, ignoring');
        return;
      }
      
      console.log('[playNextInQueue] ✅ Phrase #', nextItem.order, 'ended');
      
      // 🔒 PAS d'intervalle à nettoyer car il n'y en a plus
      
      // 🎯 FIX PERSISTANCE: Afficher phrase complète en GARDANT tout le texte précédent
      setConversation(prev => prev.map(msg => {
        if (msg.id === nextItem.messageId) {
          const previousText = msg.previousDisplayedText || '';
          const sep = msg.separator || '';
          
          // Construire: TOUT le texte précédent + phrase complète
          const finalDisplayed = previousText + sep + phraseToDisplay;
          
          console.log('[playNextInQueue] 🎯 FIX PERSISTANCE: Final displayed length:', finalDisplayed.length, '(previous:', previousText.length, '+ phrase:', phraseToDisplay.length, ')');
          
          return {
            ...msg,
            displayedContent: finalDisplayed,
            currentPhraseToAdd: null,
            previousDisplayedText: null,
            separator: null,
            isPlayingAudio: false,
            audioProgress: 1
          };
        }
        return msg;
      }));
      
      isPlayingAudioQueue.current = false;
      setIsPlayingQueue(false); // 🎯 CORRECTION UX: Synchroniser state
      currentAudioRef.current = null;
      currentlyPlayingPhrase.current = null;
      currentPlayingMessageId.current = null;
      
      // Incrémenter l'ordre attendu
      nextOrderToPlay.current++;
      
      // 🎯 CORRECTION STATUT: Vérifier si c'est le dernier audio AVANT d'appeler playNextInQueue
      const hasMoreAudio = audioQueue.current.some(item => item.order === nextOrderToPlay.current && item.status === 'ready');
      
      if (!hasMoreAudio) {
        // 🔧 P0 FIX COHÉRENCE: C'est le dernier audio - transition IDLE + USER_TURN
        // Cette transition se fait ICI car audio.onended vient de se terminer
        console.log('[playNextInQueue] 🏁 P0 FIX: Dernier audio terminé (onended), transition vers IDLE + USER_TURN');
        setOwenVocalState('IDLE');
        setIsSpeaking(false);
        // 🎤 LIP-SYNC: Clear spoken text
        console.log('🧠 [OWEN_STATE=IDLE] Audio réellement terminé (onended)');
        ensureUserTurnAfterAudio('last_audio_onended_complete');
      } else {
        // Il y a encore de l'audio à jouer - garder PREPARING pour le prochain
        console.log('[playNextInQueue] ⏭️ More audio to play, continuing...');
        setOwenVocalState('PREPARING_AUDIO');
        setIsSpeaking(false);
      }
      
      // Jouer le prochain audio si disponible
      playNextInQueue();
    };
    
    audio.onerror = (err) => {
      // 🔒 P0 FIX: Ne rien faire si composant démonté
      if (!isMountedRef.current) {
        console.log('[audio.onerror] ⏹️ Component unmounted, ignoring');
        return;
      }
      
      console.error('[playNextInQueue] ❌ MOT PAR MOT: Audio error:', err);
      
      // Nettoyer l'intervalle
      if (audioSyncInterval.current) {
        clearInterval(audioSyncInterval.current);
        audioSyncInterval.current = null;
      }
      
      isPlayingAudioQueue.current = false;
      setIsPlayingQueue(false); // 🎯 CORRECTION UX: Synchroniser state
      currentAudioRef.current = null;
      currentlyPlayingPhrase.current = null;
      currentPlayingMessageId.current = null;
      
      nextOrderToPlay.current++;
      
      // 🎯 CORRECTION STATUT: Vérifier si c'est le dernier audio AVANT d'appeler playNextInQueue
      const hasMoreAudio = audioQueue.current.some(item => item.order === nextOrderToPlay.current && item.status === 'ready');
      
      if (!hasMoreAudio) {
        // 🔧 P0 FIX COHÉRENCE: Erreur sur dernier audio - transition forcée vers USER_TURN
        // En cas d'erreur, on DOIT permettre à l'utilisateur de reprendre la main
        console.log('[playNextInQueue] 🏁 P0 FIX: Erreur dernier audio, transition sécurisée');
        setOwenVocalState('IDLE');
        setIsSpeaking(false);
        console.log('🧠 [OWEN_STATE=IDLE] Erreur audio - recovery obligatoire');
        ensureUserTurnAfterAudio('audio_error_recovery');
      } else {
        setOwenVocalState('PREPARING_AUDIO');
        setIsSpeaking(false);
      }
      
      playNextInQueue();
    };
    
    // 🔧 FIX STREAMING: Setup timeout sécurité avant lecture
    setupAudioSafetyTimeout(audio, 60000);
    
    audio.play().catch(err => {
      console.error('[playNextInQueue] ❌ SYNC: Auto-play failed:', err);
      isPlayingAudioQueue.current = false;
      setIsPlayingQueue(false); // 🎯 CORRECTION UX: Synchroniser state
      setIsSpeaking(false);
      
      // 🔧 FIX STREAMING: Nettoyer timeout en cas d'échec play
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      
      toast.info('Clique pour écouter Owen');
    });
  };
  
  // 🚀 TTS PROGRESSIF: Lecture séquentielle des segments audio reçus via SSE
  const playNextSegment = () => {
    if (!isMountedRef.current) return;
    if (isPlayingSegment.current) return;
    
    const nextSeg = audioSegmentQueue.current.find(s => s.order === currentSegmentOrder.current);
    if (!nextSeg) return;
    
    audioSegmentQueue.current = audioSegmentQueue.current.filter(s => s.order !== nextSeg.order);
    isPlayingSegment.current = true;
    
    console.log(`[TTS_PROGRESSIVE] ▶️ Lecture segment #${nextSeg.order}`);
    
    const audio = OwenAudioBus.createAudio(nextSeg.audioUrl, { label: `tts-progressive-${nextSeg.order}` });
    audioRef.current = audio;
    
    audio.onplay = () => {
      setBlocState('SPEAKING');
      setOwenVocalState('SPEAKING');
      setIsSpeaking(true);
      setLoadingMessage('');
      setIsProcessing(false);
      // Lip-sync: transmettre le texte du segment immédiatement (durée mise à jour via ondurationchange)
      const dur = audio.duration;
    };

    // Update duration as soon as metadata is available (mobile fix)
    audio.ondurationchange = () => {
      const dur = audio.duration;
      if (dur && isFinite(dur) && dur > 0) {
      }
    };
    
    audio.onended = () => {
      console.log(`[TTS_PROGRESSIVE] ✅ Segment #${nextSeg.order} terminé`);
      isPlayingSegment.current = false;
      currentSegmentOrder.current++;
      
      if (nextSeg.isLast) {
        // Dernier segment — transition vers USER_TURN
        console.log('[TTS_PROGRESSIVE] ✅ Tous les segments joués');
        progressiveAudioActive.current = false;
        setBlocState('IDLE');
        setOwenVocalState('IDLE');
        setIsSpeaking(false);
        setVocalStage('USER_TURN');
        // Clear lip-sync data
      } else {
        // Jouer le segment suivant
        playNextSegment();
      }
    };
    
    audio.onerror = (e) => {
      console.error(`[TTS_PROGRESSIVE] ❌ Erreur segment #${nextSeg.order}:`, e);
      isPlayingSegment.current = false;
      currentSegmentOrder.current++;

      // 🛡️ V4.5 (Iter349) — Cas terminal contrôlé sur dernier segment.
      // ZÉRO fallback silencieux : si le dernier segment échoue à la lecture,
      // on libère l'UI proprement ET on signale l'erreur visuellement.
      if (nextSeg.isLast) {
        console.error(
          '[TTS_PROGRESSIVE] ❌ DERNIER SEGMENT ÉCHOUÉ — la consigne finale ' +
          'a peut-être été perdue. Libération de l\'UI et notification utilisateur.'
        );
        progressiveAudioActive.current = false;
        setBlocState('IDLE');
        setOwenVocalState('IDLE');
        setIsSpeaking(false);
        setVocalStage('USER_TURN');
        try {
          toast.error(
            "La fin du message audio n'a pas pu être lue. Le texte reste affiché à l'écran.",
            { duration: 6000, id: 'tts-last-segment-error' }
          );
        } catch (toastErr) {
          console.warn('[TTS_PROGRESSIVE] toast unavailable:', toastErr);
        }
        return;
      }
      // Segment intermédiaire — skip et continuer
      playNextSegment();
    };
    
    audio.play().catch(e => {
      console.warn('[TTS_PROGRESSIVE] Autoplay blocked:', e);
      isPlayingSegment.current = false;
      setBlocState('IDLE');
      setLoadingMessage('');
      setIsProcessing(false);
    });
  };
  
  // 🎯 FIX ORDRE: Fonction pour générer TTS avec ordre garanti
  const generatePhraseAudio = async (phrase, messageId, emotion = 'empathique', order) => {
    // Ignorer les phrases trop courtes
    if (!phrase || phrase.trim().length < 3) {
      console.log('[generatePhraseAudio] ⏭️ FIX: Skipping short phrase:', phrase);
      return;
    }
    
    try {
      console.log('[generatePhraseAudio] 🎙️ FIX: Generating TTS for phrase #', order, ':', phrase.substring(0, 40), '...');
      
      // Ajouter immédiatement à la queue avec statut "generating"
      audioQueue.current.push({
        order: order,
        phrase: phrase,
        audioUrl: null,
        messageId: messageId,
        status: 'generating'
      });
      
      const ttsResponse = await safeFetchJSON(`${BACKEND_URL}/api/ai/text-to-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: phrase,
          emotion: emotion,
          user_id: user?.user_id || user?.id || 'anonymous'
        })
      });
      
      if (ttsResponse.audio_url) {
        console.log('[generatePhraseAudio] ✅ FIX: TTS ready for phrase #', order);
        
        // Mettre à jour l'item dans la queue
        const item = audioQueue.current.find(i => i.order === order);
        if (item) {
          item.audioUrl = ttsResponse.audio_url;
          item.status = 'ready';
        }
        
        // Essayer de jouer si c'est le prochain dans l'ordre
        playNextInQueue();
      }
    } catch (error) {
      console.error('[generatePhraseAudio] ❌ FIX: TTS failed for phrase #', order, ':', error);
      
      // Marquer comme erreur pour ne pas bloquer la queue
      const item = audioQueue.current.find(i => i.order === order);
      if (item) {
        item.status = 'error';
      }
      
      // Si c'est le prochain attendu, passer au suivant
      if (order === nextOrderToPlay.current) {
        nextOrderToPlay.current++;
        playNextInQueue();
      }
    }
  };
  
  // 🎯 PHASE 4 - SYNC VOIX/TEXTE: Fonction dédiée pour génération TTS non-bloquante
  const generateAndPlayTTS = async (text, messageId, emotion = 'empathique') => {
    try {
      console.log('[generateAndPlayTTS] 🎙️ PHASE 4: Generating TTS for message:', messageId);
      
      const ttsStartTime = performance.now();
      console.log(`[PERF-FRONTEND] [TTS_ASYNC] Start: ${ttsStartTime}ms`);
      
      const ttsResponse = await safeFetchJSON(`${BACKEND_URL}/api/ai/text-to-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text,
          emotion: emotion,
          user_id: user?.user_id || user?.id || 'anonymous'
        })
      });
      
      const ttsEndTime = performance.now();
      const ttsDuration = ttsEndTime - ttsStartTime;
      console.log(`[PERF-FRONTEND] [TTS_ASYNC] End: ${ttsEndTime}ms | Duration: ${ttsDuration.toFixed(2)}ms`);
      
      if (ttsResponse.audio_url) {
        console.log('[generateAndPlayTTS] ✅ PHASE 4: TTS ready, updating message with audio');
        
        // Mettre à jour le message avec l'audio URL
        setConversation(prev => prev.map(msg => 
          msg.id === messageId 
            ? { 
                ...msg, 
                audioUrl: ttsResponse.audio_url,
                spokenText: ttsResponse.spoken_text,
                hasAudio: true
              }
            : msg
        ));
        
        // 🔒 P0 ALIGNEMENT: PREPARING_AUDIO d'abord (pas SPEAKING!)
        console.log('[generateAndPlayTTS] 🔊 PHASE 4: Auto-playing audio');
        setOwenVocalState('PREPARING_AUDIO');
        console.log('🔊 [OWEN_STATE=PREPARING_AUDIO] TTS audio en préparation');
        
        const audio = OwenAudioBus.createAudio(ttsResponse.audio_url, { label: 'tts-auto-reply' });
        audioRef.current = audio;
        
        // 🔒 P0 ALIGNEMENT: SPEAKING uniquement sur onplay (audio réellement diffusé)
        audio.onplay = () => {
          setOwenVocalState('SPEAKING');
          setIsSpeaking(true);
          // Lip-sync: Feed spoken text and audio duration to avatar
          const dur = audio.duration;
          console.log('🗣️ [OWEN_STATE=SPEAKING] TTS audio réellement diffusé');
        };

        // Update duration as soon as metadata is available (mobile fix)
        audio.ondurationchange = () => {
          const dur = audio.duration;
        };
        
        // Track audio playback for synchronization
        audio.ontimeupdate = () => {
          // 🎯 PHASE 4: Update message with current playback time for sync
          setConversation(prev => prev.map(msg => 
            msg.id === messageId 
              ? { ...msg, audioCurrentTime: audio.currentTime, audioDuration: audio.duration }
              : msg
          ));
        };
        
        audio.onended = () => {
          console.log('[generateAndPlayTTS] ✅ PHASE 4: Audio playback ended');
          setOwenVocalState('IDLE');
          setIsSpeaking(false);
          // 🎤 LIP-SYNC: Clear spoken text
          console.log('🧠 [OWEN_STATE=IDLE] TTS audio terminé');
          setConversation(prev => prev.map(msg => 
            msg.id === messageId 
              ? { ...msg, audioCurrentTime: 0, isPlayingAudio: false }
              : msg
          ));
        };
        
        audio.onerror = (err) => {
          console.error('[generateAndPlayTTS] ❌ Audio playback error:', err);
          setOwenVocalState('IDLE');
          setIsSpeaking(false);
          console.log('🧠 [OWEN_STATE=IDLE] TTS erreur - reset');
          toast.warning('Audio non disponible, mais la réponse est visible');
        };
        
        // Mark as playing before starting
        setConversation(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, isPlayingAudio: true }
            : msg
        ));
        
        audio.play().catch(err => {
          console.error('[generateAndPlayTTS] ❌ Auto-play failed:', err);
          setIsSpeaking(false);
          toast.info('Clique sur le bouton pour écouter Owen');
        });
      }
    } catch (error) {
      console.error('[generateAndPlayTTS] ❌ PHASE 4: TTS generation failed:', error);
      toast.info('Audio non disponible, mais le texte est visible');
    }
  };
  
  const processAudio = async (audioBlob, optimisticMessageId = null) => {
    // 🔒 ANTI-BOUCLE POST-CLÔTURE: Bloquer tout envoi après clôture
    if (sessionTerminee) {
      console.warn('[processAudio] 🔒 BLOCKED - Session déjà clôturée (sessionTerminee=true)');
      return;
    }
    
    // ✅ GARDE ANTI-RÉENTRANCE: Empêcher les appels simultanés
    if (isProcessingAudioRef.current) {
      console.warn('[processAudio] ⚠️ BLOCKED - Already processing audio, skipping duplicate call');
      return;
    }
    
    console.log('✅ processAudio guarded - starting processing');
    isProcessingAudioRef.current = true;
    
    // 🎯 FIX BLOCAGE: Timeout global de sécurité (60s max pour TOUT le processus)
    const globalTimeoutId = setTimeout(() => {
      console.error('[processAudio] ⏱️ FIX BLOCAGE: GLOBAL TIMEOUT - Forcing USER_TURN');
      isProcessingAudioRef.current = false;
      setIsProcessing(false);
      ensureUserTurnAfterAudio('global_timeout');
      toast.error('Temps de traitement dépassé. Réessayez.');
    }, 60000);
    
    // 🎯 PHASE 1 - PERFORMANCE DIAGNOSTIC: Track total processing time
    const processingStartTime = performance.now();
    console.log(`[PERF-FRONTEND] [TOTAL_PROCESSING] Start: ${processingStartTime}ms`);
    
    // 🎯 CORRECTION: Transition vers PROCESSING_USER pour désactiver tous les boutons
    transitionTo('PROCESSING_USER', 'user_audio_received');
    setIsProcessing(true);
    setCoachState('loading');
    setOwenVocalState('GENERATING_RESPONSE');  // 🔒 P0 ALIGNEMENT: Génération RÉPONSE (pas scénario)
    console.log('🔄 [OWEN_STATE=GENERATING_RESPONSE] Owen traite la réponse utilisateur');
    setLoadingMessage('Owen écoute votre message...');
    
    try {
      // 🔒 P0 — Pré-check size côté client (économise un aller-retour et un
      // appel Whisper sur audio manifestement vide). Seuil aligné backend.
      const MIN_AUDIO_BYTES = 5 * 1024;
      const recordedDurationMs = recordingStartTimeRef.current
        ? Date.now() - recordingStartTimeRef.current
        : null;
      if (audioBlob && audioBlob.size < MIN_AUDIO_BYTES) {
        console.warn('[processAudio] 🔒 P0 client guard: audio too short', {
          size: audioBlob.size, durationMs: recordedDurationMs,
        });
        // Reset état + toast (re-utilise le path "transcription empty" existant)
        if (optimisticMessageId) {
          setConversation(prev => prev.filter(m => m.id !== optimisticMessageId));
        }
        setIsProcessing(false);
        setCoachState('active');
        setOwenVocalState('IDLE');
        setIsSpeaking(false);
        isProcessingAudioRef.current = false;
        clearTimeout(globalTimeoutId);
        transitionTo('USER_TURN', 'audio_too_short_client');
        toast.error('🎤 Audio trop court. Reprenez votre phrase.', { duration: 4000 });
        return;
      }

      // 1. Transcrire l'audio avec Whisper
      const audioBase64 = await blobToBase64(audioBlob);
      
      setLoadingMessage('Owen réfléchit à sa réponse...');
      
      // 🎯 PHASE 1 - PERFORMANCE DIAGNOSTIC: Track transcription time
      const transcriptionStartTime = performance.now();
      console.log(`[PERF-FRONTEND] [TRANSCRIPTION] Start: ${transcriptionStartTime}ms`);
      console.log('[processAudio] 🎤 Calling transcription API...');
      
      // ✅ Utiliser safeFetchJSON avec body-read-once
      const transcriptionData = await safeFetchJSON(`${BACKEND_URL}/api/ai/transcribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user?.user_id || user?.id || 'anonymous',
          audio_data: audioBase64,
          module_id: moduleId,
          // 🔒 Métadonnées qualité (P0/P2 backend)
          audio_duration_ms: recordedDurationMs,
          mime_type: recordedMimeTypeRef.current,
          mode: isFreeMode ? 'libre' : 'certifiant',
        })
      });
      
      // 🎯 PHASE 1 - PERFORMANCE DIAGNOSTIC: Transcription completed
      const transcriptionEndTime = performance.now();
      const transcriptionDuration = transcriptionEndTime - transcriptionStartTime;
      console.log(`[PERF-FRONTEND] [TRANSCRIPTION] End: ${transcriptionEndTime}ms | Duration: ${transcriptionDuration.toFixed(2)}ms`);
      
      console.log('[processAudio] ✅ Transcription successful');
      const userText = transcriptionData.transcribed_text;
      
      console.log('[processAudio] 📝 Transcribed text:', userText);
      console.log('[processAudio] 📏 Text length:', userText ? userText.length : 0);
      
      // ✅ CRITICAL: Validate transcribed text is not empty
      // 🔧 P0 FIX A: Garde renforcée avec reset complet des états
      if (!userText || userText.trim() === '' || userText.trim().length < 2) {
        console.error('[processAudio] ❌ GARDE A: Transcription vide détectée:', userText);
        
        // 🔒 P0 OPTIMISTIC UI: Supprimer le message optimiste en cas d'échec
        if (optimisticMessageId) {
          setConversation(prev => prev.filter(m => m.id !== optimisticMessageId));
          console.log('[processAudio] 🔒 P0: Message optimiste supprimé (transcription vide)');
        }
        
        // 🔧 P0 FIX A: Reset COMPLET des états pour garantir retour USER_TURN
        setIsProcessing(false);
        setCoachState('active');
        setOwenVocalState('IDLE');
        setIsSpeaking(false);
        isProcessingAudioRef.current = false;
        clearTimeout(globalTimeoutId);
        
        // 🔧 P0 FIX A: Transition explicite vers USER_TURN
        transitionTo('USER_TURN', 'transcription_empty_fallback');
        console.log('[processAudio] 🔧 P0 FIX A: États réinitialisés, retour USER_TURN');
        
        // 🔒 Toast contextuel selon le motif backend (P1/P2 quality reasons).
        // Mécanisme local minimal — aucun nouveau bouton, juste un message.
        const reason = transcriptionData?.rejection_reason || '';
        const reasonKey = reason.startsWith('whisper_hallucination')
          ? 'whisper_hallucination' : reason;
        const REJECTION_TOASTS = {
          audio_too_short: '🎤 Audio trop court. Reprenez votre phrase.',
          whisper_no_speech_high: '🔈 Je n\'ai pas entendu votre voix. Vérifiez votre micro et reprenez.',
          whisper_logprob_low: '🎤 Je n\'ai pas pu comprendre clairement. Reprenez votre dernière phrase.',
          whisper_hallucination: '🎤 Votre voix s\'est mélangée à un bruit ambiant. Reprenez simplement votre dernière phrase.',
          transcription_empty_after_clean: '🎤 Je n\'ai pas bien entendu. Peux-tu répéter plus fort ?',
        };
        const toastMessage = REJECTION_TOASTS[reasonKey]
          || '🎤 Je n\'ai pas bien entendu. Peux-tu répéter plus fort ?';
        toast.error(toastMessage, { duration: 4000 });
        return;
      }
      
      console.log('[processAudio] ✅ Text validation passed, proceeding to voice session');
      
      // 🔒 P0 OPTIMISTIC UI: Mettre à jour le message optimiste avec le texte réel
      // Au lieu de créer un nouveau message, on update celui qui existe déjà
      if (optimisticMessageId) {
        setConversation(prev => prev.map(msg => 
          msg.id === optimisticMessageId 
            ? { ...msg, content: userText, isTranscribing: false }
            : msg
        ));
        setTranscript(userText);
        console.log('[processAudio] 🔒 P0: Message optimiste mis à jour avec transcription réelle');
      } else {
        // Fallback: ancien comportement si pas d'ID optimiste (compatibilité)
        const newUserMessage = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: userText,
          timestamp: new Date().toISOString()
        };
        setConversation(prev => [...prev, newUserMessage]);
        setTranscript(userText);
      }
      
      // 2. Build system context with scenario if available
      // ════════════════════════════════════════════════════════════════════
      // 🎓 CERTIFIANT MODE: Le marqueur "[Mode: roleplay]" active le mode 
      // certifiant dans le backend (is_certifiant_mode() retourne true)
      // Cela garantit l'injection du prompt système avec RÈGLE FRANÇAIS ZÉRO
      // ════════════════════════════════════════════════════════════════════
      // 🔧 P0 FIX B: Contexte système TOUJOURS défini avec fallback robuste
      let systemContext = `Tu es Owen, le coach IA de ${userName || 'l\'utilisateur'} pour le module "${moduleName || 'coaching'}".\n\n[Mode: roleplay] - Jeu de rôle certifiant actif.`;
      
      if (scenario) {
        // Ajouter le contexte du scénario avec marqueurs explicites
        const scenarioPrompt = scenario.prompt || scenario.context || '';
        const scenarioRole = scenario.ai_role || '';
        systemContext += `\n\nScénario: ${scenario.title || 'Simulation de coaching'}`;
        if (scenarioPrompt) {
          systemContext += `\nContexte: ${scenarioPrompt}`;
        }
        if (scenarioRole) {
          systemContext += `\nJ'incarne: ${scenarioRole}`;
        }
        systemContext += `\n\nUtilise le prénom ${userName} dans tes réponses. Module: "${moduleName}".`;
        console.log('[processAudio] 📋 P0 FIX B: Contexte scénario chargé:', scenario.title);
      } else {
        // 🔧 P0 FIX B: Fallback contexte minimal si pas de scénario
        console.warn('[processAudio] ⚠️ GARDE B: Pas de scénario - utilisation contexte fallback');
        systemContext += `\n\nSession de coaching vocal en cours. Réponds de manière bienveillante et constructive à ${userName || 'l\'utilisateur'}. Module: "${moduleName || 'coaching'}".`;
      }
      
      console.log('[processAudio] 📋 P0 FIX B: Contexte système construit:', systemContext.length, 'chars');
      
      setLoadingMessage('Owen génère sa réponse vocale...');
      
      console.log('[processAudio] 🧠 Preparing voice session API call...');
      console.log('[processAudio] 📋 Payload validation:', {
        user_id: user?.user_id || user?.id || 'anonymous',
        input_text: userText,
        input_text_length: userText.length,
        context_length: conversation.slice(-6).length,
        module_id: moduleId,
        has_system_context: !!systemContext
      });
      
      // ✅ CRITICAL: Triple validation before API call
      if (!userText || userText.trim().length < 2) {
        console.error('[processAudio] ❌ CRITICAL: input_text is invalid before voice-session call');
        throw new Error('Texte invalide pour la session vocale');
      }
      
      // ✅ CRITICAL: Validate all required fields and ensure proper types
      const userId = String(user?.user_id || user?.id || user?.email || 'anonymous');
      const cleanUserText = String(userText.trim());
      const validModuleId = String(moduleId || '1');
      
      // 🎯 CORRECTION #4: Validate and sanitize context array (exclude first Owen message)
      const validContext = Array.isArray(conversation) 
        ? conversation
            .slice(-6)
            .filter(msg => {
              // 🎯 CORRECTION #4: Simple exclusion via ID du premier message d'Owen
              if (firstOwenMessageIdRef.current && msg.id === firstOwenMessageIdRef.current) {
                console.log('[processAudio] ✅ CORRECTION #4: Excluding first Owen message from context');
                return false; // Exclure le message de bienvenue
              }
              return true;
            })
            .map(msg => ({
              role: String(msg?.role || 'user'),
              content: String(msg?.content || '')
            }))
        : [];
      
      console.log('[processAudio] 🚀 Calling voice session API with validated payload...');
      console.log('[processAudio] 📋 Payload validation:', {
        user_id: userId,
        user_id_type: typeof userId,
        input_text_length: cleanUserText.length,
        input_text_type: typeof cleanUserText,
        module_id: validModuleId,
        module_id_type: typeof validModuleId,
        context_length: validContext.length,
        has_system_context: !!systemContext,
        is_coach_premium: isCoachPremiumMode  // 🏆 COACH PREMIUM
      });
      
      // ✅ CRITICAL: Build validated payload with EXPLICIT string conversion
      const voiceSessionPayload = {
        user_id: String(userId || 'anonymous'),
        input_text: String(cleanUserText || ''),
        context: validContext, // Array is OK per backend model
        module_id: validModuleId ? String(validModuleId) : null, // 🏆 TEC-1 FIX: null si mode libre
        // 🔒 B1 MOA (Déc 2025): Envoyer objective_id pour cibler l'objectif actif exact
        objective_id: activeObjective?.id || null,
        // 🔒 Mode: free ou certifiant uniquement
        mode: isFreeMode ? 'free' : 'certifiant',
        // 🏆 COACH PREMIUM: Plus de d4_vocal_mode ou d4_context
        coach_premium_context: coachPremiumContext || null,
        // 🧱 MODE BLOCS: Skip audio pour < 3s, TTS en background côté frontend
        skip_audio: true,
        // 🔒 P0 FIX SESSION_RUN_ID: Renvoyer l'ID unique de session pour éviter la fragmentation
        session_run_id: sessionRunIdRef.current || null,
        // 🎯 P1.0: Thème sélectionné (mode libre uniquement)
        selected_theme: isFreeMode && selectedTheme ? {
          slug: selectedTheme.slug,
          label: selectedTheme.label,
          category: selectedTheme.category,
          prompt_context: selectedTheme.prompt_context || selectedTheme.label
        } : null
      };
      
      // 🎯 P1.0 DEBUG: Log du thème envoyé
      if (isFreeMode && selectedTheme) {
        console.log('🎯 [P1.0 THEME] Envoi thème au backend:', voiceSessionPayload.selected_theme);
      }
      
      // 🏆 COACH PREMIUM LOG
      if (isCoachPremiumMode) {
        console.log('[processAudio] 🏆 COACH PREMIUM MODE ACTIF');
        console.log('[processAudio] 🏆 Premium Context:', coachPremiumContext);
      }
      
      // ✅ Only add system_context if it exists and is valid string
      if (systemContext) {
        const cleanContext = String(systemContext).trim();
        if (cleanContext.length > 0) {
          voiceSessionPayload.system_context = cleanContext;
        }
      }
      
      // ✅ CRITICAL: Validate ALL string fields before sending
      console.log('[processAudio] 🔍 PRE-SEND VALIDATION:');
      console.log('[processAudio]   user_id:', typeof voiceSessionPayload.user_id, '=', voiceSessionPayload.user_id);
      console.log('[processAudio]   input_text:', typeof voiceSessionPayload.input_text, '=', voiceSessionPayload.input_text.substring(0, 50));
      console.log('[processAudio]   module_id:', typeof voiceSessionPayload.module_id, '=', voiceSessionPayload.module_id);
      // 🔒 B1 MOA: Log objective_id pour debug
      console.log('[processAudio]   objective_id:', typeof voiceSessionPayload.objective_id, '=', voiceSessionPayload.objective_id);
      console.log('[processAudio]   system_context:', typeof voiceSessionPayload.system_context, '=', voiceSessionPayload.system_context ? voiceSessionPayload.system_context.substring(0, 50) : 'undefined');
      console.log('[processAudio]   context:', Array.isArray(voiceSessionPayload.context), 'length:', voiceSessionPayload.context.length);
      
      // ✅ ULTRA-CRITICAL: Validate no undefined/null/non-string in string fields
      if (typeof voiceSessionPayload.user_id !== 'string' || voiceSessionPayload.user_id.length === 0) {
        throw new Error('VALIDATION ERROR: user_id must be a non-empty string');
      }
      if (typeof voiceSessionPayload.input_text !== 'string' || voiceSessionPayload.input_text.length === 0) {
        throw new Error('VALIDATION ERROR: input_text must be a non-empty string');
      }
      // 🔧 P0 FIX: module_id peut être null en mode libre (isFreeMode)
      if (voiceSessionPayload.module_id !== null && (typeof voiceSessionPayload.module_id !== 'string' || voiceSessionPayload.module_id.length === 0)) {
        throw new Error('VALIDATION ERROR: module_id must be a non-empty string or null');
      }
      if (voiceSessionPayload.system_context !== undefined && typeof voiceSessionPayload.system_context !== 'string') {
        throw new Error('VALIDATION ERROR: system_context must be a string if provided');
      }
      
      console.log('[processAudio] ✅ ALL VALIDATIONS PASSED - Payload ready to send');
      
      // 🎯 PHASE 1 - PERFORMANCE DIAGNOSTIC: Track AI streaming time
      const streamingStartTime = performance.now();
      let firstTokenTime = null;
      console.log(`[PERF-FRONTEND] [AI_STREAMING] Start: ${streamingStartTime}ms`);
      
      // 🎯 DIAGNOSTIC FIX: Create message with separated content fields
      const tempMessageId = `streaming-${Date.now()}-${Math.random()}`;
      console.log('[processAudio] 🎯 DIAGNOSTIC: Creating message with fullContent/displayedContent separation');
      
      // Add empty message immediately
      const streamingMessage = {
        id: tempMessageId,
        role: 'assistant',
        content: '',  // Legacy (pour compatibilité)
        fullContent: '',  // Texte complet streamé (caché)
        displayedContent: '',  // Texte affiché (synchronisé avec audio)
        emotion: currentEmotion,
        posture: isFreeMode ? 'COACH' : 'ROLEPLAY',  // 🎯 V16 - Posture basée sur le mode
        timestamp: new Date().toISOString(),
        isStreaming: true,
        isVocal: true
      };
      
      setConversation(prev => [...prev, streamingMessage]);
      
      // ═══════════════════════════════════════════════════════════════════════
      // 🚀 P0.1 & P0.2: MODE SSE DÉCOUPLÉ — Validation MOA Janvier 2025
      // ═══════════════════════════════════════════════════════════════════════
      // NOUVEAU FLUX DÉCOUPLÉ:
      // 1. SSE streaming texte → Affichage IMMÉDIAT du texte
      // 2. TTS généré en background → audio_ready quand prêt
      // GAIN UX: Utilisateur voit le texte pendant que l'audio se génère
      // QUALITÉ AUDIO: HD identique (aucune modification TTS)
      // ═══════════════════════════════════════════════════════════════════════
      
      // 🧱 MACHINE À ÉTATS: THINKING
      // 🎯 P0-UX-1: Message français adapté au mode (certifiant vs libre)
      console.log('[P0.1 SSE] 🧠 ÉTAT → THINKING');
      setBlocState('THINKING');
      setOwenVocalState('THINKING');
      
      // 🎯 P0-UX-1: Message adapté selon le mode
      const isCertifiantMode = voiceSessionPayload.mode === 'certifiant';
      const thinkingMessage = isCertifiantMode 
        ? 'Owen analyse votre situation...' 
        : 'Owen réfléchit à sa réponse...';
      setLoadingMessage(thinkingMessage);
      
      let owenResponseText = '';
      let audioUrlReceived = null;
      
      try {
        console.log('[P0.1 SSE] 🚀 MODE SSE DÉCOUPLÉ — Streaming vers /voice-session/stream');
        
        // 🎯 Réinitialiser les compteurs
        currentPhraseIndex.current = 0;
        phrasesBuffer.current = [];
        audioQueue.current = [];
        nextOrderToPlay.current = 0;
        displayedTextLength.current = 0;
        currentlyPlayingPhrase.current = null;
        
        // 🚀 P0.2: Endpoint SSE streaming
        const sseUrl = `${BACKEND_URL}/api/ai/voice-session/stream`;
        console.log('[P0.1 SSE] 🌊 SSE URL:', sseUrl);
        console.log('[P0.1 SSE] 📋 Payload:', JSON.stringify(voiceSessionPayload, null, 2));
        
        // 🔧 P0 HEARTBEAT: Stocker le payload pour retry éventuel
        lastUserMessageRef.current = { url: sseUrl, payload: voiceSessionPayload, userText: cleanUserText };
        
        // 📊 Mesure des temps pour KPIs MOA
        const requestStartTime = Date.now();
        let ttfbMs = 0;
        let ttcMs = 0;
        let ttfaMs = 0;
        
        // 🔐 Token d'authentification
        const token = localStorage.getItem('token') || localStorage.getItem('access_token');
        
        // 🚀 P0.1: SSE Streaming avec découplage texte/audio
        // 🔒 P0 FIX (Jan 2025): Injection header X-Execution-Context-Id obligatoire
        const response = await fetch(sseUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            ...(token && { 'Authorization': `Bearer ${token}` }),
            // 🔒 P0 FIX: Injection contexte exécution pour éviter SSE Error 520
            ...(window.__currentExecutionContextId && { 
              'X-Execution-Context-Id': window.__currentExecutionContextId 
            })
          },
          body: JSON.stringify(voiceSessionPayload)
        });
        
        if (!response.ok) {
          // 🔒 P0 UX MOA (21 Jan 2026): 402 = fin de crédit, pas une erreur
          if (response.status === 402) {
            console.log('[P0 UX] 402 reçu - ouverture modal recharge (pas d\'erreur)');
            setShowSessionsExhaustedModal(true);
            refreshSessions(); // Mettre à jour le compteur
            return; // Stop sans erreur
          }
          
          // Autres erreurs = vraies erreurs techniques
          let userMessage = 'Une erreur est survenue. Réessayez.';
          
          if (response.status === 400) {
            userMessage = 'Erreur de connexion. Actualisez la page.';
          } else if (response.status === 520 || response.status === 502 || response.status === 503) {
            userMessage = 'Service temporairement indisponible. Réessayez dans quelques instants.';
          } else if (response.status === 401 || response.status === 403) {
            userMessage = 'Session expirée. Reconnectez-vous.';
          }
          
          throw new Error(userMessage);
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        // 🔧 P0 FIX PARSER SSE ROBUSTE (Janvier 2025)
        // Root cause: Le parser précédent ignorait les lignes data: si event: n'était pas reçu avant
        // Solution: Accumuler event/data dans un buffer et traiter uniquement quand les deux sont présents
        let pendingEvent = null;
        let pendingData = null;
        let tokenEventsReceived = 0; // 📊 Compteur diagnostic
        let textCompleteReceived = false; // 📊 Flag diagnostic
        
        // 🎯 Fonction de traitement d'un événement SSE complet
        const processSSEEvent = (eventName, eventData) => {
          switch (eventName) {
            case 'ping':
              // 🏓 Événement de connexion - ignorer silencieusement
              console.log('[P0.1 SSE] 🏓 Connexion SSE établie');
              break;
              
            case 'llm_fallback':
              // ⚠️ LLM a utilisé un fallback
              console.warn('[P0.1 SSE] ⚠️ LLM fallback utilisé:', eventData.reason);
              break;
              
            case 'analysis_start':
              console.log('[P0.1 SSE] 📊 Analyse démarrée:', eventData.message_fr);
              if (eventData.message_fr) setLoadingMessage(eventData.message_fr);
              break;
            
            // ═══════════════════════════════════════════════════════════════════
            // 🔒 P0 MOA (Jan 2026): SESSION_STARTED — CHAÎNE DE TRAÇABILITÉ
            // ═══════════════════════════════════════════════════════════════════
            // Cet événement est émis par le backend au début d'une session certifiante.
            // Le session_run_id est UNIQUE pour cette session et DOIT être transmis
            // à l'endpoint de feedback final pour isoler les preuves de CETTE session.
            // ═══════════════════════════════════════════════════════════════════
            case 'session_started':
              if (eventData.session_run_id) {
                sessionRunIdRef.current = eventData.session_run_id;
                console.log(`🔒 [SESSION_RUN_ID] REÇU DU BACKEND: ${eventData.session_run_id}`);
                console.log(`🔒 [SESSION_RUN_ID] objective_key=${eventData.objective_key || 'N/A'}`);
              } else {
                console.warn('[SESSION_RUN_ID] ⚠️ Event session_started sans session_run_id');
              }
              break;
              
            case 'first_token':
              ttfbMs = eventData.ttfb_ms || Date.now() - requestStartTime;
              console.log(`[P0.1 SSE] ⚡ TTFB: ${ttfbMs}ms`);
              // 🎯 P0.2: Transition vers GENERATING_RESPONSE
              setOwenVocalState('GENERATING_RESPONSE');
              setLoadingMessage('Owen formule sa réponse...');
              break;
              
            case 'token':
              tokenEventsReceived++; // 📊 Compteur diagnostic
              if (eventData.chunk) {
                owenResponseText += eventData.chunk;
                // 🎯 P0.1: Mise à jour texte en temps réel
                setConversation(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
                    updated[lastIdx] = {
                      ...updated[lastIdx],
                      text: owenResponseText
                    };
                  }
                  return updated;
                });
              }
              break;
              
            case 'text_complete':
              textCompleteReceived = true; // 📊 Flag diagnostic
              ttcMs = eventData.ttc_ms || Date.now() - requestStartTime;
              owenResponseText = eventData.full_text || owenResponseText;
              console.log(`[P0.1 SSE] 📝 TTC: ${ttcMs}ms | Texte: ${owenResponseText.length} chars`);
              
              // 🎯 P0.1: Texte complet visible IMMÉDIATEMENT
              setConversation(prev => {
                const updated = [...prev];
                const lastIdx = updated.length - 1;
                if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
                  updated[lastIdx] = {
                    ...updated[lastIdx],
                    text: owenResponseText,
                    isStreaming: false
                  };
                }
                return updated;
              });
              break;
              
            case 'tts_started':
              console.log('[P0.1 SSE] 🎧 TTS démarré:', eventData.message_fr);
              // 🎯 P0-UX-1: État PREPARING_AUDIO avec message français
              setOwenVocalState('PREPARING_AUDIO');
              setLoadingMessage(eventData.message_fr || 'Owen prépare sa réponse vocale...');
              break;
              
            case 'audio_ready':
              // 🚀 P0.1: Audio HD prêt — découplé du texte (fallback si pas de progressive)
              ttfaMs = eventData.ttfa_ms || 0;
              audioUrlReceived = eventData.audio_url;
              console.log(`[P0.1 SSE] 🔊 Audio HD prêt: TTFA=${ttfaMs}ms | Qualité: ${eventData.quality}`);
              break;
              
            case 'audio_segment':
              // 🚀 TTS PROGRESSIF: Segment audio reçu — lecture immédiate
              console.log(`[TTS_PROGRESSIVE] 📦 Segment #${eventData.order} reçu (is_last=${eventData.is_last})`);
              
              if (eventData.order === 0) {
                // Premier segment — initialiser et démarrer la lecture immédiatement
                ttfaMs = eventData.ttfa_ms || 0;
                progressiveAudioActive.current = true;
                audioSegmentQueue.current = [];
                currentSegmentOrder.current = 0;
                isPlayingSegment.current = false;
                console.log(`[TTS_PROGRESSIVE] ⚡ Premier segment prêt: TTFA=${ttfaMs}ms`);
              }
              
              // Ajouter à la queue
              audioSegmentQueue.current.push({
                order: eventData.order,
                audioUrl: eventData.audio_url,
                isLast: eventData.is_last,
                text: eventData.spoken_text || ''
              });
              
              // Tenter la lecture du prochain segment
              playNextSegment();
              break;
              
            case 'audio_error':
              console.warn('[P0.1 SSE] ⚠️ Audio error:', eventData.error);
              setLoadingMessage(eventData.message_fr || 'Audio temporairement indisponible');
              break;
              
            case 'validation_warning':
              console.warn('[P0.1 SSE] ⚠️ Validation warning:', eventData.reason);
              break;
              
            case 'metadata':
              // 🔒 ANTI-BOUCLE POST-CLÔTURE (Avril 2026)
              // Traiter les metadata SSE — notamment session_terminee
              console.log('[P0.1 SSE] 📋 Metadata:', eventData.action, eventData);
              // 📊 Phase 5 (12/02/2026) : trackers pour session_outcomes
              if (eventData.diagnostic && eventData.diagnostic.sufficiency_verdict) {
                lastVerdictRef.current = eventData.diagnostic.sufficiency_verdict;
              }
              if (eventData.decision && eventData.decision.direction) {
                lastDirectionRef.current = eventData.decision.direction;
              }
              turnsCountRef.current = (turnsCountRef.current || 0) + 1;
              // 🎯 Iter339 — Séparation stricte conversation_ended vs evaluation_ready.
              //   conversation_ended → STOP_CONVERSATION (micro off, audio Owen continue)
              //   evaluation_ready   → EVALUATION_READY (bouton Évaluation actif)
              //   session_terminee legacy traité comme conversation_ended (Phase 1 compat).
              if (eventData.conversation_ended || eventData.session_terminee) {
                transitionSessionPhase('STOP_CONVERSATION', 'sse_conversation_ended');
              }
              if (eventData.evaluation_ready) {
                transitionSessionPhase('EVALUATION_READY', 'sse_evaluation_ready');
              }
              // ⚠️ Garde-fou historique SUPPRIMÉ (Iter339) — is_last_criterion + critere_valide
              //     déclenchait une transition implicite redondante. Source canonique
              //     désormais : backend conversation_ended/evaluation_ready.
              // 📊 UX P1 (12/02/2026) : sync "preuves collectées" dès qu'un critère
              //     est validé en cours de session (pas uniquement à la fin) —
              //     single source of truth, on re-fetch via event bus.
              if (eventData.critere_valide) {
                console.log('[P0.1 SSE] 📊 Critère validé → refresh preuves collectées');
                try {
                  notifyEvaluationComplete({
                    module_id: moduleId ? parseInt(moduleId) : undefined,
                    reason: 'criterion_validated',
                    timestamp: new Date().toISOString(),
                  });
                } catch (err) {
                  console.warn('[P0.1 SSE] notifyEvaluationComplete failed:', err);
                }
              }
              break;
              
            case 'done':
              const totalMs = eventData.total_time_ms || Date.now() - requestStartTime;
              console.log(`[P0.1 SSE] ✅ DONE: Total=${totalMs}ms | Mode=${eventData.mode}`);
              
              // 📊 Log KPIs MOA
              console.log(`[P0.1 SSE] 📊 KPIs MOA: TTFB=${ttfbMs}ms | TTC=${ttcMs}ms | TTFA=${ttfaMs}ms | Total=${totalMs}ms`);
              
              // SLA Check MOA — Iter345: démoté en console.debug.
              //   La métrique elle-même (KPIs MOA log ci-dessus) reste, mais
              //   le warning seuil (3s) se déclenche normalement sur les
              //   réponses Owen longues → bruit en prod. Le suivi P95 se fait
              //   via la télémétrie backend (voice_session_metrics), pas via
              //   le console du navigateur.
              if (ttfbMs > 300) console.debug(`[P0.1 SSE] TTFB > 300ms: ${ttfbMs}ms`);
              if (ttcMs > 3000) console.debug(`[P0.1 SSE] TTC > 3000ms: ${ttcMs}ms`);
              
              // 💳 P0 FIX (Jan 2026): Rafraîchir le compteur de sessions APRÈS un délai
              // 🔒 P0 MOA: Utiliser refreshSilent pour éviter re-render (ne touche pas au loading)
              console.log('[P0.1 SSE] 💳 Rafraîchissement compteur sessions (SILENT)...');
              setTimeout(() => {
                if (isMountedRef.current) {
                  refreshSessionsSilent();
                }
              }, 500);
              break;
              
            default:
              console.log('[P0.1 SSE] 📦 Event:', eventName, eventData);
          }
        };
        
        // 🌊 Boucle de lecture SSE avec parser robuste
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // 🔧 P0 FIX: Traiter le dernier événement en attente avant de terminer
            if (pendingEvent && pendingData !== null) {
              processSSEEvent(pendingEvent, pendingData);
            }
            console.log('[P0.1 SSE] ✅ Stream terminé');
            console.log(`[P0.1 SSE] 📊 DIAGNOSTIC: tokenEvents=${tokenEventsReceived}, textComplete=${textCompleteReceived}`);
            break;
          }
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            // 🔧 P0 FIX: Ligne vide = fin d'un bloc SSE → traiter le bloc accumulé
            if (!line.trim()) {
              if (pendingEvent && pendingData !== null) {
                processSSEEvent(pendingEvent, pendingData);
                pendingEvent = null;
                pendingData = null;
              }
              continue;
            }
            
            // Parse SSE events avec buffer robuste
            if (line.startsWith('event: ')) {
              // 🔧 P0 FIX: Si on a un événement en attente, le traiter d'abord
              if (pendingEvent && pendingData !== null) {
                processSSEEvent(pendingEvent, pendingData);
              }
              pendingEvent = line.slice(7).trim();
              pendingData = null;
            } else if (line.startsWith('data: ')) {
              try {
                pendingData = JSON.parse(line.slice(6));
              } catch (parseErr) {
                // Données non-JSON (rare)
                pendingData = { raw: line.slice(6) };
                console.warn('[P0.1 SSE] Parse warning (non-JSON data):', line.slice(6));
              }
              
              // 🔧 P0 FIX: Traiter immédiatement si event ET data sont présents
              if (pendingEvent && pendingData !== null) {
                processSSEEvent(pendingEvent, pendingData);
                pendingEvent = null;
                pendingData = null;
              }
            }
          }
        }
        
        // 📊 DIAGNOSTIC: Logger si aucun événement token reçu
        if (tokenEventsReceived === 0) {
          console.error('[P0.1 SSE] 🔴 DIAGNOSTIC: Aucun événement token reçu!');
        }
        
        // 🚀 P0.1: Le SSE est terminé — Utiliser l'audio reçu via audio_ready
        console.log('[P0.1 SSE] 📊 Fin streaming SSE, traitement audio...');
        
        // 🔒 MOA JANVIER 2026: Suppression fallback hardcodé
        // Règle: Si le backend ne retourne pas de texte, c'est une ERREUR TECHNIQUE explicite
        // Pas de message "Owen" qui masque le bug — notification d'erreur claire
        if (!owenResponseText || owenResponseText.trim().length === 0) {
          console.error('[P0 SSE] ❌ ERREUR: Réponse texte vide reçue du backend');
          toast.error("Erreur technique: Owen n'a pas pu générer de réponse. Veuillez réessayer.", { duration: 5000 });
          // Marquer l'état comme erreur mais ne pas bloquer l'UI
          setIsProcessing(false);
          setBlocState('IDLE');
          setOwenVocalState('IDLE');
          return; // Sortir proprement sans afficher de faux message Owen
        }
        
        // 🎭 ROLEPLAY DETECTION: Vérifier si la réponse contient des marqueurs de jeu de rôle
        const responseTextLower = owenResponseText.toLowerCase();
        const roleplayIndicators = [
          "j'incarne", "🎭", "personnage", "simulation", "je suis votre",
          "marc dupont", "sophie martin", "thomas bernard", "claire moreau", "situation :"
        ];
        
        if (roleplayIndicators.some(ind => responseTextLower.includes(ind.toLowerCase()))) {
          console.log('[P0.1 SSE] 🎭 ROLEPLAY DETECTED in response');
          const characterMatch = owenResponseText.match(/(?:j'incarne|je suis)\s*:?\s*([A-ZÀ-Ü][a-zà-ü]+(?:\s+[A-ZÀ-Ü][a-zà-ü]+)?)/i);
          const detectedCharacter = characterMatch ? characterMatch[1].toLowerCase().replace(/\s+/g, '_') : 'senior_frustre';
          setRoleplaySessionData(prev => prev || {
            isRoleplay: true,
            character_key: detectedCharacter,
            detected_at: new Date().toISOString()
          });
        }
        
        // 🚀 P0.1: Mise à jour finale de la conversation
        console.log('[P0.1 SSE] 📝 Mise à jour finale conversation');
        
        setConversation(prev => {
          return prev.map(m => {
            if (m.id === tempMessageId) {
              return { 
                ...m, 
                content: owenResponseText,
                fullContent: owenResponseText,
                displayedContent: owenResponseText,
                isStreaming: false
              };
            }
            return m;
          });
        });
        
        console.log(`[P0.1 SSE] ✅ Texte affiché (${owenResponseText.length} chars)`);
        
        // ═══════════════════════════════════════════════════════════════════════════
        // 🚀 AUDIO — Mode progressif OU mode legacy
        // ═══════════════════════════════════════════════════════════════════════════
        // Si le mode progressif est actif (audio_segment reçus), le playback est
        // déjà géré par playNextSegment(). Sinon, fallback sur audio_ready legacy.
        // ═══════════════════════════════════════════════════════════════════════════
        
        if (progressiveAudioActive.current) {
          // Mode progressif actif — audio déjà en cours de lecture via playNextSegment
          console.log('[TTS_PROGRESSIVE] ✅ Mode progressif actif — audio géré par segments');
          // Ne rien faire ici, playNextSegment gère tout
        } else if (audioUrlReceived) {
          console.log('[P0.1 SSE] 🔊 Audio HD reçu via SSE — Lecture immédiate (legacy)');
          
          try {
            const audioElement = OwenAudioBus.createAudio(audioUrlReceived, { label: 'sse-hd-audio' });
            owenAudioRef.current = audioElement;
            
            audioElement.onplay = () => {
              console.log('[P0.1 SSE] 🗣️ ÉTAT → SPEAKING (audio.onplay)');
              setBlocState('SPEAKING');
              setOwenVocalState('SPEAKING');
              // Lip-sync: Feed text and duration for SSE legacy audio
              const dur = audioElement.duration;
              setLoadingMessage('');
              setIsProcessing(false);
            };

            // Update duration as soon as metadata is available (mobile fix)
            audioElement.ondurationchange = () => {
              const dur = audioElement.duration;
            };
            
            audioElement.onended = () => {
              console.log('[P0.1 SSE] ✅ ÉTAT → IDLE (audio.onended)');
              setBlocState('IDLE');
              setOwenVocalState('IDLE');
              // 🎤 LIP-SYNC: Clear
              setVocalStage('USER_TURN');
            };
            
            audioElement.onerror = (e) => {
              console.error('[P0.1 SSE] ❌ ÉTAT → ERROR (audio.onerror)', e);
              setBlocState('ERROR');
              setOwenVocalState('IDLE');
              setLoadingMessage('');
              setIsProcessing(false);
            };
            
            audioElement.play().catch(e => {
              console.warn('[P0.1 SSE] Autoplay blocked:', e);
              setBlocState('IDLE');
              setLoadingMessage('');
              setIsProcessing(false);
            });
            
            console.log('[P0.1 SSE] ✅ Audio HD lancé avec succès');
            
          } catch (audioErr) {
            console.warn('[P0.1 SSE] Audio error (non-bloquant):', audioErr);
            setBlocState('IDLE');
            setLoadingMessage('');
            setIsProcessing(false);
          }
        } else {
          // Pas d'audio reçu via SSE — terminer proprement
          console.log('[P0.1 SSE] ⚠️ Pas d\'audio reçu via SSE');
          setBlocState('IDLE');
          setOwenVocalState('IDLE');
          setLoadingMessage('');
          setIsProcessing(false);
        }
        
        // 📊 METRICS: Log temps total SSE
        const totalSseTime = Date.now() - requestStartTime;
        console.log(`[P0.1 SSE] 📊 METRICS MODE SSE: Total=${totalSseTime}ms`);
        
        // SLA Check MOA — Iter345: démoté en console.debug
        if (ttcMs > 3000) {
          console.debug(`[P0.1 SSE] SLA TTC > 3s: ${ttcMs}ms`);
        } else {
          console.debug(`[P0.1 SSE] SLA TTC OK: ${ttcMs}ms < 3s`);
        }
        
      } catch (apiError) {
        // 🚨 ERREUR API: Gérer proprement
        console.error('[processAudio] ❌ API Error:', apiError);
        
        // Afficher message d'erreur à l'utilisateur
        toast.error('Erreur lors de la communication avec Owen. Réessayez.');
        
        // Supprimer le message placeholder
        setConversation(prev => prev.filter(m => m.id !== tempMessageId));
        
        // L'erreur sera catchée par le try-catch global et le finally nettoiera tout
        throw apiError;
      }
      
      console.log('[processAudio] ✅ Owen response received - MODE BLOCS');
      setCoachState('active');
      
      // 🎯 FIX ÉTAT: Fallback - si aucun audio n'a été généré (queue vide), désactiver isProcessing après 2 secondes
      setTimeout(() => {
        if (audioQueue.current.length === 0 && !isPlayingAudioQueue.current) {
          console.log('[processAudio] ⚠️ FIX: No audio in queue after 2s - disabling isProcessing as fallback');
          setIsProcessing(false);
        }
      }, 2000);
      
    } catch (err) {
      console.error('[processAudio] ❌ Error:', err.message);
      const errorMsg = err.message || 'Erreur lors du traitement de votre message. Réessayez.';
      setError(errorMsg);
      setCoachState('error');
      
      // 🎯 FIX ÉTAT: En cas d'erreur, désactiver isProcessing immédiatement
      setIsProcessing(false);
      
      // ✅ PHASE 3: Auto-dismiss error toast after 5s
      toast.error(err.message || '⚠️ Je n\'ai pas bien entendu, peux-tu répéter ?', { 
        duration: 5000 
      });
      
      // ✅ PHASE 3: Auto-clear error message after 5s
      setTimeout(() => {
        setError(null);
        setCoachState('active');
      }, 5000);
    } finally {
      // 🎯 CORRECTION #6: Nettoyer le timeout global (CRITIQUE)
      clearTimeout(globalTimeoutId);
      
      // 🎯 CORRECTION #1: GARANTIR le relâchement de la garde (fail-safe)
      isProcessingAudioRef.current = false;
      console.log('✅ [CORRECTION #1] Guard released in finally block');
      
      // 🎯 CORRECTION #3: Nettoyer tous les flags isStreaming dans la conversation
      setConversation(prev => prev.map(msg => ({
        ...msg,
        isStreaming: false
      })));
      console.log('✅ [CORRECTION #3] All isStreaming flags cleaned');
      
      // 🎯 PHASE 1 - PERFORMANCE DIAGNOSTIC: Total processing completed
      const processingEndTime = performance.now();
      const totalProcessingDuration = processingEndTime - processingStartTime;
      console.log(`[PERF-FRONTEND] [TOTAL_PROCESSING] End: ${processingEndTime}ms | Total Duration: ${totalProcessingDuration.toFixed(2)}ms`);
      console.log(`[PERF-FRONTEND] [SUMMARY] Processing completed in ${totalProcessingDuration.toFixed(2)}ms`);
      
      setTranscript('');
      setLoadingMessage('');
    }
  };

  /**
   * 🎯 P1.0 MOA: Handler pour sélection de thème
   * 
   * RÈGLE MOA NON NÉGOCIABLE:
   * - Un thème sélectionné = un acte utilisateur
   * - Le thème est injecté comme message utilisateur explicite dans l'historique
   * - Visible dans l'UI au même titre qu'un message parlé
   * - Owen traite ce thème comme une intention utilisateur
   */
  const handleThemeSelection = async (theme) => {
    // Si même thème ou null, juste mettre à jour l'état sans envoyer de message
    if (!theme || (selectedTheme && selectedTheme.slug === theme.slug)) {
      setSelectedTheme(theme);
      return;
    }

    // 🛡️ GARDE: Empêcher les sélections pendant traitement
    if (isProcessingAudioRef.current || isProcessing || isListening || isSpeaking) {
      console.warn('[handleThemeSelection] ⚠️ BLOCKED - Processing in progress');
      return;
    }

    console.log('🎯 [P1.0 MOA] Thème sélectionné:', theme.label);

    // 1. Mettre à jour l'état du thème sélectionné
    setSelectedTheme(theme);

    // 2. Créer le message utilisateur système pour le thème
    const themeMessageId = `theme-${Date.now()}`;
    const themeUserText = `Thème choisi — ${theme.label}`;
    const themeMessage = {
      id: themeMessageId,
      role: 'user',
      content: themeUserText,
      timestamp: new Date().toISOString(),
      isThemeSelection: true  // Flag pour identifier ce type de message
    };

    // 3. Ajouter le message à la conversation (visible dans l'UI)
    setConversation(prev => [...prev, themeMessage]);
    console.log('🎯 [P1.0 MOA] Message thème ajouté à l\'historique:', themeUserText);

    // 4. Envoyer au backend via voice-session (comme un message utilisateur normal)
    // ✅ GARDE ANTI-RÉENTRANCE
    if (isProcessingAudioRef.current) {
      console.warn('[handleThemeSelection] ⚠️ BLOCKED - Already processing');
      return;
    }

    isProcessingAudioRef.current = true;
    setIsProcessing(true);
    setCoachState('active');
    setOwenVocalState('GENERATING_RESPONSE');
    setLoadingMessage('Owen prend en compte votre thème...');

    // Timeout de sécurité
    const globalTimeoutId = setTimeout(() => {
      console.error('[handleThemeSelection] ⏱️ TIMEOUT - Forcing USER_TURN');
      isProcessingAudioRef.current = false;
      setIsProcessing(false);
      setCoachState('idle');
      toast.error('Temps de traitement dépassé. Réessayez.');
    }, 60000);

    try {
      const userId = String(user?.user_id || user?.id || user?.email || 'anonymous');

      // 🎯 P1.0 MOA FIX: Inclure le message d'ouverture d'Owen dans le contexte
      // C'est crucial pour que le backend détecte turn_count=2 (DIAG) au lieu de 1 (START)
      const validContext = Array.isArray(conversation) 
        ? conversation
            .slice(-6)
            // 🚨 NE PAS exclure le premier message d'Owen pour les thèmes !
            // Le backend a besoin de voir qu'Owen a déjà parlé pour passer en DIAG
            .map(msg => ({
              role: String(msg?.role || 'user'),
              content: String(msg?.content || '')
            }))
        : [];

      console.log('🎯 [P1.0 MOA] Context construit:', {
        context_length: validContext.length,
        has_assistant_msg: validContext.some(m => m.role === 'assistant'),
        first_msg_role: validContext[0]?.role
      });

      // 🎯 P1.0 MOA: Payload avec le thème comme message utilisateur
      const voiceSessionPayload = {
        user_id: userId,
        input_text: themeUserText,  // "Thème choisi — Délégation"
        context: validContext,
        module_id: isFreeMode ? null : String(moduleId || '1'),
        mode: isFreeMode ? 'free' : 'guided',
        skip_audio: false,  // 🔊 MOA: TTS OBLIGATOIRE - Toute réponse Owen doit être lue
        // 🔒 P0 FIX SESSION_RUN_ID: Renvoyer l'ID unique de session pour éviter la fragmentation
        session_run_id: sessionRunIdRef.current || null,
        // 🎯 P1.0: Envoyer aussi l'objet thème complet pour le prompt
        selected_theme: {
          slug: theme.slug,
          label: theme.label,
          category: theme.category,
          prompt_context: theme.prompt_context || theme.label
        }
      };

      console.log('🎯 [P1.0 MOA] Envoi payload thème:', {
        input_text: themeUserText,
        selected_theme: voiceSessionPayload.selected_theme,
        context_length: validContext.length
      });

      // Créer message Owen temporaire
      const tempMessageId = `theme-response-${Date.now()}`;
      const streamingMessage = {
        id: tempMessageId,
        role: 'assistant',
        content: '',
        fullContent: '',
        displayedContent: '',
        timestamp: new Date().toISOString(),
        isStreaming: true,
        isVocal: false
      };
      setConversation(prev => [...prev, streamingMessage]);

      // 🔐 Token d'authentification
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');

      // 🧱 Appel API mode blocs (JSON complet, pas SSE)
      // 🔒 P0 FIX: Injection header X-Execution-Context-Id
      const response = await fetch(`${BACKEND_URL}/api/ai/voice-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(window.__currentExecutionContextId && { 
            'X-Execution-Context-Id': window.__currentExecutionContextId 
          })
        },
        body: JSON.stringify(voiceSessionPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // 🧱 Parse JSON complet
      const responseData = await response.json();
      
      console.log('🎯 [P1.0 MOA] Réponse backend:', {
        reply_text_length: responseData.reply_text?.length,
        has_audio: !!responseData.audio_url,
        reply_preview: responseData.reply_text?.substring(0, 100)
      });

      // Validation
      if (!responseData.reply_text || responseData.reply_text.trim().length === 0) {
        throw new Error('Réponse vide reçue du backend');
      }

      const owenResponseText = responseData.reply_text;
      const audioUrl = responseData.audio_url;
      
      // 🎯 Iter339 — Cf. streaming : séparation conversation_ended / evaluation_ready.
      if (responseData.metadata) {
        if (responseData.metadata.conversation_ended || responseData.metadata.session_terminee) {
          transitionSessionPhase('STOP_CONVERSATION', 'nonstream_conversation_ended');
        }
        if (responseData.metadata.evaluation_ready) {
          transitionSessionPhase('EVALUATION_READY', 'nonstream_evaluation_ready');
        }
        // ⚠️ Garde-fou historique is_last_criterion+critere_valide supprimé (Iter339).
      }

      // 🔊 MOA: Log pipeline TTS
      console.log('🔊 [P1.0 TTS] Pipeline audio:', {
        has_audio_url: !!audioUrl,
        audio_url: audioUrl,
        has_audio_ref: !!audioRef.current,
        reply_text_preview: owenResponseText?.substring(0, 50)
      });

      // Mettre à jour le message Owen avec la réponse complète
      setConversation(prev => prev.map(msg =>
        msg.id === tempMessageId
          ? { 
              ...msg, 
              content: owenResponseText, 
              fullContent: owenResponseText,
              displayedContent: owenResponseText,
              isStreaming: false, 
              audio_url: audioUrl,
              isVocal: !!audioUrl  // 🔊 Marquer comme message vocal si audio présent
            }
          : msg
      ));

      // 🔊 MOA: Jouer l'audio OBLIGATOIREMENT si disponible
      if (audioUrl) {
        console.log('🔊 [P1.0 TTS] Lecture audio démarrée:', audioUrl);
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          
          // 🎯 P1.0 MOA: Binding TTS ↔ État UI Animation "Owen parle"
          audioRef.current.onplay = () => {
            setOwenVocalState('SPEAKING');
            setIsSpeaking(true);
            console.log('🔊 [P1.0 TTS] 🗣️ Animation Owen parle ACTIVÉE');
          };
          
          audioRef.current.onended = () => {
            setOwenVocalState('IDLE');
            setIsSpeaking(false);
            setCoachState('active'); // Let audio handlers manage coach state
            console.log('🔊 [P1.0 TTS] 🧠 Animation Owen parle DÉSACTIVÉE (fin audio)');
          };
          
          audioRef.current.onerror = (err) => {
            setOwenVocalState('IDLE');
            setIsSpeaking(false);
            setCoachState('active'); // Let audio handlers manage coach state
            console.error('🔊 [P1.0 TTS] ❌ Erreur audio:', err);
          };
          
          try {
            await audioRef.current.play();
            console.log('🔊 [P1.0 TTS] ✅ Audio en lecture');
          } catch (e) {
            console.warn('🔊 [P1.0 TTS] ⚠️ Auto-play bloqué:', e.message);
            setIsSpeaking(false);
          }
        } else {
          console.error('🔊 [P1.0 TTS] ❌ audioRef.current est null');
        }
      } else {
        console.warn('🔊 [P1.0 TTS] ⚠️ Pas d\'audio_url dans la réponse - TTS non généré par backend');
      }

      console.log('🎯 [P1.0 MOA] ✅ Réponse Owen affichée pour thème:', theme.label);
      console.log('🎯 [P1.0 MOA] Réponse complète:', owenResponseText);
      
      // 💳 P0 FIX (Jan 2026): Rafraîchir le compteur de sessions après réponse POST
      // 🔒 P0 MOA: Utiliser refreshSilent pour éviter re-render
      console.log('[P1.0 MOA] 💳 Rafraîchissement compteur sessions (SILENT)...');
      refreshSessionsSilent();

    } catch (error) {
      console.error('[handleThemeSelection] ❌ Erreur:', error);
      toast.error('Erreur lors du traitement du thème. Réessayez.');
      
      // Supprimer le message streaming en cas d'erreur
      setConversation(prev => prev.filter(msg => !msg.isStreaming));
    } finally {
      clearTimeout(globalTimeoutId);
      isProcessingAudioRef.current = false;
      setIsProcessing(false);
      // Note: Removed setCoachState('idle') - let audio handlers manage state
      setLoadingMessage('');
      
      // Nettoyer les flags streaming
      setConversation(prev => prev.map(msg => ({
        ...msg,
        isStreaming: false
      })));
    }
  };
  
  // 🔒 P0 FIX: Cleanup complet à la sortie de page (mode vocal uniquement)
  // ═══════════════════════════════════════════════════════════════════════════════
  // RESPONSABILITÉS:
  // - Marquer comme démonté pour ignorer callbacks asynchrones
  // - Stopper tous les Audio en cours
  // - Vider la queue audio
  // - Annuler les requêtes TTS en vol
  // - Détacher tous les event listeners
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Marquer comme monté au montage
    isMountedRef.current = true;
    
    return () => {
      // 🔒 1. Marquer comme démonté IMMÉDIATEMENT
      isMountedRef.current = false;
      console.log('[CoachVocal] 🛑 CLEANUP: Composant en cours de démontage');
      
      // 🔒 2. Stopper et nettoyer audioRef
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.onplay = null;
        audioRef.current.onerror = null;
        audioRef.current.onloadedmetadata = null;
        audioRef.current.src = '';
        audioRef.current = null;
      }
      
      // 🔒 3. Stopper et nettoyer currentAudioRef
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.onended = null;
        currentAudioRef.current.onplay = null;
        currentAudioRef.current.onerror = null;
        currentAudioRef.current.src = '';
        currentAudioRef.current = null;
      }

      // 🔒 3bis (Iter338). Stopper et nettoyer owenAudioRef (audio HD legacy SSE)
      //   Ancien bug : cette ref n'était JAMAIS nettoyée à l'unmount, l'audio survivait.
      if (owenAudioRef.current) {
        try { owenAudioRef.current.pause(); } catch (_) { /* noop */ }
        owenAudioRef.current.onended = null;
        owenAudioRef.current.onplay = null;
        owenAudioRef.current.onerror = null;
        try { owenAudioRef.current.src = ''; } catch (_) { /* noop */ }
        owenAudioRef.current = null;
      }
      
      // 🔒 4. VIDER la queue audio (empêche playNextInQueue de relancer)
      audioQueue.current = [];
      isPlayingAudioQueue.current = false;
      nextOrderToPlay.current = 0;
      currentlyPlayingPhrase.current = null;
      currentPlayingMessageId.current = null;
      
      // 🔒 5. Nettoyer les intervalles
      if (revealIntervalRef.current) {
        clearInterval(revealIntervalRef.current);
        revealIntervalRef.current = null;
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      if (audioSyncInterval.current) {
        clearInterval(audioSyncInterval.current);
        audioSyncInterval.current = null;
      }
      if (safetyTimeout.current) {
        clearTimeout(safetyTimeout.current);
        safetyTimeout.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      
      // 🔒 6. Annuler les requêtes TTS en vol
      if (ttsAbortControllerRef.current) {
        ttsAbortControllerRef.current.abort();
        ttsAbortControllerRef.current = null;
      }
      
      // 🔒 6bis. Annuler le flux d'initialisation (scénario, opener, TTS welcome)
      if (initAbortControllerRef.current) {
        initAbortControllerRef.current.abort();
        initAbortControllerRef.current = null;
        console.log('[CoachVocal] 🛑 CLEANUP: Flux initialisation annulé');
      }
      
      // 🔒 7. Annuler le streaming en cours
      cancelStream();
      
      // 🔒 8. Arrêter le media stream (microphone)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      // 🛑 9. Iter338 — Filet de sécurité : stop tout audio Owen orphelin
      //    (race condition : un fetch TTS en vol peut créer un new Audio APRÈS
      //    le cleanup ; le bus l'aura registered → stopAll garantit l'arrêt).
      OwenAudioBus.stopAll('coachvocal_unmount');

      console.log('[CoachVocal] ✅ CLEANUP COMPLET: Audio stoppé, queue vidée, refs nettoyées');
    };
  }, [cancelStream]); // cancelStream est stable (useCallback)
  
  // 🔧 FIX STREAMING RC4: Synchroniser isStreaming global avec messages locaux
  useEffect(() => {
    const hasStreamingMessage = conversation.some(msg => msg.isStreaming === true);
    
    // Si aucun message n'est en streaming mais le hook dit que oui, reset.
    // 🔇 Iter345: passé en console.debug — c'est un cleanup async normal qui
    //   se déclenche à chaque fin de tour (race entre flip du hook et flip
    //   des flags message). Pas un bug → ne pas polluer la console prod.
    if (!hasStreamingMessage && isStreaming) {
      console.debug('[SYNC] RC4: hook isStreaming=true sans message en stream → reset');
      resetStream();
    }
    
    // Si un message est en streaming mais le hook dit que non, nettoyer les flags
    if (hasStreamingMessage && !isStreaming) {
      console.debug('[SYNC] RC4: message en stream mais hook isStreaming=false → cleanup flags');
      setConversation(prev => prev.map(msg => ({
        ...msg,
        isStreaming: false
      })));
    }
  }, [conversation, isStreaming, resetStream]);
  
  // Fetch module data, scenario, and start fresh session
  useEffect(() => {
    // ═══════════════════════════════════════════════════════════════════════════
    // 🔒 P0 MOA (Jan 2026): GUARD ABSOLU - OPTION C
    // Une fois initialisé, JAMAIS re-init (empêche refresh induit par sessionsLoading)
    // ═══════════════════════════════════════════════════════════════════════════
    if (hasInitialized) {
      console.log('[CoachVocal] 🔒 GUARD ABSOLU - hasInitialized=true, skip re-init');
      return;
    }
    
    // 🔒 P0 UX MOA (21 Jan 2026): Attendre que le hook sessions soit chargé
    if (sessionsLoading) {
      console.log('[CoachVocal] ⏳ Waiting for sessions to load...');
      return;
    }
    
    // 🔒 P0 UX MOA (21 Jan 2026): GUARD SESSIONS ÉPUISÉES - Bloquer toute initialisation IA
    if (sessionsExhausted) {
      console.log('[CoachVocal] 🔒 BLOCKED - Sessions épuisées, pas d\'initialisation IA');
      setShowSessionsExhaustedModal(true);
      return; // STOP - aucun appel backend
    }
    
    // ✅ CRITICAL FIX: Robust duplicate initialization prevention
    // Check 1: No user
    if (!user) {
      console.log('[CoachVocal] Skipping initialization - no user');
      return;
    }
    
    // Check 2: Already played welcome (ABSOLUTE GUARD - never bypass)
    if (hasPlayedWelcomeRef.current) {
      console.log('[CoachVocal] ✋ ABSOLUTE BLOCK - welcome already played, preventing any re-initialization');
      return;
    }
    
    // Check 3: Already initializing (race condition prevention)
    if (isInitializingRef.current) {
      console.log('[CoachVocal] ⚠️ BLOCKED duplicate initialization attempt - already initializing');
      return;
    }
    
    // Check 4: Recent initialization (within 5 seconds - increased for safety)
    const now = Date.now();
    if (initTimestampRef.current && (now - initTimestampRef.current) < 5000) {
      console.log('[CoachVocal] ⚠️ BLOCKED duplicate initialization - too recent (', now - initTimestampRef.current, 'ms ago)');
      return;
    }
    
    // ✅ moduleId always has fallback to '1', so no need for guard
    console.log('[CoachVocal] 🚀 Starting initialization for moduleId:', moduleId);
    
    const initializeCoaching = async () => {
      console.log('[CoachVocal] ✅ Starting initialization - all checks passed');
      
      // 🔒 P0 BIS: Créer AbortController pour ce flux d'initialisation
      initAbortControllerRef.current = new AbortController();
      const initSignal = initAbortControllerRef.current.signal;
      
      // Mark as initializing IMMEDIATELY (synchronous)
      isInitializingRef.current = true;
      initTimestampRef.current = Date.now();
      console.log('[CoachVocal] 🔒 Locked initialization (isInitializing=true, timestamp=' + initTimestampRef.current + ')');
      
      // 🔒 Iter339 — Reset à ACTIVE via setSessionPhase direct (bypass matrice pour init).
      sessionPhaseRef.current = 'ACTIVE';
      setSessionPhase('ACTIVE');
      outcomeLoggedRef.current = false;
      // 🎯 FLOW ÉVALUATION: reset phase à 'idle' pour nouvelle session
      setEvaluationFlowState({ phase: 'idle' });
      // Set loading state
      setCoachState('loading');
      setOwenVocalState('THINKING');  // 🔒 P0 ALIGNEMENT: État canonique THINKING
      console.log('🧠 [OWEN_STATE=THINKING] Génération scénario démarrée');
      setLoadingMessage('🧠 Owen prépare ton scénario de coaching personnalisé...');
      
      // Get user name and extract first name only
      const fullName = user.name || user.email?.split('@')[0] || 'Manager';
      const firstName = extractFirstName(fullName);
      setUserName(firstName);
      
      console.log(`[CoachVocal] User full name: "${fullName}", Using first name: "${firstName}"`);
      
      // 🔒 P0 BIS: Vérifier montage avant de continuer
      if (!isMountedRef.current || initSignal.aborted) {
        console.log('[CoachVocal] ⏹️ Initialization aborted - component unmounted');
        return;
      }
      
      // Fetch module and generate automatic scenario if moduleId provided
      if (isFreeMode) {
        // ✨ FREE MODE: Start free training session without module constraints
        console.log('[CoachVocal] 🆓 FREE MODE activated - starting free training session');
        setModuleName('Session Owen');  // 🏆 Coach Premium: Label sobre
        setLoadingMessage('');  // 🏆 Coach Premium: Pas de message de chargement
        await startFreeTrainingSession(firstName, initSignal);
        if (!isMountedRef.current) return; // 🔒 P0 BIS: Guard après await
        setCoachState('active');
      } else if (moduleId) {
        try {
          console.log('[CoachVocal] Fetching module data for moduleId:', moduleId);
          
          // ✅ CRITICAL FIX: Use safeFetchJSON
          // 🔒 P0 BIS: Passer le signal d'annulation
          const moduleData = await safeFetchJSON(`${BACKEND_URL}/api/modules-v2/${moduleId}`, { signal: initSignal });
          
          // 🔒 P0 BIS: Vérifier montage après fetch
          if (!isMountedRef.current || initSignal.aborted) {
            console.log('[CoachVocal] ⏹️ Module fetch complete but component unmounted');
            return;
          }
          
          console.log('[CoachVocal] Module data:', moduleData);
          
          if (moduleData) {
            setModuleName(moduleData.title);
            
            // ⭐ FIX ÉTAPE 1: TOUJOURS générer un nouveau scénario (pas de cache)
            console.log('[CoachVocal] 🔄 ÉTAPE 1: Generating NEW scenario (cache disabled)...');
            setLoadingMessage('🎯 Owen génère un nouveau scénario adapté à ton niveau...');
            transitionTo('SCENARIO_LOADING', 'scenario_generation_started');  // ✅ Transition vers LOADING
            
            const token = localStorage.getItem('token');
            let generatedScenario;
            
            // ✅ CRITICAL FIX: Use PEDAGOGICAL-ALIGNED endpoint /api/owen-voice/new-scenario
            // 🎯 GOUVERNANCE QUALIOPI: Ce endpoint récupère l'objectif pédagogique actif
            try {
              // 🎯 PERFORMANCE: Timestamp de début de génération
              const generateStartTime = performance.now();
              console.log(`[PERF-FRONTEND] [SCENARIO_GENERATION] Start: ${generateStartTime}ms`);
              console.log(`[CoachVocal] 🎯 GOUVERNANCE: Calling /api/owen-voice/new-scenario/${moduleId} (ALIGNED with active pedagogical objective)`);
              
              // 🔒 P0 BIS: Passer le signal d'annulation
              const generateData = await safeFetchJSON(`${BACKEND_URL}/api/owen-voice/new-scenario/${moduleId}`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                signal: initSignal
              });
              
              // 🔒 P0 BIS: Vérifier montage après génération scénario
              if (!isMountedRef.current || initSignal.aborted) {
                console.log('[CoachVocal] ⏹️ Scenario generation complete but component unmounted');
                return;
              }
              
              // 🎯 PERFORMANCE: Timestamp de fin de génération
              const generateEndTime = performance.now();
              const generateDuration = generateEndTime - generateStartTime;
              console.log(`[PERF-FRONTEND] [SCENARIO_GENERATION] End: ${generateEndTime}ms | Duration: ${generateDuration.toFixed(2)}ms`);
              
              // 🎯 VALIDATION: Vérifier que le scénario est aligné sur l'objectif pédagogique
              if (generateData.scenario?.target_objective_id) {
                console.log(`[CoachVocal] ✅ GOUVERNANCE: Scenario aligned with objective: ${generateData.scenario.target_objective_id} - "${generateData.scenario.target_objective_title}"`);
              } else if (generateData.scenario?.all_objectives_validated) {
                console.log('[CoachVocal] ✅ GOUVERNANCE: All objectives validated - consolidation scenario');
              } else {
                console.warn('[CoachVocal] ⚠️ GOUVERNANCE WARNING: Scenario may not be aligned with pedagogical objective');
              }
              
              console.log('[CoachVocal] ✅ ÉTAPE 1: Generated NEW ALIGNED scenario:', generateData.scenario);
              
              generatedScenario = generateData.scenario;
              setScenario(generatedScenario);
              
              // ✅ OPTIMISATION: Passer en mode actif IMMÉDIATEMENT (avant la génération TTS)
              // L'utilisateur voit l'interface pendant que l'audio se génère en arrière-plan
              console.log('[CoachVocal] ⚡ OPTIMISATION: Setting coach to active state immediately');
              setCoachState('active');
              setLoadingMessage('🎙️ Génération du scénario en cours…');
              
              // 🔒 P0 BIS: Vérifier une dernière fois avant de lancer TTS
              if (!isMountedRef.current || initSignal.aborted) {
                console.log('[CoachVocal] ⏹️ About to start TTS but component unmounted');
                return;
              }
              
              // Start with fresh scenario (génération TTS en arrière-plan)
              await startFreshSessionWithGeneratedScenario(moduleData.title, generatedScenario, firstName, initSignal);
            } catch (generateError) {
                // 🔒 P0 BIS: Ignorer les erreurs d'abort
                if (generateError.name === 'AbortError') {
                  console.log('[CoachVocal] ⏹️ Scenario generation aborted');
                  return;
                }
                // Fallback to existing scenarios if generation fails
                console.log('[CoachVocal] Generation failed, trying existing scenarios...', generateError);
                
                // 🔒 P0 BIS: Vérifier montage avant fallback
                if (!isMountedRef.current || initSignal.aborted) return;
                
                setLoadingMessage('Chargement du scénario de formation...');
                
                // ✅ CRITICAL FIX: Use safeFetchJSON
                const scenariosData = await safeFetchJSON(`${BACKEND_URL}/api/modules-v2/${moduleId}/scenarios`, { signal: initSignal });
                
                if (scenariosData && scenariosData.length > 0) {
                  const moduleScenario = scenariosData[0];
                  console.log('[CoachVocal] Using existing scenario:', moduleScenario);
                  setScenario(moduleScenario);
                  await startFreshSession(moduleData.title, moduleScenario, firstName);
                  setCoachState('active');
                } else {
                  console.log('[CoachVocal] No scenario available, starting generic session');
                  await startFreshSession(moduleData.title, null, firstName);
                  setCoachState('active');
                }
              }
          } else {
            // Module data not found
            console.error('[CoachVocal] Module data not found for moduleId:', moduleId);
            setCoachState('error');
            toast.error('Module introuvable');
          }
        } catch (error) {
          console.error('[CoachVocal] Error fetching module data:', error);
          
          // 🔒 P0 UX MOA (21 Jan 2026): 402 = fin de crédit, pas une erreur
          if (error.message?.includes('402') || error.message?.includes('sessions_exhausted')) {
            console.log('[P0 UX] 402 dans catch - ouverture modal recharge (pas d\'erreur)');
            setShowSessionsExhaustedModal(true);
            refreshSessions();
            // Ne PAS mettre coachState en 'error' - rester en état normal
            return;
          }
          
          setCoachState('error');
          
          // Vraies erreurs techniques seulement
          let userMessage = 'Une erreur est survenue lors du chargement. Réessayez.';
          let toastMessage = 'Erreur de chargement du module';
          
          // Erreur 400 = Contexte manquant (ne devrait plus arriver après le fix)
          if (error.message?.includes('400') || error.message?.includes('context')) {
            userMessage = 'Erreur de connexion. Actualisez la page pour réessayer.';
            toastMessage = 'Erreur de connexion';
          }
          // Erreur réseau
          else if (error.message?.includes('timeout') || error.message?.includes('network')) {
            userMessage = 'Connexion lente ou interrompue. Vérifiez votre connexion internet.';
            toastMessage = 'Problème de connexion';
          }
          
          setError(userMessage);
          toast.error(toastMessage);
        } finally {
          // ✅ CRITICAL: Release lock after initialization completes (success or error)
          isInitializingRef.current = false;
          console.log('[CoachVocal] 🔓 Released initialization lock');
        }
      } else {
        // No module ID, set to active immediately
        setCoachState('active');
        isInitializingRef.current = false;
        console.log('[CoachVocal] 🔓 Released initialization lock (no module)');
      }
    };
    
    initializeCoaching();
    
    // 🔒 P0 MOA (Jan 2026): Marquer comme initialisé APRÈS le lancement
    // Empêche définitivement les re-initialisations induites par sessionsLoading
    setHasInitialized(true);
    console.log('[CoachVocal] 🔒 hasInitialized = true - init locked forever');
    
  }, [moduleId, user?.user_id, user?.email, sessionsLoading, sessionsExhausted, hasInitialized]); // 🔒 P0 MOA OPTION C: hasInitialized ajouté pour compléter le guard
  
  // Start a fresh coaching session with scenario
  // 🎯 GOUVERNANCE PÉDAGOGIQUE: Le message d'accueil est maintenant orienté objectif
  const startFreshSession = async (moduleTitle, moduleScenario, userName) => {
    console.log(`[startFreshSession] Starting session for user: "${userName}", module: "${moduleTitle}"`);
    
    // 🎯 GOUVERNANCE PÉDAGOGIQUE: Récupérer le scénario orienté objectif si disponible
    let speechText;
    
    if (objectiveScenario && activeObjective) {
      // 🎯 Mode Gouvernance Owen: Utiliser le scénario orienté objectif
      console.log(`[startFreshSession] 🎯 GOVERNANCE MODE - Objectif actif: ${activeObjective.title}`);
      speechText = objectiveScenario.opening_message || 
        `Je suis ${objectiveScenario.owen_character_name || 'Alex'}. ${objectiveScenario.triggering_situation || 'J\'aurais besoin de votre aide sur une situation...'}`; 
    } else if (moduleScenario) {
      // Mode scénario classique (fallback)
      speechText = `Bonjour ${userName} ! Je suis Owen, ton coach vocal. Aujourd'hui, nous allons travailler sur : ${moduleScenario.title || moduleScenario.scenario_title}. ${moduleScenario.goal ? 'Ton objectif : ' + moduleScenario.goal : ''}. Es-tu prêt ?`;
    } else {
      // Mode sans scénario
      speechText = `Bonjour ${userName} ! Je suis Owen, ton coach vocal pour le module ${moduleTitle}. Partage-moi une situation concrète de ton quotidien managérial. Par où veux-tu commencer ?`;
    }
    
    // 💬 Owen 360° Proactive Intro (Phase 4)
    if (proactiveIntroMessage && !objectiveScenario) {
      speechText = `${proactiveIntroMessage}\n\n${speechText}`;
      console.log('[startFreshSession] ✨ Added proactive intro message');
    }
    
    console.log(`[startFreshSession] Speech text prepared (${speechText.length} chars)`);
    console.log(`[startFreshSession] ⚠️ Pas de message temporaire - attente de la transcription vocale`);
    
    // ✨ NOUVEAU: Ne PAS ajouter de message temporaire à la conversation
    // Le message sera ajouté automatiquement après la génération audio avec la transcription exacte
    // Cela évite les doublons (message temporaire écrit + transcription vocale)
    
    // Generate and play welcome audio with speech-optimized text
    // La transcription sera ajoutée automatiquement à la conversation dans playWelcomeAudio
    await playWelcomeAudio(speechText, userName, moduleTitle);
  };
  
  // 🆓 Start a free training session (no module constraints)
  // 🏆 COACH PREMIUM: Le mode libre utilise TOUJOURS le coach premium bienveillant
  // 🔒 P0 BIS: Accepte signal pour annulation
  const startFreeTrainingSession = async (userName, signal = null) => {
    // 🔒 P0 BIS: Guard de montage
    if (!isMountedRef.current || signal?.aborted) {
      console.log('[startFreeTrainingSession] ⏹️ Aborted - component unmounted');
      return;
    }
    
    console.log(`[startFreeTrainingSession] Starting free training for user: "${userName}"`);
    console.log(`[startFreeTrainingSession] 🏆 COACH PREMIUM MODE`);
    
    let speechText;
    let sessionLabel = 'Session Owen';
    
    // 🏆 COACH PREMIUM: Récupérer une ouverture premium du backend
    if (coachPremiumContext?.init) {
      // Utiliser l'intro fournie par le contexte
      console.log('[startFreeTrainingSession] 🏆 Premium Context with init:', coachPremiumContext);
      speechText = coachPremiumContext.init;
      sessionLabel = coachPremiumContext.label || 'Session Owen';
    } else {
      // 🏆 Fetch une ouverture PREMIUM du backend
      console.log('[startFreeTrainingSession] 🏆 Fetching TEC-1 PREMIUM opener from backend...');
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        // 🏆 TEC-1 PREMIUM: Passer le prénom pour ouverture personnalisée "Bonjour [Prénom],"
        const notificationType = coachPremiumContext?.type || '';
        // P1.3bis: URL enrichie avec theme pour ouverture adaptative
        const openerUrl = `${BACKEND_URL}/api/ai/d4-free-training/opener?user_first_name=${encodeURIComponent(userName)}&notification_type=${encodeURIComponent(notificationType)}`;
        
        console.log(`[startFreeTrainingSession] [P1.3bis] Fetching adaptive opener...`);
        
        // 🔒 P0 FIX: Injection header X-Execution-Context-Id + signal
        const openerResponse = await fetch(openerUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(window.__currentExecutionContextId && { 
              'X-Execution-Context-Id': window.__currentExecutionContextId 
            })
          },
          signal: signal // 🔒 P0 BIS: Propagation du signal
        });
        
        // 🔒 P0 BIS: Guard après fetch
        if (!isMountedRef.current || signal?.aborted) {
          console.log('[startFreeTrainingSession] ⏹️ Opener fetch complete but component unmounted');
          return;
        }
        
        if (openerResponse.ok) {
          const openerData = await openerResponse.json();
          speechText = openerData.opener;
          console.log(`[startFreeTrainingSession] [P1.3bis] Adaptive opener received:`);
          console.log(`[startFreeTrainingSession] [P1.3bis] profile_type=${openerData.profile_type}`);
          console.log(`[startFreeTrainingSession] [P1.3bis] confidence=${openerData.confidence}`);
          console.log(`[startFreeTrainingSession] [P1.3bis] opener="${speechText.substring(0, 60)}..."`);
        } else {
          // Fallback FIRST_SESSION avec prenom
          speechText = `Bonjour ${userName}. Tu peux parler librement ici, mais je ne te laisserai pas tourner en rond. Qu'est-ce que tu aimerais comprendre ou décider aujourd'hui ?`;
          console.log('[startFreeTrainingSession] [P1.3bis] API failed, using FIRST_SESSION fallback');
        }
      } catch (fetchError) {
        // 🔒 P0 BIS: Ignorer les erreurs d'abort
        if (fetchError.name === 'AbortError') {
          console.log('[startFreeTrainingSession] ⏹️ Opener fetch aborted');
          return;
        }
        console.error('[startFreeTrainingSession] [P1.3bis] fetch error:', fetchError);
        // P1.3bis: Fallback local FIRST_SESSION (PAS DE RANDOM)
        speechText = `Bonjour ${userName}. Tu peux parler librement ici, mais je ne te laisserai pas tourner en rond. Qu'est-ce que tu aimerais comprendre ou decider aujourd'hui ?`;
        console.log('[startFreeTrainingSession] [P1.3bis] Using FIRST_SESSION fallback (no random)');
      }
      sessionLabel = 'Session Owen';
    }
    
    // 🔒 P0 BIS: Guard avant de continuer vers TTS
    if (!isMountedRef.current || signal?.aborted) {
      console.log('[startFreeTrainingSession] ⏹️ About to call playWelcomeAudio but component unmounted');
      return;
    }
    
    console.log(`[startFreeTrainingSession] [P1.3bis] Owen init: "${speechText}"`);
    console.log(`[startFreeTrainingSession] Speech text prepared (${speechText.length} chars)`);
    
    // Set module name to indicate session type
    setModuleName(sessionLabel);
    
    // Generate and play welcome audio - 🔒 P0 BIS: Passer le signal
    await playWelcomeAudio(speechText, userName, sessionLabel, signal);
  };
  
  // Start a fresh coaching session with generated scenario
  // 🔒 P0 BIS: Accepte signal pour annulation
  const startFreshSessionWithGeneratedScenario = async (moduleTitle, generatedScenario, userName, signal = null) => {
    // 🔒 P0 BIS: Guard de montage
    if (!isMountedRef.current || signal?.aborted) {
      console.log('[startFreshSessionWithGeneratedScenario] ⏹️ Aborted - component unmounted');
      return;
    }
    
    console.log(`[startFreshSessionWithGeneratedScenario] Starting with AI scenario for user: "${userName}"`);
    
    // Extract scenario details
    const scenarioTitle = generatedScenario.title || generatedScenario.scenario_title || 'ce scénario';
    const scenarioContext = generatedScenario.context || generatedScenario.prompt || '';
    const scenarioGoal = generatedScenario.goal || generatedScenario.objective || '';
    const scenarioRole = generatedScenario.ai_role || '';
    
    // ═══════════════════════════════════════════════════════════════════════════
    // 🔒 P0-I.ter FIX (02/01/2025): UTILISER opening_message SI DISPONIBLE
    // ═══════════════════════════════════════════════════════════════════════════
    // L'ouverture ACTION-DRIVEN est construite côté backend quand une action de progrès
    // existe. Elle contient les marqueurs de continuité pédagogique:
    // - "Lors de notre dernière session..."
    // - "Nous avions identifié comme axe de progrès..."
    // - "C'est précisément le point que nous allons travailler aujourd'hui."
    // 
    // Si opening_message n'existe pas (première session), utiliser le format générique.
    // ═══════════════════════════════════════════════════════════════════════════
    
    let speechText;
    
    // Vérifier si l'ouverture ACTION-DRIVEN existe (P0-I.ter)
    const actionDrivenOpening = generatedScenario.opening_message || generatedScenario.openingMessage || generatedScenario.opening_question;
    
    if (actionDrivenOpening && actionDrivenOpening.trim().length > 50) {
      // 🔒 P0-I.ter: Utiliser l'ouverture ACTION-DRIVEN du backend
      console.log('[P0-I.ter] ✅ Using ACTION-DRIVEN opening from backend');
      console.log('[P0-I.ter] Opening preview:', actionDrivenOpening.substring(0, 100) + '...');
      
      speechText = actionDrivenOpening;
      
      // Ajouter le nom de l'utilisateur si absent
      if (!speechText.toLowerCase().includes(userName.toLowerCase()) && userName && userName !== 'Coach') {
        speechText = speechText.replace(/^Bonjour\.?/i, `Bonjour ${userName}.`);
      }
    } else {
      // Première session ou pas d'action de progrès - utiliser le format générique
      console.log('[P0-I.ter] ℹ️ No ACTION-DRIVEN opening - using generic format (first session)');
      
      speechText = `Bonjour ${userName}. Je suis Owen, ton coach vocal pour le module ${moduleTitle}. `;
      speechText += `J'ai préparé un scénario spécialement pour toi, intitulé : ${scenarioTitle}. `;
      
      // Ajouter le contexte (essentiel pour comprendre la situation)
      if (scenarioContext) {
        const cleanContext = scenarioContext
          .replace(/\*\*/g, '')
          .replace(/\n/g, ' ')
          .replace(/  +/g, ' ')
          .trim();
        speechText += `Voici le contexte : ${cleanContext}. `;
      }
      
      // Ajouter l'objectif
      if (scenarioGoal) {
        const cleanGoal = scenarioGoal
          .replace(/\*\*/g, '')
          .replace(/\n/g, ' ')
          .trim();
        speechText += `Ton objectif est le suivant : ${cleanGoal}. `;
      }
      
      // Ajouter le rôle de l'IA
      if (scenarioRole) {
        const cleanRole = scenarioRole
          .replace(/\*\*/g, '')
          .replace(/\n/g, ' ')
          .trim();
        speechText += `Pour cette simulation, ${cleanRole}. `;
      }
      
      speechText += `Es-tu prêt à commencer cet entraînement ?`;
    }
    
    // 💬 Owen 360° Proactive Intro (Phase 4)
    if (proactiveIntroMessage) {
      speechText = `${proactiveIntroMessage}\n\n${speechText}`;
      console.log('[startFreshSessionWithGeneratedScenario] ✨ Added proactive intro message');
    }
    
    console.log(`[startFreshSessionWithGeneratedScenario] Speech text prepared (${speechText.length} chars)`);
    console.log(`[startFreshSessionWithGeneratedScenario] ⚠️ Pas de message temporaire - attente de la transcription vocale`);
    
    // ✨ NOUVEAU: Ne PAS ajouter de message temporaire à la conversation
    // Cela évite les doublons (message temporaire écrit + transcription vocale)
    // Le message avec la transcription exacte sera ajouté dans playWelcomeAudioUnified après génération
    
    // 🔒 P0 BIS: Vérifier montage avant appel TTS
    if (!isMountedRef.current || signal?.aborted) {
      console.log('[startFreshSessionWithGeneratedScenario] ⏹️ About to call playWelcomeAudioUnified but component unmounted');
      return;
    }
    
    // 🎯 Appeler l'endpoint unifié /api/ai/scenario/speak
    // La transcription exacte sera ajoutée à la conversation dans cette fonction
    await playWelcomeAudioUnified(speechText, userName, moduleTitle, generatedScenario, signal);
  };

  // ✨ NEW FUNCTION: Révéler le texte du scénario mot par mot (synchronisé avec l'audio)
  const revealTextWordByWord = (fullText, estimatedDurationMs) => {
    console.log('[revealTextWordByWord] Starting word-by-word reveal...');
    console.log('[revealTextWordByWord] Text length:', fullText.length, 'chars, Duration:', estimatedDurationMs, 'ms');
    
    // Nettoyer le texte et extraire les mots
    const words = fullText.trim().split(/\s+/);
    const totalWords = words.length;
    
    console.log('[revealTextWordByWord] Total words:', totalWords);
    
    if (totalWords === 0) {
      setScenarioTextRevealed(true);
      return;
    }
    
    // Calculer l'intervalle entre chaque mot (en ms)
    const intervalMs = Math.max(50, estimatedDurationMs / totalWords); // Minimum 50ms par mot
    
    console.log('[revealTextWordByWord] Interval per word:', intervalMs, 'ms');
    
    setIsRevealingText(true);
    setRevealedScenarioText(''); // Reset
    
    let currentWordIndex = 0;
    
    // Créer l'intervalle d'animation
    const interval = setInterval(() => {
      if (currentWordIndex < totalWords) {
        // Ajouter le mot actuel
        const revealedSoFar = words.slice(0, currentWordIndex + 1).join(' ');
        setRevealedScenarioText(revealedSoFar);
        currentWordIndex++;
      } else {
        // Fin de l'animation
        clearInterval(interval);
        setIsRevealingText(false);
        setScenarioTextRevealed(true); // Texte complètement révélé
        setRevealedScenarioText(fullText); // Assurer le texte complet
        console.log('[revealTextWordByWord] Text reveal complete!');
      }
    }, intervalMs);
    
    // Sauvegarder la référence pour pouvoir l'annuler si nécessaire
    revealIntervalRef.current = interval;
  };
  
  // Generate and play welcome audio
  // 🔒 P0 BIS: Accepte signal pour annulation
  const playWelcomeAudio = async (greetingText, userName, moduleTitle, signal = null) => {
    // 🔒 P0 BIS: Guard de montage immédiat
    if (!isMountedRef.current || signal?.aborted) {
      console.log('[playWelcomeAudio] ⏹️ Aborted - component unmounted before start');
      return;
    }
    
    // ✅ CRITICAL FIX: Generate unique request ID for deduplication
    const requestId = `welcome-${Date.now()}-${Math.random()}`;
    console.log('[playWelcomeAudio] Called with requestId:', requestId);
    console.log('[playWelcomeAudio] State - hasPlayedWelcome:', hasPlayedWelcomeRef.current, 'isSpeaking:', isSpeaking, 'activeRequests:', activeRequestsRef.current.size);
    
    // Check 1: Already played welcome
    if (hasPlayedWelcomeRef.current) {
      console.log('[playWelcomeAudio] ⛔ BLOCKED - welcome already played');
      return;
    }
    
    // Check 2: Duplicate request in progress
    if (activeRequestsRef.current.has('playWelcomeAudio')) {
      console.log('[playWelcomeAudio] ⛔ BLOCKED - duplicate request already in progress');
      return;
    }
    
    // Mark as active IMMEDIATELY (synchronous)
    activeRequestsRef.current.add('playWelcomeAudio');
    hasPlayedWelcomeRef.current = true;
    console.log('[playWelcomeAudio] 🔒 LOCKED - marked as played and added to active requests');
    
    if (isSpeaking) {
      console.log('[playWelcomeAudio] Currently speaking - skipping but keeping lock');
      return;
    }
    
    console.log('[playWelcomeAudio] ✅ All checks passed - proceeding with audio generation');
    
    // 🎯 PHASE 2 - OPTIMISATION: Clean text for TTS only (preserve Markdown for display)
    const cleanText = greetingText
      .replace(/\*\*/g, '') // Remove bold markdown
      .replace(/🎯|👋|✅/g, '') // Remove emojis
      .replace(/\n\n/g, '. '); // Convert line breaks to pauses
    
    // 🎯 AUDIO-SYNCED: Préparer message avec fullContent/displayedContent
    console.log('[playWelcomeAudio] ⚡ AUDIO-SYNCED: Preparing welcome message with progressive display');
    const welcomeMessageId = `welcome-${Date.now()}`;
    
    // 🎯 CORRECTION #4: Marquer ce message comme premier message d'Owen
    firstOwenMessageIdRef.current = welcomeMessageId;
    console.log('[playWelcomeAudio] ✅ CORRECTION #4: Marked as first Owen message:', welcomeMessageId);
    
    // ✅ FIX PERSISTANCE: Ajouter le message SANS écraser les précédents
    // content = texte original riche (Markdown) pour affichage final
    // fullContent = texte nettoyé pour TTS audio-sync progressif
    setConversation(prev => [...prev, {
        id: welcomeMessageId,
        role: 'assistant',
        content: greetingText,  // Texte original riche (Markdown) pour affichage
        fullContent: cleanText,  // Texte nettoyé pour TTS
        displayedContent: '',  // Vide au début, sera rempli progressivement
        emotion: 'empathique',
        posture: isFreeMode ? 'COACH' : 'ROLEPLAY',  // 🎯 V16
        timestamp: new Date().toISOString(),
        isVocal: true,
        isGeneratingAudio: true,
        isPlayingAudio: false
      }
    ]);
    
    // 🎯 PHASE 2 - OPTIMISATION: Passer l'interface en mode "active" immédiatement
    setCoachState('active');
    
    try {
      // 🎯 PHASE 2 - PERFORMANCE: Timestamp de début de génération TTS
      const ttsWelcomeStartTime = performance.now();
      console.log(`[PERF-FRONTEND] [TTS_WELCOME] Start: ${ttsWelcomeStartTime}ms`);
      console.log('[playWelcomeAudio] 📞 Calling TTS API in background (text length:', cleanText.length, 'chars)');
      
      // ✅ CRITICAL FIX: Use safeFetchJSON to prevent "Response body already used"
      // 🔒 P0 BIS: Passer le signal d'annulation
      const data = await safeFetchJSON(`${BACKEND_URL}/api/ai/text-to-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: cleanText,
          emotion: 'empathique',
          user_id: user?.user_id || user?.id || 'anonymous'
        }),
        signal: signal // 🔒 P0 BIS: Propagation du signal
      });
      
      // 🔒 P0 BIS: Guard après TTS fetch - CRITIQUE
      if (!isMountedRef.current || signal?.aborted) {
        console.log('[playWelcomeAudio] ⏹️ TTS complete but component unmounted - NOT playing audio');
        return;
      }
      
      // 🎯 PHASE 2 - PERFORMANCE: Timestamp de fin de génération TTS
      const ttsWelcomeEndTime = performance.now();
      const ttsWelcomeDuration = ttsWelcomeEndTime - ttsWelcomeStartTime;
      console.log(`[PERF-FRONTEND] [TTS_WELCOME] End: ${ttsWelcomeEndTime}ms | Duration: ${ttsWelcomeDuration.toFixed(2)}ms`);
      
      console.log('[playWelcomeAudio] ✅ TTS success - audio_url:', !!data.audio_url);
      
      if (data.audio_url) {
        // 🔒 P0 BIS: Vérification finale avant création Audio
        if (!isMountedRef.current || signal?.aborted) {
          console.log('[playWelcomeAudio] ⏹️ About to create Audio but component unmounted');
          return;
        }
        
        // ✨ NOUVEAU: Utiliser spoken_text (transcription exacte) au lieu du texte original
        const displayText = data.spoken_text || cleanText; // Fallback si pas de transcription
        console.log('[playWelcomeAudio] 🎯 Using spoken_text for perfect audio ↔ text sync');
        console.log('[playWelcomeAudio] Playing audio...');
        console.log(`[playWelcomeAudio] 🔊 Intro vocale Owen lancée pour "${userName}"`);
        console.log('[playWelcomeAudio] 🔍 Text sync - Original:', cleanText.substring(0, 30), '... | Spoken:', displayText.substring(0, 30), '...');
        
        // 🎯 AUDIO-SYNCED: Mettre à jour avec audio et démarrer affichage progressif
        console.log('[playWelcomeAudio] 📝 AUDIO-SYNCED: Starting progressive display synchronized with audio');
        
        // 🔒 P0 ALIGNEMENT: Transition vers PREPARING_AUDIO (pas SPEAKING!)
        transitionTo('SCENARIO_INTRO', 'welcome_audio_starting');
        setOwenVocalState('PREPARING_AUDIO');
        console.log('🔊 [OWEN_STATE=PREPARING_AUDIO] Welcome audio en préparation');
        
        const audio = OwenAudioBus.createAudio(data.audio_url, { label: 'welcome' });
        audioRef.current = audio;
        currentPlayingMessageId.current = welcomeMessageId;
        
        // 🔒 P0 ALIGNEMENT: SPEAKING uniquement sur onplay (audio réellement diffusé)
        audio.onplay = () => {
          setOwenVocalState('SPEAKING');
          setIsSpeaking(true);
          // Lip-sync: Feed spoken text and audio duration to avatar
          const dur = audio.duration;
          console.log('🗣️ [OWEN_STATE=SPEAKING] Welcome audio réellement diffusé');
        };

        // Update duration as soon as metadata is available (mobile fix)
        audio.ondurationchange = () => {
          const dur = audio.duration;
        };
        
        // Mettre à jour avec audio URL
        setConversation(prev => prev.map(msg => {
          if (msg.id === welcomeMessageId) {
            return {
              ...msg,
              audioUrl: data.audio_url,
              spokenText: data.spoken_text,
              originalText: data.original_text,
              transcriptionConfidence: data.transcription_confidence,
              isGeneratingAudio: false,
              isPlayingAudio: true,
              fullContent: displayText
            };
          }
          return msg;
        }));
        
        // 🎯 DIAGNOSTIC: DÉSACTIVATION de revealTextWordByWord (remplacé par audio-synced display)
        console.log('[playWelcomeAudio] 🎯 DIAGNOSTIC: Old revealTextWordByWord DISABLED - using audio-synced display instead');
        
        // 🎯 AUDIO-SYNCED: Synchroniser l'affichage avec la lecture audio
        audio.onloadedmetadata = () => {
          console.log('[playWelcomeAudio] 🎯 Audio loaded, duration:', audio.duration, 's');
          console.log('[playWelcomeAudio] 🇫🇷 STREAMING VISUEL DÉSACTIVÉ - Affichage immédiat');
          
          // 🔒 AFFICHER IMMÉDIATEMENT le texte complet (pas d'intervalle progressif)
          setConversation(prev => prev.map(msg => {
            if (msg.id === welcomeMessageId) {
              return {
                ...msg,
                displayedContent: displayText, // 🔒 Texte COMPLET immédiatement
                audioProgress: 0,
                isPlayingAudio: true
              };
            }
            return msg;
          }));
          console.log('[playWelcomeAudio] 🇫🇷 Texte complet affiché immédiatement:', displayText.length, 'chars');
        };
        
        audio.onended = () => {
          console.log('[playWelcomeAudio] ✅ Audio playback ended');
          
          // 🔒 PAS d'intervalle à nettoyer car il n'y en a plus
          
          // 🔒 P0 ALIGNEMENT: Reset état vocal
          setOwenVocalState('IDLE');
          setIsSpeaking(false);
          // 🎤 LIP-SYNC: Clear spoken text
          console.log('🧠 [OWEN_STATE=IDLE] Welcome audio terminé');
          
          // Finaliser le message
          setConversation(prev => prev.map(msg => {
            if (msg.id === welcomeMessageId) {
              return {
                ...msg,
                displayedContent: displayText,
                audioProgress: 1,
                isPlayingAudio: false
              };
            }
            return msg;
          }));
          
          setIsInitialScenarioPlaying(false);  // Débloquer les boutons après intro
          currentPlayingMessageId.current = null;
          
          console.log('[playWelcomeAudio] 🔓 Initial scenario playback ended - buttons unlocked');
          
          // S'assurer que le texte est complètement révélé
          if (scenario && !scenarioTextRevealed) {
            console.log('[playWelcomeAudio] Audio ended - forcing text reveal completion');
            setScenarioTextRevealed(true);
            setIsRevealingText(false);
            if (revealIntervalRef.current) {
              clearInterval(revealIntervalRef.current);
            }
          }
          
          // 🔧 FIX STREAMING: Garantir transition vers USER_TURN après welcome
          transitionTo('USER_TURN', 'welcome_audio_ended');
        };
        
        // Fallback: Débloquer après 10s si l'audio ne démarre pas ou ne se termine pas
        const unlockTimeout = setTimeout(() => {
          if (isInitialScenarioPlaying) {
            console.log('[playWelcomeAudio] ⏰ Timeout fallback - unlocking buttons after 10s');
            setIsInitialScenarioPlaying(false);
            setOwenVocalState('IDLE');
            setIsSpeaking(false);
            console.log('🧠 [OWEN_STATE=IDLE] Timeout fallback');
          }
        }, 10000);
        
        audio.onerror = () => {
          console.error('[playWelcomeAudio] Error playing welcome audio');
          clearTimeout(unlockTimeout);  // Nettoyer timeout
          setOwenVocalState('IDLE');
          setIsSpeaking(false);
          console.log('🧠 [OWEN_STATE=IDLE] Welcome audio error');
          setIsInitialScenarioPlaying(false);  // Débloquer en cas d'erreur
          // Afficher message discret si l'audio ne peut pas se jouer
          toast.info('🔈 Active ton son pour écouter Owen', { duration: 4000 });
          
          console.log('[playWelcomeAudio] 🔓 Audio error - buttons unlocked');
          
          // En cas d'erreur audio, révéler quand même le texte immédiatement
          if (scenario) {
            setScenarioTextRevealed(true);
            setIsRevealingText(false);
            if (revealIntervalRef.current) {
              clearInterval(revealIntervalRef.current);
            }
          }
          
          // 🔧 FIX STREAMING: Garantir transition vers USER_TURN en cas d'erreur
          transitionTo('USER_TURN', 'welcome_audio_error');
        };
        
        // 🔧 FIX STREAMING: Setup timeout sécurité avant lecture
        setupAudioSafetyTimeout(audio, 60000);
        
        // Tenter de jouer l'audio
        audio.play().catch(err => {
          console.error('[playWelcomeAudio] Autoplay blocked by browser:', err);
          setIsSpeaking(false);
          clearTimeout(unlockTimeout);
          
          // 🔒 D4 FIX: Débloquer les boutons même si autoplay échoue
          setIsInitialScenarioPlaying(false);
          
          // Message discret si l'autoplay est bloqué
          toast.info('🔈 Active ton son pour écouter l\'introduction d\'Owen', { duration: 4000 });
          
          // En cas d'échec autoplay, révéler le texte immédiatement
          if (scenario) {
            setScenarioTextRevealed(true);
            setIsRevealingText(false);
            if (revealIntervalRef.current) {
              clearInterval(revealIntervalRef.current);
            }
          }
          
          // 🔧 FIX STREAMING: Garantir transition vers USER_TURN en cas d'échec play
          transitionTo('USER_TURN', 'welcome_audio_play_failed');
        });
      } else {
        console.log('[playWelcomeAudio] No audio URL in response');
        setIsSpeaking(false);
      }
    } catch (error) {
      // 🔒 P0 BIS: Ignorer les erreurs d'abort (sortie de page normale)
      if (error.name === 'AbortError') {
        console.log('[playWelcomeAudio] ⏹️ TTS fetch aborted - page exit');
        return;
      }
      console.error('[playWelcomeAudio] ❌ Fatal error:', error);
      setIsSpeaking(false);
      toast.error('Erreur lors de la génération de l\'audio', { duration: 5000 });
    } finally {
      // ✅ CRITICAL: Remove from active requests when done
      activeRequestsRef.current.delete('playWelcomeAudio');
      console.log('[playWelcomeAudio] 🔓 UNLOCKED - removed from active requests');
    }
  };

  // 🎯 NOUVEAU: Lecture audio unifiée avec endpoint /api/ai/scenario/speak

  // 🎯 FONCTION COMMUNE : Lecture audio SANS streaming visuel (28 déc 2024)
  // ════════════════════════════════════════════════════════════════════════════
  // 🇫🇷 STABILISATION LINGUISTIQUE: Le streaming visuel est SUPPRIMÉ
  // Le texte est affiché COMPLET immédiatement, sans animation progressive
  // Cela aligne le frontend avec l'architecture backend non-streaming
  // ════════════════════════════════════════════════════════════════════════════
  const playAudioWithStreamingDisplay = async (text, audioUrl, messageId, options = {}) => {
    const {
      onStart = () => {},
      onEnd = () => {},
      messageType = 'assistant',
      emotion = 'empathique',
      skipTextDisplay = false
    } = options;
    
    console.log('[playAudioWithStreamingDisplay] 🎵 Starting audio playback (NON-STREAMING DISPLAY)');
    console.log('[playAudioWithStreamingDisplay] Message ID:', messageId, 'Type:', messageType);
    console.log('[playAudioWithStreamingDisplay] 🇫🇷 STREAMING VISUEL DÉSACTIVÉ - Affichage texte complet immédiat');
    
    // 🎯 SAFETY: Ref pour éviter double appel onEnd
    let onEndCalled = false;
    const safeOnEnd = () => {
      if (onEndCalled) {
        console.log('[playAudioWithStreamingDisplay] ⚠️ onEnd already called, skipping');
        return;
      }
      onEndCalled = true;
      console.log('[playAudioWithStreamingDisplay] 🔓 Calling onEnd callback (first time)');
      onEnd();
    };
    
    try {
      // Créer NOUVEAU Audio (pas de réutilisation audioRef global)
      const audio = OwenAudioBus.createAudio(audioUrl, { label: 'streaming-display' });
      audioRef.current = audio;
      currentPlayingMessageId.current = messageId;
      
      // Set états
      setIsSpeaking(true);
      onStart();
      
      console.log('[playAudioWithStreamingDisplay] ✅ Audio created, calling onStart callback');

      // Lip-sync: set owenVocalState and feed text/duration on actual playback
      audio.onplay = () => {
        setOwenVocalState('SPEAKING');
        const dur = audio.duration;
      };

      // Update duration as soon as metadata is available (mobile fix)
      audio.ondurationchange = () => {
        const dur = audio.duration;
      };
      
      // 🇫🇷 AFFICHAGE IMMÉDIAT DU TEXTE COMPLET (pas de streaming)
      // 🚀 O3: skipTextDisplay = true pour segment 2 (texte déjà affiché par segment 1)
      if (!skipTextDisplay) {
        setConversation(prev => prev.map(msg => {
          if (msg.id === messageId) {
            return {
              ...msg,
              displayedContent: text,
              audioProgress: 0,
              isPlayingAudio: true
            };
          }
          return msg;
        }));
        console.log('[playAudioWithStreamingDisplay] Texte affiché:', text.length, 'chars');
      } else {
        console.log('[playAudioWithStreamingDisplay] 🚀 O3: skipTextDisplay=true (segment 2)');
      }
      
      // 🎯 SAFETY TIMEOUT: Si l'audio ne se termine pas après 180s (3 minutes), forcer l'arrêt
      // Note: Timeout volontairement long pour permettre les scénarios d'ouverture complets
      const safetyTimeoutId = setTimeout(() => {
        console.warn('[playAudioWithStreamingDisplay] ⚠️ Safety timeout reached (180s) - forcing onEnd');
        // Vérifier si l'audio est vraiment bloqué (pas en train de jouer)
        if (audio && audio.paused && audio.currentTime === 0) {
          console.error('[playAudioWithStreamingDisplay] Audio appears blocked - forcing cleanup');
          setIsSpeaking(false);
          setIsInitialScenarioPlaying(false);
          safeOnEnd();
        } else if (audio && !audio.paused) {
          console.warn('[playAudioWithStreamingDisplay] Audio still playing after 180s - allowing to continue');
          // L'audio joue encore, ne pas interrompre (scénario très long)
        } else {
          // Audio terminé mais onended pas appelé - forcer cleanup
          console.error('[playAudioWithStreamingDisplay] Audio ended but onended not fired - forcing cleanup');
          setIsSpeaking(false);
          setIsInitialScenarioPlaying(false);
          safeOnEnd();
        }
      }, 180000);
      
      // 🇫🇷 PAS DE STREAMING VISUEL - Audio joue normalement
      audio.onloadedmetadata = () => {
        console.log('[playAudioWithStreamingDisplay] 🎯 Audio loaded, duration:', audio.duration, 's');
        // 🔒 PAS d'intervalle de streaming - le texte est déjà affiché complet
      };
      
      // Audio terminé
      audio.onended = () => {
        console.log('[playAudioWithStreamingDisplay] ✅ Audio playback ended');
        clearTimeout(safetyTimeoutId);
        
        setConversation(prev => prev.map(msg => {
          if (msg.id === messageId) {
            return {
              ...msg,
              displayedContent: text,
              audioProgress: 1,
              isPlayingAudio: false
            };
          }
          return msg;
        }));
        
        setIsSpeaking(false);
        setOwenVocalState('IDLE');
        setIsInitialScenarioPlaying(false);
        currentPlayingMessageId.current = null;
        safeOnEnd();
      };
      
      // Erreur audio
      audio.onerror = (err) => {
        console.error('[playAudioWithStreamingDisplay] ❌ Audio error:', err);
        clearTimeout(safetyTimeoutId);
        
        if (audioSyncInterval.current) {
          clearInterval(audioSyncInterval.current);
          audioSyncInterval.current = null;
        }
        
        setIsSpeaking(false);
        setOwenVocalState('IDLE');
        setIsInitialScenarioPlaying(false);
        safeOnEnd();
        
        toast.info('Erreur de lecture audio. Vérifie que ton son est activé.', { duration: 3000 });
      };
      
      // Tenter de jouer
      console.log('[playAudioWithStreamingDisplay] 📢 Attempting to play audio...');
      await audio.play().catch(err => {
        console.error('[playAudioWithStreamingDisplay] ❌ Autoplay blocked:', err);
        clearTimeout(safetyTimeoutId);
        setIsSpeaking(false);
        setOwenVocalState('IDLE');
        setIsInitialScenarioPlaying(false);
        safeOnEnd();
        if (messageType !== 'suggestion') {
          toast.info('Clique pour activer le son et écouter Owen', { duration: 4000 });
        }
      });
      
    } catch (error) {
      console.error('[playAudioWithStreamingDisplay] ❌ Fatal error:', error);
      setIsSpeaking(false);
      setIsInitialScenarioPlaying(false);
      safeOnEnd();
    }
  };
  

  // 🔒 P0 BIS: Accepte signal pour annulation
  const playWelcomeAudioUnified = async (greetingText, userName, moduleTitle, generatedScenario, signal = null) => {
    // 🔒 P0 BIS: Guard de montage immédiat
    if (!isMountedRef.current || signal?.aborted) {
      console.log('[playWelcomeAudioUnified] ⏹️ Aborted - component unmounted before start');
      return;
    }
    
    // ✅ CRITICAL FIX: Generate unique request ID for deduplication
    const requestId = `welcomeUnified-${Date.now()}-${Math.random()}`;
    console.log('[playWelcomeAudioUnified] Called with requestId:', requestId);
    console.log('[playWelcomeAudioUnified] State - hasPlayedWelcome:', hasPlayedWelcomeRef.current, 'isSpeaking:', isSpeaking, 'activeRequests:', activeRequestsRef.current.size);
    
    // Check 1: Already played welcome
    if (hasPlayedWelcomeRef.current) {
      console.log('[playWelcomeAudioUnified] ⛔ BLOCKED - welcome already played');
      return;
    }
    
    // Check 2: Duplicate request in progress
    if (activeRequestsRef.current.has('playWelcomeAudioUnified')) {
      console.log('[playWelcomeAudioUnified] ⛔ BLOCKED - duplicate request already in progress');
      return;
    }
    
    // Mark as active IMMEDIATELY (synchronous)
    activeRequestsRef.current.add('playWelcomeAudioUnified');
    hasPlayedWelcomeRef.current = true;
    console.log('[playWelcomeAudioUnified] 🔒 LOCKED - marked as played and added to active requests');
    
    if (isSpeaking) {
      console.log('[playWelcomeAudioUnified] Currently speaking - skipping but keeping lock');
      return;
    }
    
    console.log('[playWelcomeAudioUnified] ✅ All checks passed - proceeding with unified API');
    
    // ✅ OPTIMISATION LATENCE: Afficher placeholder pendant génération audio
    const placeholderMessageId = `welcome-placeholder-${Date.now()}`;
    setConversation(prev => [...prev, {
      id: placeholderMessageId,
      role: 'assistant',
      content: '...',
      displayedContent: '...',
      posture: isFreeMode ? 'COACH' : 'ROLEPLAY',
      timestamp: new Date().toISOString(),
      isGeneratingAudio: true
    }]);
    
    try {
      setIsSpeaking(true);
      console.log('🔊 [OWEN_STATE=UNCHANGED] playWelcomeAudioUnified - conserve état THINKING');
      
      const scenarioId = generatedScenario?.scenario_id || generatedScenario?.id || null;
      const userId = user?.user_id || user?.id || user?.email || 'anonymous';
      
      // ═══════════════════════════════════════════════════════════════════
      // 🚀 O3 PERF: TTS SEGMENTÉ — Première parole < 1.5s
      // ═══════════════════════════════════════════════════════════════════
      // 1. Découper l'opener en 2 segments à la frontière de phrase
      // 2. Lancer TTS segment 1 + segment 2 en PARALLÈLE
      // 3. Jouer segment 1 dès qu'il est prêt (TTFA minimal)
      // 4. Enchaîner segment 2 quand segment 1 finit
      // ═══════════════════════════════════════════════════════════════════
      
      // Trouver le point de coupe optimal (après 2-3 phrases, ~120-200 chars)
      const splitOpenerText = (text) => {
        if (!text || text.length < 150) return [text, null];
        
        // Chercher une fin de phrase (. ou ?) après 100 caractères
        const minCut = Math.min(100, Math.floor(text.length * 0.3));
        const maxCut = Math.min(300, Math.floor(text.length * 0.6));
        
        for (let i = minCut; i < maxCut; i++) {
          if ((text[i] === '.' || text[i] === '?') && i + 1 < text.length && text[i + 1] === ' ') {
            return [text.substring(0, i + 1).trim(), text.substring(i + 1).trim()];
          }
        }
        // Fallback: chercher un point ou ? n'importe où après minCut
        for (let i = minCut; i < text.length; i++) {
          if (text[i] === '.' || text[i] === '?') {
            return [text.substring(0, i + 1).trim(), text.substring(i + 1).trim()];
          }
        }
        return [text, null]; // Pas de coupe possible
      };
      
      const [segment1Text, segment2Text] = splitOpenerText(greetingText);
      const hasSegment2 = segment2Text && segment2Text.length > 10;
      
      console.log(`🚀 [O3] Segmentation: seg1=${segment1Text.length} chars, seg2=${hasSegment2 ? segment2Text.length : 0} chars`);
      
      // Lancer les TTS en PARALLÈLE
      const makeRequest = (text) => safeFetchJSON(`${BACKEND_URL}/api/ai/scenario/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          module_id: moduleId || 'default',
          scenario_id: scenarioId,
          text: text,
          emotion: 'empathique'
        }),
        signal: signal
      });
      
      // 🚀 Lancement parallèle: segment 1 + segment 2 (si présent)
      const ttsPromises = [makeRequest(segment1Text)];
      if (hasSegment2) ttsPromises.push(makeRequest(segment2Text));
      
      // Attendre segment 1 (le plus court, arrive en premier)
      const seg1Data = await ttsPromises[0];
      
      if (!isMountedRef.current || signal?.aborted) return;
      
      console.log(`🚀 [O3] Segment 1 reçu: audio_url=${!!seg1Data.audio_url}`);
      
      if (seg1Data.audio_url) {
        const displayText = greetingText;
        const welcomeMessageId = `welcome-unified-${Date.now()}`;
        
        setConversation(prev => prev.map(msg => 
          msg.id === placeholderMessageId ? {
            id: welcomeMessageId,
            role: 'assistant',
            content: displayText,
            displayedContent: '',
            emotion: 'empathique',
            posture: isFreeMode ? 'COACH' : 'ROLEPLAY',
            timestamp: new Date().toISOString(),
            isVocal: true,
            audioUrl: seg1Data.audio_url,
            eventId: seg1Data.event_id,
            spokenText: seg1Data.spoken_text,
            type: 'scenario'
          } : msg
        ));
        
        console.log('🚀 [O3] Playing segment 1 immediately...');
        
        // Jouer segment 1 immédiatement
        await playAudioWithStreamingDisplay(
          displayText,
          seg1Data.audio_url,
          welcomeMessageId,
          {
            onStart: () => {
              console.log('[O3] 🎬 Segment 1 audio started — TTFA achieved!');
              setOwenVocalState('SPEAKING');
              transitionTo('SCENARIO_STREAMING', 'scenario_audio_started');
            },
            onEnd: async () => {
              console.log('[O3] 🏁 Segment 1 ended');
              
              // Si segment 2 existe, l'enchaîner
              if (hasSegment2) {
                try {
                  const seg2Data = await ttsPromises[1];
                  if (seg2Data?.audio_url && isMountedRef.current && !signal?.aborted) {
                    console.log('[O3] 🔊 Playing segment 2...');
                    await playAudioWithStreamingDisplay(
                      displayText,
                      seg2Data.audio_url,
                      welcomeMessageId,
                      {
                        onStart: () => console.log('[O3] 🎬 Segment 2 started'),
                        onEnd: () => {
                          console.log('[O3] 🏁 Segment 2 ended — Full opener complete');
                          setOwenVocalState('IDLE');
                          setIsSpeaking(false);
                          setIsInitialScenarioPlaying(false);
                          // 🔒 FIX: Effacer audioUrl partiel pour forcer TTS complet au replay
                          setConversation(prev => prev.map(msg => 
                            msg.id === welcomeMessageId ? { ...msg, audioUrl: null } : msg
                          ));
                          requestAnimationFrame(() => {
                            transitionTo('USER_TURN', 'scenario_completed');
                          });
                        },
                        messageType: 'scenario',
                        emotion: 'empathique',
                        skipTextDisplay: true
                      }
                    );
                    return; // Segment 2 handles transition
                  }
                } catch (e) {
                  console.warn('[O3] Segment 2 failed, continuing without it:', e.message);
                }
              }
              
              // Pas de segment 2 ou erreur segment 2 → transition directe
              setOwenVocalState('IDLE');
              setIsSpeaking(false);
              setIsInitialScenarioPlaying(false);
              // 🔒 FIX: Effacer audioUrl partiel pour forcer TTS complet au replay
              setConversation(prev => prev.map(msg => 
                msg.id === welcomeMessageId ? { ...msg, audioUrl: null } : msg
              ));
              requestAnimationFrame(() => {
                transitionTo('USER_TURN', 'scenario_completed');
              });
            },
            messageType: 'scenario',
            emotion: 'empathique'
          }
        );
      } else {
        console.log('[playWelcomeAudioUnified] ❌ No audio URL in response');
        // 🔒 P0 ALIGNEMENT: Pas d'audio = IDLE
        setOwenVocalState('IDLE');
        setIsSpeaking(false);
        console.log('🧠 [OWEN_STATE=IDLE] playWelcomeAudioUnified - pas d\'audio');
        setIsInitialScenarioPlaying(false);
        transitionTo('USER_TURN', 'no_audio');
      }
    } catch (error) {
      // 🔒 P0 BIS: Ignorer les erreurs d'abort (sortie de page normale)
      if (error.name === 'AbortError') {
        console.log('[playWelcomeAudioUnified] ⏹️ Fetch aborted - page exit');
        return;
      }
      console.error('[playWelcomeAudioUnified] ❌ Fatal error:', error);
      // 🔒 P0 ALIGNEMENT: Erreur = IDLE
      setOwenVocalState('IDLE');
      setIsSpeaking(false);
      console.log('🧠 [OWEN_STATE=IDLE] playWelcomeAudioUnified - erreur');
      setIsInitialScenarioPlaying(false);
      transitionTo('USER_TURN', 'error');
      toast.error('Erreur de génération audio', { duration: 5000 });
    } finally {
      // ✅ CRITICAL: Remove from active requests when done
      activeRequestsRef.current.delete('playWelcomeAudioUnified');
      console.log('[playWelcomeAudioUnified] 🔓 UNLOCKED - removed from active requests');
    }
  };

  
  // ✨ Helper: Construire le texte complet du scénario pour l'affichage
  const buildFullScenarioText = (scenario) => {
    let fullText = '';
    
    if (scenario.context || scenario.prompt) {
      fullText += `Contexte : ${scenario.context || scenario.prompt}\n\n`;
    }
    
    if (scenario.objective || scenario.goal) {
      fullText += `Votre objectif : ${scenario.objective || scenario.goal}\n\n`;
    }
    
    if (scenario.ai_role) {
      fullText += `Rôle de l'IA : ${scenario.ai_role}\n\n`;
    }
    
    if (scenario.manager_mission) {
      fullText += `Votre mission : ${scenario.manager_mission}`;
    }
    
    return fullText;
  };
  
  // 🔊 NEW FUNCTION: Play specific message audio
  const playMessageAudio = async (messageText, messageIndex, emotion = 'empathique', existingAudioUrl = null) => {
    console.log('[playMessageAudio] Playing message:', messageIndex, 'existingAudioUrl:', existingAudioUrl ? 'YES' : 'NO');
    
    // ✅ Validation: Vérifier que le texte n'est pas vide
    if (!messageText || messageText.trim().length === 0) {
      console.log('[playMessageAudio] ⚠️ Empty message text - cannot play');
      toast.error('Message vide, impossible de lire l\'audio', { duration: 3000 });
      return;
    }
    
    // ✅ PHASE 3: Prevent starting replay if Owen is speaking
    if (isSpeaking) {
      console.log('[playMessageAudio] ⚠️ Owen is speaking - cannot start replay');
      toast.info('Attendez qu\'Owen finisse de parler', { duration: 3000 });
      return;
    }
    
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    setPlayingMessageIndex(messageIndex);
    setIsOwenSpeaking(true);  // Désactiver tous les boutons pendant replay
    console.log('[playMessageAudio] 🎵 Starting history playback - buttons will be disabled');
    
    try {
      let audioUrl = existingAudioUrl;
      
      // ✅ If audio_url already exists (scenario_spoken), use it directly
      if (existingAudioUrl) {
        console.log('[playMessageAudio] Using existing audio URL from scenario_spoken event');
      } else {
        // Generate new TTS
        console.log('[playMessageAudio] Generating new TTS audio');
        
        // Clean text for speech
        const cleanText = messageText
          .replace(/\*\*/g, '') // Remove bold markdown
          .replace(/🎯|👋|✅|📋|🤖/g, '') // Remove emojis
          .replace(/\n\n/g, '. '); // Convert line breaks to pauses
        
        // ✅ CRITICAL FIX: Use safeFetchJSON to prevent "Response body already used"
        const data = await safeFetchJSON(`${BACKEND_URL}/api/ai/text-to-speech`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: cleanText,
            emotion: emotion,
            user_id: user?.user_id || user?.id || 'anonymous'
          })
        });
        
        audioUrl = data.audio_url;
      }
      
      if (audioUrl) {
        const audio = OwenAudioBus.createAudio(audioUrl, { label: 'history-playback' });
        audioRef.current = audio;
        
        audio.onended = () => {
          console.log('[playMessageAudio] ✅ History playback ended - buttons re-enabled');
          setPlayingMessageIndex(null);
          setIsOwenSpeaking(false);  // Réactiver tous les boutons
        };
        
        audio.onerror = () => {
          console.log('[playMessageAudio] ❌ History playback error - buttons re-enabled');
          setPlayingMessageIndex(null);
          setIsOwenSpeaking(false);  // Réactiver tous les boutons
          // ✅ PHASE 3: Auto-dismiss error after 5s
          toast.error('Impossible de lire l\'audio', { duration: 5000 });
        };
        
        await audio.play();
      } else {
        setPlayingMessageIndex(null);
        setIsOwenSpeaking(false);  // Réactiver si pas d'audio
      }
    } catch (error) {
      console.error('[playMessageAudio] Error:', error);
      console.log('[playMessageAudio] ❌ Error during history playback - buttons re-enabled');
      setPlayingMessageIndex(null);
      setIsOwenSpeaking(false);  // Réactiver tous les boutons
      // ✅ PHASE 3: Auto-dismiss error after 5s
      toast.error('Erreur lors de la lecture', { duration: 5000 });
    }
  };
  
  // 🎨 Auto-scroll to top on page load for better UX
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // =========================================================================
  // 🔒 P0 UX MOA (21 Jan 2026): EARLY RETURN — RENDU EXCLUSIF
  // =========================================================================
  // Si sessions en cours de chargement → écran de chargement (pas d'UI IA)
  if (sessionsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF8EA] via-[#F7E9FF] to-[#E6D8FF] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#8B5CF6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#554C6C] font-medium">Chargement...</p>
        </div>
      </div>
    );
  }
  
  // 🔒 Iter339: Plus de page recharge EXCLUSIVE.
  //   Le modal `SessionsExhaustedModal` s'affiche en overlay au-dessus de la
  //   conversation existante (rendu plus bas dans le JSX principal).
  //   La conversation, le contexte, sessionId et toutes les refs survivent.
  //   La pause audio est gérée via OwenAudioBus.pauseAll('credits_modal').
  // =========================================================================
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF8EA] via-[#F7E9FF] to-[#E6D8FF] relative overflow-x-hidden">
      {/* 🎯 GOUVERNANCE PÉDAGOGIQUE: Bandeau objectif actif (Owen-controlled) */}
      {moduleId && !isFreeMode && (
        <ActiveObjectiveBanner 
          moduleId={parseInt(moduleId)}
          moduleName={moduleName}
          isSessionActive={vocalStage !== 'IDLE'}
          onObjectiveLoaded={handleObjectiveLoaded}
        />
      )}
      
      {/* Navbar Sticky - Premium Glass */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="sticky top-0 left-0 right-0 z-50 bg-white/30 backdrop-blur-xl shadow-lg border-b border-white/20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => moduleId ? navigate(`/module-v2/${moduleId}`) : navigate('/training')}
                data-testid="coach-vocal-back-btn"
                className="p-2 hover:bg-white/40 rounded-full transition-all duration-300"
                title="Retour"
              >
                <ArrowLeft className="w-5 h-5 text-[#3B1E73]" />
              </motion.button>
              <div>
                <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#3B1E73] to-[#8B5CF6]">
                  🎙️ Mode Vocal
                </h1>
                {/* 🔒 P1.4 MOA (Jan 2026): RÈGLE UNIVERSELLE OBJECTIF ACTIF
                    - Source unique: activeObjective?.title depuis /scenario/current
                    - Mode libre: afficher moduleName (pas de gouvernance pédagogique)
                    - Mode certifiant: JAMAIS afficher moduleName comme objectif
                    - Si activeObjective null + module non terminé: "Chargement..."
                */}
                <p className="text-sm text-[#554C6C]">
                  Owen • {isFreeMode 
                    ? moduleName 
                    : (activeObjective?.title || 'Chargement de l&apos;objectif...')
                  }
                </p>
                {/* 🏆 Coach Premium: Pas de salutation générique en mode libre (ouverture personnalisée) */}
                {userName && !isFreeMode && !isCoachPremiumMode && (
                  <p className="text-xs text-[#8B5CF6]/70">Bonjour {userName} 👋</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Owen 360° Recommendations Trigger */}
              {recommendations.length > 0 && (
                <div className="relative">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowRecommendations(!showRecommendations)}
                    className="relative flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-300/30 text-purple-700 rounded-full transition-all duration-300 hover:shadow-lg"
                  >
                    <Brain className="w-4 h-4" />
                    <span className="text-xs font-semibold hidden sm:inline">Owen 360°</span>
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full border-2 border-white"
                    />
                  </motion.button>
                  
                  {/* Recommendations Popover */}
                  <AnimatePresence>
                    {showRecommendations && (
                      <motion.div
                        key="recommendations-popover"
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 top-14 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-200/50 p-4 z-50"
                      >
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-purple-200/30">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          <h3 className="text-sm font-bold text-purple-900">Suggestions Owen 360°</h3>
                        </div>
                        
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {recommendations.map((rec, index) => (
                            <motion.div
                              key={rec.module_id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              onClick={() => {
                                setShowRecommendations(false);
                                navigate(`/module-v2/${rec.module_id}`);
                              }}
                              className="p-3 rounded-xl bg-gradient-to-br from-purple-50/50 to-pink-50/50 border border-purple-200/30 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                            >
                              <div className="flex items-start gap-2 mb-1">
                                <div className={clsx(
                                  "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                                  rec.type === "emotion_based" && "bg-pink-500 text-white",
                                  rec.type === "progression_based" && "bg-purple-500 text-white",
                                  rec.type === "deblocage" && "bg-orange-500 text-white"
                                )}>
                                  {Math.round(rec.relevance_score * 100)}
                                </div>
                                <div className="flex-1">
                                  <h4 className="text-xs font-bold text-purple-900 leading-tight">{rec.module_title}</h4>
                                  <p className="text-xs text-purple-700/80 mt-1">{rec.reason}</p>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-purple-200/30 text-center">
                          <button
                            onClick={() => {
                              setShowRecommendations(false);
                              navigate('/training');
                            }}
                            className="text-xs text-purple-600 hover:text-purple-800 font-semibold transition-colors"
                          >
                            Voir toutes les recommandations →
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
              
              {/* Switch to Written Mode */}
              {moduleId && (
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(139,92,246,0.3)' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(`/entrainement-ecrit/${moduleId}`)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#8B5CF6] to-[#C084FC] text-white rounded-full transition-all duration-300"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-xs font-semibold">Mode Écrit</span>
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </motion.nav>
      
      {/* Main Content - Responsive Layout: Mobile stacked with fixed heights, Desktop side-by-side */}
      <div className="py-6 px-4 sm:px-6 lg:px-8 min-h-screen">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 pb-24">{/*  Padding bottom for mobile controls */}
          
          {/* LEFT COLUMN (65%) - Video Frame + Controls - FIXED on mobile, flexible on desktop */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="lg:col-span-2 flex flex-col"
          >
            {/* Video Card - Glassmorphism Premium - FIXED HEIGHT for mobile, sticky for desktop */}
            <div className="bg-white/15 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-[0_0_50px_rgba(180,100,255,0.25)] overflow-hidden relative h-[300px] lg:h-[560px]">
              {/* Gradient Halo Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#8B5CF6]/10 via-transparent to-[#FFD86F]/10 pointer-events-none"></div>
              
              {/* Video Content - Standard Avatar ONLY */}
              <div className="relative z-10 w-full h-full">
                {/* Standard Avatar with AI Animation */}
                <div className="w-full h-full bg-gradient-to-br from-[#1B0F3B] via-[#2B1854] to-[#3B1E73] flex items-center justify-center relative">
                  {/* Loading State — Owen present with thinking animation */}
                  {coachState === 'loading' && (
                    <div className="w-full h-full relative">
                      <OwenPresence mode="thinking" hideLabel={true} />
                      <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center z-20 gap-2">
                        <h2 className="text-lg font-bold text-white">Owen se prepare...</h2>
                        <p className="text-[#E0BFFF] text-sm font-medium px-8 text-center">{loadingMessage || 'Chargement en cours...'}</p>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-[#FFD86F] rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                          <div className="w-2 h-2 bg-[#FFD86F] rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                          <div className="w-2 h-2 bg-[#FFD86F] rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Initial State */}
                  {coachState === 'initial' && (
                    <div className="w-full h-full relative">
                      <OwenPresence mode="idle" />
                      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20">
                        <p className="text-sm text-[#E0BFFF]/60 px-6 py-2 rounded-full backdrop-blur-md" style={{ background: 'rgba(255,255,255,0.06)' }}>Appuyez sur le micro pour reveiller Owen</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Active State */}
                  {coachState === 'active' && (
                    <div className="w-full h-full relative">
                      {(() => {
                        // 🎯 FLOW ÉVALUATION (Fév 2026) — Priorité ABSOLUE au phase unifié
                        //    Séquence stricte: analyzing → speaking → completed
                        //    Aucun saut d'état, aucune approximation, déclenché uniquement
                        //    par les événements réels du TTS (onplay / onended).
                        const evalPhase = evaluationFlowState.phase;
                        const isEvalAnalyzing = evalPhase === 'analyzing';
                        const isEvalSpeaking = evalPhase === 'speaking';
                        const isEvalCompleted = evalPhase === 'completed';
                        const isEvalActive = isEvalAnalyzing || isEvalSpeaking || isEvalCompleted;

                        const showScenarioLabel = !isEvalActive && isOwenThinking && !isOwenGeneratingResponse && !isOwenPreparingAudio && !isOwenCurrentlySpeaking;
                        const showResponseLabel = !isEvalActive && (isOwenGeneratingResponse || isOwenPreparingAudio) && !isOwenCurrentlySpeaking;
                        // "Owen parle" : soit phase=speaking (priorité), soit speaking classique hors flow éval
                        const showSpeakingLabel = isEvalSpeaking || (!isEvalActive && isOwenCurrentlySpeaking);
                        // "Analyse en cours" : EXCLUSIVEMENT pendant phase=analyzing (ou legacy path)
                        const showFeedbackLabel = isEvalAnalyzing || (!isEvalActive && isSessionEnding && !sessionTerminee && !isOwenCurrentlySpeaking && !showSpeakingLabel);
                        // "Session terminée" : EXCLUSIVEMENT après phase=completed (ou legacy path)
                        const showSessionEndedLabel = isEvalCompleted || (!isEvalActive && sessionTerminee && !isOwenCurrentlySpeaking);
                        const hasCustomLabel = showScenarioLabel || showResponseLabel || showSpeakingLabel || showFeedbackLabel || showSessionEndedLabel;
                        
                        return (
                          <>
                            <OwenPresence
                              mode={
                                // 🎯 FLOW ÉVALUATION: priorité absolue à la phase unifiée
                                isEvalCompleted
                                  ? 'completed'
                                  : isEvalSpeaking
                                    ? 'speaking'
                                    : isEvalAnalyzing
                                      ? 'analyzing'
                                  : isListening 
                                    ? 'listening' 
                                    : isOwenCurrentlySpeaking
                                      ? 'speaking' 
                                      : (isOwenThinking || isOwenGeneratingResponse || isOwenPreparingAudio)
                                        ? 'thinking'
                                        : sessionTerminee
                                          ? 'completed'
                                          : (isSessionEnding && !sessionTerminee)
                                            ? 'analyzing'
                                            : 'idle'
                              }
                              hideLabel={hasCustomLabel || (isSessionEnding && !isOwenCurrentlySpeaking) || sessionTerminee || isEvalActive}
                            />
                            
                            {/* Status badges — floating overlays at bottom of avatar */}
                            {showScenarioLabel && (
                              <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute bottom-6 left-0 right-0 flex justify-center z-30"
                              >
                                <motion.div 
                                  animate={{ opacity: [0.5, 1, 0.5] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                  className="bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white text-sm px-6 py-2 rounded-full shadow-lg flex items-center gap-2 backdrop-blur-md"
                                >
                                  <motion.span
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                  >
                                    <span className="inline-block w-2 h-2 bg-white/80 rounded-full" />
                                  </motion.span>
                                  <span>Generation du scenario...</span>
                                </motion.div>
                              </motion.div>
                            )}
                            
                            {showResponseLabel && (
                              <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute bottom-6 left-0 right-0 flex justify-center z-30"
                              >
                                <motion.div 
                                  animate={{ opacity: [0.6, 1, 0.6] }}
                                  transition={{ duration: 1.2, repeat: Infinity }}
                                  className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white text-sm px-6 py-2 rounded-full shadow-lg flex items-center gap-2 backdrop-blur-md"
                                >
                                  <span className="inline-block w-2 h-2 bg-white/80 rounded-full animate-pulse" />
                                  <span>Owen prepare sa reponse...</span>
                                </motion.div>
                              </motion.div>
                            )}
                            
                            {showSpeakingLabel && (
                              <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute bottom-6 left-0 right-0 flex justify-center z-30"
                              >
                                <motion.div 
                                  className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white text-sm px-6 py-2 rounded-full shadow-lg flex items-center gap-2 backdrop-blur-md"
                                >
                                  <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse" />
                                  <span>Owen parle</span>
                                  <div className="flex gap-0.5 items-end">
                                    <motion.div 
                                      animate={{ height: [4, 12, 4] }}
                                      transition={{ duration: 0.4, repeat: Infinity, delay: 0 }}
                                      className="w-1 bg-white rounded-full"
                                    />
                                    <motion.div 
                                      animate={{ height: [4, 12, 4] }}
                                      transition={{ duration: 0.4, repeat: Infinity, delay: 0.1 }}
                                      className="w-1 bg-white rounded-full"
                                    />
                                    <motion.div 
                                      animate={{ height: [4, 12, 4] }}
                                      transition={{ duration: 0.4, repeat: Infinity, delay: 0.2 }}
                                      className="w-1 bg-white rounded-full"
                                    />
                                  </div>
                                </motion.div>
                              </motion.div>
                            )}
                            
                            {showFeedbackLabel && (
                              <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute bottom-6 left-0 right-0 flex justify-center z-30"
                                data-testid="feedback-generating-badge"
                              >
                                <motion.div 
                                  animate={{ opacity: [0.6, 1, 0.6] }}
                                  transition={{ duration: 1.5, repeat: Infinity }}
                                  className="bg-gradient-to-r from-[#6366F1] to-[#4F46E5] text-white text-sm px-6 py-2 rounded-full shadow-lg flex items-center gap-2 backdrop-blur-md"
                                >
                                  <motion.span
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                                  >
                                    <span className="inline-block w-2 h-2 bg-white/80 rounded-full" />
                                  </motion.span>
                                  <span>Analyse en cours...</span>
                                </motion.div>
                              </motion.div>
                            )}
                            
                            {showSessionEndedLabel && (
                              <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="absolute bottom-6 left-0 right-0 flex justify-center z-30"
                                data-testid="session-ended-badge"
                              >
                                <div className="bg-gradient-to-r from-[#059669] to-[#047857] text-white text-sm px-6 py-2 rounded-full shadow-lg flex items-center gap-2 backdrop-blur-md">
                                  <span className="inline-block w-2 h-2 bg-white rounded-full" />
                                  <span>Session terminée</span>
                                </div>
                              </motion.div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {coachState === 'error' && (
                    <motion.div 
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center"
                    >
                      <div className="w-32 h-32 bg-white/90 rounded-full shadow-xl flex items-center justify-center mb-4 mx-auto">
                        <XCircle className="w-16 h-16 text-red-500" />
                      </div>
                      <h2 className="text-2xl font-bold text-white mb-2">⚠️ Erreur</h2>
                      <p className="text-red-400 font-medium px-8">Une erreur est survenue</p>
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => window.location.reload()}
                        className="mt-4 px-6 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all shadow-lg"
                      >
                        Recharger
                      </motion.button>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Control Buttons - FIXED POSITION AT BOTTOM (Always Visible) */}
            <div className="fixed bottom-4 z-[60] flex flex-col items-center gap-2 left-1/2 lg:left-1/3 -translate-x-1/2"
            >
              {/* 🎙️ Recording Duration Indicator - Show when recording */}
              <AnimatePresence>
                {isListening && (
                  <motion.div
                    key="recording-timer"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="bg-red-500 text-white px-6 py-2 rounded-full shadow-lg flex items-center gap-3 font-bold"
                  >
                    <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                    <span className="text-lg">
                      {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
                    </span>
                    <span className="text-sm opacity-90">Enregistrement en cours...</span>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Control Buttons Container - Centered with consistent spacing, responsive width */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-[0_0_20px_rgba(180,100,255,0.25)] p-3 md:p-4 flex items-center justify-center gap-2 md:gap-3 max-w-[90vw] lg:max-w-[55vw]">
              {/* Microphone Button - Only for starting recording */}
              {!isListening && (
                <motion.button
                  whileHover={{ scale: sessionsExhausted ? 1 : 1.1, boxShadow: sessionsExhausted ? 'none' : '0 0 30px rgba(139,92,246,0.6)' }}
                  whileTap={{ scale: sessionsExhausted ? 1 : 0.9 }}
                  onClick={startListening}
                  disabled={!canUseMic}
                  className={clsx(
                    'relative w-16 h-16 md:w-20 md:h-20 rounded-full transition-all duration-300 flex-shrink-0',
                    !canUseMic
                      ? 'bg-gray-400 cursor-not-allowed opacity-60'
                      : 'bg-gradient-to-br from-[#8B5CF6] to-[#C084FC] shadow-[0_0_25px_rgba(139,92,246,0.5)]'
                  )}
                  title={sessionsExhausted ? "Crédits épuisés — rechargez pour continuer" : isHistoryPlaying ? "Attendez la fin de la lecture" : "Démarrer l'enregistrement"}
                  data-testid="mic-button"
                >
                  {isProcessing ? (
                    <Loader2 className="w-8 h-8 md:w-10 md:h-10 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-spin" />
                  ) : isSpeaking ? (
                    <Volume2 className="w-8 h-8 md:w-10 md:h-10 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
                  ) : (
                    <Mic className="w-8 h-8 md:w-10 md:h-10 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  )}
                </motion.button>
              )}
              
              {/* 📤 SEND Button - Green gradient - Show ONLY when recording - Responsive size */}
              <AnimatePresence>
                {isListening && (
                  <motion.button
                    key="send-button"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    whileHover={{ scale: 1.15, boxShadow: '0 0 35px rgba(34,197,94,0.7)' }}
                    whileTap={{ scale: 0.9 }}
                    onClick={stopListening}
                    className="relative w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 shadow-[0_0_30px_rgba(34,197,94,0.6)] transition-all duration-300 flex-shrink-0"
                    title="Envoyer l'enregistrement"
                  >
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" className="md:w-10 md:h-10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                    </div>
                    <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75"></span>
                    <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap hidden md:block">
                      <span className="text-xs font-bold text-green-600 bg-white/90 px-2 py-1 rounded-full shadow-lg">
                        📤 Envoyer
                      </span>
                    </div>
                  </motion.button>
                )}
              </AnimatePresence>
              
              {/* Evaluation (Feedback) Button - Hidden when recording - Responsive text */}
              {!isListening && (
                <motion.button
                  whileHover={canUseTerminer ? { scale: 1.05, boxShadow: '0 0 25px rgba(255,216,111,0.6)' } : {}}
                  whileTap={canUseTerminer ? { scale: 0.95 } : {}}
                  onClick={endSession}
                  disabled={!canUseTerminer}
                  data-testid="coach-vocal-evaluation-btn"
                  className={clsx(
                    'px-4 md:px-6 py-2 md:py-3 rounded-full transition-all duration-300 flex items-center gap-2 text-xs md:text-sm font-semibold flex-shrink-0',
                    !canUseTerminer
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                      : 'bg-gradient-to-r from-[#FFD86F] to-[#FFB347] text-[#3B1E73] shadow-[0_0_20px_rgba(255,216,111,0.4)]'
                  )}
                  title={
                    sessionPhase === 'SESSION_CLOSED' ? "Session terminée — verrou actif"
                    : sessionPhase === 'EVALUATION_OPEN' ? "Évaluation ouverte"
                    : sessionPhase === 'EVALUATION_READY' ? "Évaluation prête — cliquez pour consulter"
                    : sessionPhase === 'STOP_CONVERSATION' ? "Owen termine son intervention — patientez"
                    : isEvaluationLocked ? "Évaluation en cours — patientez"
                    : isHistoryPlaying ? "Attendez la fin de la lecture"
                    : "Terminer la session et recevoir un feedback"
                  }
                >
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                  Evaluation (Feedback)
                </motion.button>
              )}
              
              {/* Cancel Button - Show when recording with more space */}
              <AnimatePresence>
                {isListening && (
                  <motion.button
                    key="cancel-button"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={cancelRecording}
                    className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-all duration-200 flex items-center gap-2 text-sm font-semibold flex-shrink-0"
                    title="Annuler l'enregistrement"
                  >
                    <XCircle className="w-5 h-5" />
                    Annuler
                  </motion.button>
                )}
              </AnimatePresence>
              
              {/* Fermer Button - Hidden when recording. 🔓 TOUJOURS ACTIF (whitelist) */}
              {!isListening && (
                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.5)' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    // 🌊 Cancel any ongoing streaming
                    if (isStreaming) {
                      cancelStream();
                    }
                    
                    // Dynamic navigation: go back to previous page if history exists
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      // Fallback to dashboard if no history
                      navigate('/training');
                    }
                  }}
                  data-testid="coach-vocal-close-btn"
                  className="p-3 bg-white/30 backdrop-blur-md rounded-full transition-all duration-300 shadow-lg flex-shrink-0"
                  title="Fermer"
                >
                  <X className="w-5 h-5 text-[#3B1E73]" />
                </motion.button>
              )}
              </div>
            </div>
          </motion.div>
          
          {/* RIGHT COLUMN (35%) - Scrollable Conversation Feed - Awwwards Premium */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.43, 0.13, 0.23, 0.96] }}
            className="lg:col-span-1 flex flex-col conversation-panel-height lg:sticky lg:top-[100px]"
          >
            {/* Conversation Feed - Premium Glassmorphism avec gradient animé */}
            <motion.div 
              initial={{ y: 20, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.6, ease: 'easeOut' }}
              className="flex-1 relative bg-gradient-to-br from-white/40 via-white/30 to-white/20 backdrop-blur-2xl rounded-3xl border border-white/30 shadow-[0_8px_40px_rgba(139,92,246,0.25),0_0_100px_rgba(255,216,111,0.1)] p-4 sm:p-5 flex flex-col overflow-hidden"
            >
              {/* Animated gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-yellow-400/5 pointer-events-none rounded-3xl" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(139,92,246,0.1),transparent_50%)] pointer-events-none rounded-3xl" />
              
              <div className="relative z-10 flex-1 flex flex-col min-h-0">
                <motion.h3 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="text-sm sm:text-base font-bold bg-gradient-to-r from-[#3B1E73] via-[#8B5CF6] to-[#3B1E73] bg-clip-text text-transparent mb-3 flex items-center justify-between gap-2 flex-shrink-0"
                >
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#8B5CF6] animate-pulse" />
                    Conversation en direct
                  </div>
                  {/* 🎯 P1.0 MOA: Sélecteur de thème = acte utilisateur (mode libre uniquement) */}
                  {/* 🔒 MOA RÈGLE 1: Lock UI pendant génération Owen */}
                  {/* 🛡️ Iter350: Verrouillage en SESSION_CLOSED — UI = reflet exact de l'état système */}
                  {isFreeMode && (
                    <ThemeSelector 
                      onThemeSelect={handleThemeSelection}
                      selectedTheme={selectedTheme}
                      disabled={isProcessing || isListening || isSpeaking || isOwenSpeaking || isInitialScenarioPlaying || isStreaming || sessionPhase === 'SESSION_CLOSED'}
                    />
                  )}
                </motion.h3>
              
                {/* Auto-scroll Container - Premium avec smooth scroll */}
                {/* 🔧 FIX MOBILE: pb-24 pour compenser les boutons fixed bottom */}
                <ScrollToBottom 
                  className="flex-1 conversation-scroll pb-24 lg:pb-4 overflow-y-auto scroll-smooth" 
                  followButtonClassName="scroll-to-bottom-button"
                  scrollViewClassName="scroll-smooth"
                >
                  {conversation.length === 0 && !isStreaming ? (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.8, duration: 0.5 }}
                      className="text-center py-12 sm:py-16"
                    >
                      <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-400/20 to-yellow-400/20 flex items-center justify-center backdrop-blur-sm">
                        <MessageCircle className="w-8 h-8 sm:w-10 sm:h-10 text-[#8B5CF6]/60 animate-pulse" />
                      </div>
                      <p className="text-xs sm:text-sm text-[#8B5CF6]/60 font-medium">Commencez votre conversation avec Owen</p>
                    </motion.div>
                  ) : (
                    <div className="space-y-3 sm:space-y-4 pr-2 pb-4">
                      {conversation.map((message, index) => (
                        <motion.div
                          key={message.id || message.timestamp || `msg-${index}`}
                          initial={{ opacity: 0, x: message.role === 'user' ? 20 : -20, scale: 0.95 }}
                          animate={{ opacity: 1, x: 0, scale: 1 }}
                          transition={{ 
                            duration: 0.4, 
                            delay: index * 0.05,
                            ease: [0.25, 0.1, 0.25, 1]
                          }}
                          whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                          className={clsx(
                            'flex items-end gap-2 sm:gap-3',
                            message.role === 'user' ? 'justify-end' : 'justify-start'
                          )}
                        >
                          <motion.div
                            whileHover={{ 
                              boxShadow: message.role === 'user' 
                                ? '0 8px 30px rgba(255,216,111,0.4)' 
                                : '0 8px 30px rgba(139,92,246,0.4)'
                            }}
                            transition={{ duration: 0.2 }}
                            className={clsx(
                              'max-w-[85%] px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl text-xs sm:text-sm shadow-lg backdrop-blur-sm border transition-all duration-300',
                              message.role === 'user'
                                ? 'bg-gradient-to-br from-[#FFF8E1] to-[#FFF4D0] text-[#3B1E73] border-yellow-200/50 shadow-yellow-200/30'
                                : 'bg-gradient-to-br from-[#F3E8FF] to-[#E9D8FD] text-[#1A0E35] border-purple-200/50 shadow-purple-200/30'
                            )}
                          >
                            {message.role === 'assistant' && (
                              <motion.div 
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="flex items-center gap-1.5 mb-1.5 opacity-80"
                              >
                                {message.isVocal ? (
                                  <>
                                    <motion.div
                                      animate={{ scale: [1, 1.2, 1] }}
                                      transition={{ duration: 2, repeat: Infinity }}
                                    >
                                      <Volume2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-purple-600" />
                                    </motion.div>
                                    <span className="font-bold text-[10px] sm:text-xs bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">Owen • Vocal</span>
                                  </>
                                ) : (
                                  <>
                                    <User className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-purple-600" />
                                    <span className="font-bold text-[10px] sm:text-xs text-purple-800">Owen</span>
                                  </>
                                )}
                              </motion.div>
                            )}
                            <div className="whitespace-pre-line leading-relaxed">
                              {/* 🎯 P1.0 MOA: Indicateur de sélection de thème */}
                              {message.isThemeSelection ? (
                                <span className="flex items-center gap-2 text-[#3B1E73] font-medium">
                                  <span className="text-amber-500">🎯</span>
                                  <span>{message.content}</span>
                                </span>
                              ) : /* 🔒 P0 OPTIMISTIC UI: Indicateur de transcription en cours */
                              message.isTranscribing ? (
                                <motion.span
                                  animate={{ opacity: [0.6, 1, 0.6] }}
                                  transition={{ duration: 1.2, repeat: Infinity }}
                                  className="flex items-center gap-2 text-[#3B1E73]/70"
                                >
                                  <motion.span
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                  >
                                    🎤
                                  </motion.span>
                                  <span>Transcription en cours...</span>
                                </motion.span>
                              ) : /* 🔒 P0 FIX (Jan 2026): Prioriser isP12Libre pour garantir l'affichage des blocs structurés */
                              message.isP12Libre && message.p12Structure ? (
                                // 🎯 P1.2: Feedback libre structuré - TOUJOURS afficher en blocs
                                <P12FeedbackBlocks 
                                  p12Structure={message.p12Structure} 
                                  rawContent={message.content} 
                                />
                              ) : (message.isStreaming || message.isPlayingAudio || message.isGeneratingAudio) ? (
                                // 🎯 AUDIO-SYNCED: Affichage progressif avec Markdown
                                <>
                                  <span className="transition-opacity duration-200">
                                    <OwenMarkdown>{message.displayedContent || ''}</OwenMarkdown>
                                  </span>
                                  {message.isStreaming && (
                                    <motion.span
                                      animate={{ opacity: [1, 0.3, 1] }}
                                      transition={{ duration: 0.8, repeat: Infinity }}
                                      className="inline-block w-1 h-4 bg-purple-600 ml-1"
                                    />
                                  )}
                                  {message.isPlayingAudio && message.displayedContent && (
                                    <motion.span
                                      animate={{ opacity: [0.5, 1, 0.5] }}
                                      transition={{ duration: 1, repeat: Infinity }}
                                      className="inline-block ml-2 text-purple-600 text-xs"
                                    >
                                      🔊
                                    </motion.span>
                                  )}
                                </>
                              ) : (
                                // Fallback: afficher content avec Markdown
                                <OwenMarkdown>{message.content}</OwenMarkdown>
                              )}
                            </div>
                            
                            {/* 🎯 PHASE 3: Streaming indicator */}
                            {message.isStreaming && (
                              <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex items-center gap-1.5 mt-2 text-[10px] sm:text-xs text-purple-600/70"
                              >
                                <motion.span
                                  animate={{ opacity: [1, 0.3, 1] }}
                                  transition={{ duration: 1.5, repeat: Infinity }}
                                >
                                  🌊
                                </motion.span>
                                <span className="font-medium">Owen est en train d&apos;écrire...</span>
                              </motion.div>
                            )}
                            
                            {/* 🎯 SUPPRIMÉ: Indicateur "Owen parle..." dans bulle de chat (doublon avec statut global) */}
                            
                            {/* 🎯 V16 - Affichage de la posture Owen (COACH ou ROLEPLAY) */}
                            {message.posture && message.role === 'assistant' && !message.isStreaming && (
                              <motion.span 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                                className="text-[10px] sm:text-xs opacity-70 mt-1.5 block font-medium flex items-center gap-1"
                              >
                                <span>{postureEmojis[message.posture] || '🎯'}</span>
                                <span>{postureLabels[message.posture] || 'Posture Coach'}</span>
                              </motion.span>
                            )}
                          </motion.div>
                        
                          {/* 🔊 Listen Button - Premium avec effets visuels */}
                          {message.role === 'assistant' && (
                            <motion.button
                              whileHover={{ scale: 1.15, rotate: [0, -5, 5, 0] }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => playMessageAudio(
                                message.fullContent || message.displayedContent || message.content || '', 
                                index, 
                                message.emotion, 
                                message.audioUrl
                              )}
                              disabled={playingMessageIndex === index || buttonsDisabled}
                              className={clsx(
                                'flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full transition-all duration-300 flex items-center justify-center relative shadow-lg',
                                playingMessageIndex === index
                                  ? 'bg-gradient-to-br from-[#8B5CF6] via-[#A78BFA] to-[#C084FC] shadow-[0_0_20px_rgba(139,92,246,0.6)]'
                                  : buttonsDisabled
                                  ? 'bg-gray-200 cursor-not-allowed opacity-40'
                                  : 'bg-gradient-to-br from-white to-purple-50 hover:from-purple-100 hover:to-purple-200 backdrop-blur-sm border border-purple-200/50 hover:shadow-[0_0_15px_rgba(139,92,246,0.4)]'
                              )}
                              title={
                                isOwenSpeaking 
                                  ? "Owen parle... Attendez la fin" 
                                  : "Écouter ce message"
                              }
                            >
                              {playingMessageIndex === index && (
                                <>
                                  <motion.div
                                    animate={{ scale: [1, 1.5, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                    className="absolute inset-0 rounded-full bg-purple-400/30 blur-md"
                                  />
                                  <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                                    className="absolute inset-0 rounded-full border-2 border-t-white/60 border-r-transparent border-b-transparent border-l-transparent"
                                  />
                                </>
                              )}
                              {playingMessageIndex === index ? (
                                <Volume2 className="w-4 h-4 sm:w-5 sm:h-5 text-white relative z-10 drop-shadow-md" />
                              ) : (
                                <Volume2 className={clsx(
                                  "w-4 h-4 sm:w-5 sm:h-5 relative z-10",
                                  (isSpeaking || isListening) ? "text-gray-400" : "text-[#8B5CF6]"
                                )} />
                              )}
                            </motion.button>
                          )}
                        </motion.div>
                      ))}
                    
                      {/* 🌊 Streaming Message - Shows real-time AI response avec effet premium */}
                      {isStreaming && streamedText && (
                        <motion.div
                          initial={{ opacity: 0, x: -20, scale: 0.95 }}
                          animate={{ opacity: 1, x: 0, scale: 1 }}
                          transition={{ duration: 0.4, ease: 'easeOut' }}
                          className="flex items-end gap-2 sm:gap-3 justify-start"
                        >
                          <motion.div 
                            animate={{ 
                              boxShadow: [
                                '0 8px 30px rgba(139,92,246,0.3)',
                                '0 8px 35px rgba(139,92,246,0.5)',
                                '0 8px 30px rgba(139,92,246,0.3)'
                              ]
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="max-w-[85%] px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl text-xs sm:text-sm shadow-lg backdrop-blur-sm border border-purple-300/60 bg-gradient-to-br from-[#F3E8FF] to-[#E9D8FD] text-[#1A0E35] relative overflow-hidden"
                          >
                            {/* Animated gradient overlay pour effet streaming */}
                            <motion.div
                              animate={{ 
                                x: ['-100%', '100%']
                              }}
                              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
                            />
                            
                            <motion.div 
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="flex items-center gap-1.5 mb-1.5 opacity-90 relative z-10"
                            >
                              <motion.div
                                animate={{ scale: [1, 1.3, 1], rotate: [0, 180, 360] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              >
                                <Volume2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-purple-600" />
                              </motion.div>
                              <span className="font-bold text-[10px] sm:text-xs bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">Owen • En direct</span>
                              <motion.span 
                                animate={{ opacity: [1, 0.3, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                                className="text-[10px] sm:text-xs ml-1"
                              >
                                🌊
                              </motion.span>
                            </motion.div>
                            <p className="whitespace-pre-line leading-relaxed relative z-10">{streamedText}</p>
                            
                            {/* Typing indicator */}
                            <motion.div 
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="flex gap-1 mt-2 relative z-10"
                            >
                              {[0, 1, 2].map((i) => (
                                <motion.div
                                  key={i}
                                  animate={{ y: [0, -5, 0] }}
                                  transition={{ 
                                    duration: 0.6, 
                                    repeat: Infinity, 
                                    delay: i * 0.2 
                                  }}
                                  className="w-1.5 h-1.5 rounded-full bg-purple-500"
                                />
                              ))}
                            </motion.div>
                          </motion.div>
                        </motion.div>
                      )}
                    </div>
                  )}
                </ScrollToBottom>
              </div>
            </motion.div>
            
            {/* Error Display - Compact + Bouton Reprendre */}
            <div className="space-y-2 flex-shrink-0">
              <AnimatePresence>
                {error && (
                  <motion.div 
                    key="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-2xl text-xs"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span>{error}</span>
                      <button
                        onClick={async () => {
                          setError(null);
                          // 🔧 P0 HEARTBEAT: Retry avec le dernier payload
                          if (lastUserMessageRef?.current?.payload) {
                            toast.info('Reconnexion en cours...', { duration: 2000 });
                            console.log('[P0 RETRY] 🔄 Relancement avec payload précédent');
                            
                            // Reset états pour nouveau try
                            setOwenVocalState('THINKING');
                            setBlocState('THINKING');
                            setLoadingMessage('Owen reprend sa réflexion...');
                            
                            try {
                              const { url, payload } = lastUserMessageRef.current;
                              const token = localStorage.getItem('token') || localStorage.getItem('access_token');
                              
                              const response = await fetch(url, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Accept': 'text/event-stream',
                                  ...(token && { 'Authorization': `Bearer ${token}` }),
                                  ...(window.__currentExecutionContextId && { 
                                    'X-Execution-Context-Id': window.__currentExecutionContextId 
                                  })
                                },
                                body: JSON.stringify(payload)
                              });
                              
                              if (response.ok) {
                                toast.success('Connexion rétablie !', { duration: 2000 });
                                // Le flux SSE reprend normalement
                              } else {
                                throw new Error('Échec de la reconnexion');
                              }
                            } catch (retryErr) {
                              console.error('[P0 RETRY] ❌ Échec retry:', retryErr);
                              setError('Impossible de reprendre. Veuillez actualiser la page.');
                              setOwenVocalState('IDLE');
                              setBlocState('IDLE');
                            }
                          } else {
                            toast.info('Aucune action à reprendre', { duration: 2000 });
                          }
                        }}
                        className="bg-red-600 text-white px-3 py-1 rounded-lg text-xs hover:bg-red-700 transition-colors whitespace-nowrap"
                      >
                        Reprendre
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* 🔧 P0 HEARTBEAT: Message de progression pendant génération longue */}
              <AnimatePresence>
                {(owenIsStreaming || owenPhase === OWEN_PHASES.THINKING) && !error && (
                  <motion.div
                    key="processing-message"
                               initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="bg-blue-50 border border-blue-200 text-blue-700 px-3 py-2 rounded-2xl text-xs flex items-center gap-2"
                  >
                    <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Owen réfléchit à ta question...</span>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* 🔒 P0 OPTIMISTIC UI: Bloc transcript supprimé - la conversation est la source unique de vérité */}
            </div>
          </motion.div>
        </div>
      </div>

      {/* 🔒 Iter339 — Sessions épuisées : overlay non-destructif.
          La conversation reste visible derrière (backdrop), la session est conservée,
          et le polling détecte automatiquement la recharge pour reprendre l'échange. */}
      <SessionsExhaustedModal
        isOpen={showSessionsExhaustedModal}
        onClose={() => setShowSessionsExhaustedModal(false)}
        onRecharged={async () => {
          setShowSessionsExhaustedModal(false);
          // refreshSessions est exposé par le hook useSessionCredits — recharge
          // canonique avant reprise des contrôles.
          try { await refreshSessions(); } catch (_) { /* noop */ }
        }}
        resumeSnapshot={{
          pathname: window.location.pathname,
          search: window.location.search,
          mode: isFreeMode ? 'libre' : (moduleId ? 'certifiant' : 'libre'),
          moduleId: moduleId || null,
          sessionId: null,  // Pas de sessionId persistant dans CoachVocal (constructions inline par appel)
        }}
      />
    </div>
  );
};

export default CoachVocal;
